/**
 * Imperative DOM for the widget shell. One set of DOM nodes renders all
 * five layouts (ICON / INPUT / EXPANDED / SIDEBAR / FULL) — CSS keys off
 * `data-layout` on the root to hide/show/position the pieces. This keeps
 * the bundle small (no per-layout React tree) and layout transitions
 * become one attribute change + CSS transition.
 *
 * DOM structure mirrors legacy `ChatInterface.vue` / `ChatHeader.vue`
 * (ai-experience branch) so the visual port from `styles.ts` lines up.
 *
 * State machine:
 *   ICON --click--> {EXPANDED | FULL on mobile}
 *   EXPANDED <--header-toggle--> SIDEBAR
 *   any --close--> ICON
 *   resume-from-sessionStorage --> SIDEBAR (legacy convention)
 */

import type {
  AgentConfigResponse,
  AgentLinkResponseItem,
  ChatEvent,
  ChatHistoryMessage,
} from '@showpilot/core';
import { cursorIfLoaded, loadCursor } from './cursor-loader';
import { LAYOUTS, type Layout } from './layouts';
import { applyHostDock, removeHostDock, shouldDock, type RailMode } from './host-dock';
import { rebaseToVisitorOrigin } from './nav';
import { detectBookingCompletion } from './booking-providers';
import {
  bootStateFor,
  viewFromLegacyLayout,
  stateToLayout,
  transition,
  type AgentView,
  type TransitionAction,
  type TransitionContext,
  type WidgetState,
} from './state';
import { injectStyles } from './styles';
import type { FormFieldSpec } from './form-fields';
import { loadForms, type FormsModule } from './forms-loader';
import { createStreamingText } from './components/streaming-text';
import type { VoiceRecorder, VoiceRecorderHandlers } from './voice-recorder';
import { loadSpeech, speechSupported } from './speech-loader';
import {
  isInStaleToolWindow,
  notifyVoiceOfContentClosed,
  notifyVoiceOfVisitorAction,
} from './voice/daily-session';
import { createAudioSpeech } from './audio-speech';
import { createPlaylistDrawer, type PlaylistItem } from './components/playlist-drawer';
import type { PresenterStage } from './presenter-stage';
import { createContentPanel, type ContentProgress } from './components/content-panel';
import { beamInteractiveHost, contentDismissAction, isCinemaContent, stopsVoiceOnContentDismiss } from './beam-content';
import { formSubmittedAck, meetingBookedAck } from './capture-ack';
import { appendJourneyStep, type JourneyStepPayload } from './components/journey-step';
import { RecommendationCards } from './components/RecommendationCards';
import {
  AssistantBubble,
  CallInviteCard,
  ErrorBubble,
  RepBubble,
  RepTypingBubble,
  StreamingAssistantBubble,
  SystemLine,
  ThinkingBubble,
  TypingBubble,
  UserBubble,
} from './components/message-bubble';
import { createCallOverlay, type CallOverlayController } from './relay/call-overlay';
import {
  Backdrop,
  Header,
  Launcher,
  SpeakCta,
  WelcomeBubble,
} from './components/widget-chrome';
import { VideoGreeter, type VideoGreeterBits } from './components/video-greeter';
import { createAvatarStage, type AvatarDockName } from './avatar-stage';
import { markBeamPresence, sessionStorageOrNull } from './beam-presence';
import { shouldKeyLive } from './live-key';
import { CopilotPip } from './components/copilot-pip';
import { Footer } from './components/footer';
import { AgentLinks } from './components/AgentLinks';
import { findElementByText } from './page-scraper';
import { labelForTool } from './tool-labels';
import { mountPreactIsland } from './voice/preact-mount';
import { botVideoStream, voiceState } from './voice/voice-state';
import { setVoiceMicMuted, setBotAudioMuted, notifyVoiceVisitorReady, markVoiceFaceReady } from './voice/daily-session';
import { loadBoundless } from './boundless-loader';
import type { BoundlessHandle } from './boundless/stage';
import { playChirp } from './chirp';
import { pushMilestone } from './data-layer';
import { computed, effect, signal } from '@preact/signals';
import { deviceSig, kbOffsetSig, orientationSig, installViewportAttrs, keyboardState } from './viewport';
import { dismissKeyboard, focusComposerOnLayout } from './composer-focus';

/**
 * M8 metric ladder (Phase 6) — a pre-session presence funnel event.
 * `shown` = a dormant surface rendered (accept-rate denominator);
 * `accepted` = the FIRST conversation-starting act, cut by HOW;
 * `dismissed` = active dismissal (swipe-down / ×).
 */
export interface PresenceFunnelEvent {
  type: 'shown' | 'accepted' | 'dismissed';
  surface: 'bar' | 'launcher' | 'bubble' | 'engage' | 'card';
  act?: 'typed' | 'talked' | 'clicked';
}

/** v3-B slot booking — what a chip tap POSTs to /book-slot. */
export type SlotBookBody = {
  startIso: string;
  email?: string;
  timezone: string;
};

/**
 * Graded /book-slot outcome (rep/booking slice 1): `slot-taken`
 * failures carry the server's fresh times so chips re-offer inline;
 * `already-booked` renders a done-line; anything else → modal ladder.
 */
export type SlotBookOutcome =
  | { ok: true; startIso: string }
  | {
      ok: false;
      code?: string;
      freshSlots?: Array<{ startIso: string }>;
      durationMinutes?: number;
    };

/** Prequalify P-2 — the /route-readiness response the card consumes. */
export interface SlotReadiness {
  ready: boolean;
  repUnchanged?: boolean;
  rep?: { name?: string | null; title?: string | null; avatarUrl?: string | null } | null;
  startsIso?: string[];
  durationMinutes?: number;
  nextFromIso?: string | null;
  gaps?: Array<{
    key: string;
    label: string;
    why: string;
    options?: Array<{ value: string; label: string }>;
  }>;
}

export interface WidgetUI {
  root: HTMLElement;
  /**
   * Anchor element for the voice-mode status badge. The voice
   * bootstrap mounts a Preact component here (sp-voice-status) that
   * reflects connecting / listening / speaking / etc. The widget UI
   * just provides the slot — voice owns what renders inside.
   */
  voiceBadgeAnchor: HTMLElement;
  setLayout(layout: Layout): void;
  getLayout(): Layout;
  /**
   * Switch the widget to a different view after /config returns.
   * Recomputes the current state via `bootStateFor` and updates the
   * visual layout. Idempotent — calling with the same view is a no-op.
   */
  setView(view: AgentView): void;
  setConfig(config: AgentConfigResponse): void;
  /**
   * Directed Experience (P2): suppress the static inbound welcome bubble
   * ("how can I help?"). In a shared/embedded experience the agent LEADS
   * with a directed opening, so the reactive greeting must never show.
   */
  clearWelcome(): void;
  /**
   * Actions on a phone, the notification tier: show the agent's first move
   * as the line above the bar (INPUT layout) with the composer right there.
   * Bypasses the engage greeting's no-swap rule — this is the Action's own
   * opening, not a rewrite. Hidden again the moment the layout leaves the
   * bar (a reply converges to the sheet; a swipe collapses to the launcher).
   */
  showBarNotice(text: string): void;
  /**
   * Actions on a phone, the sheet tier: the visitor asked for the thing
   * itself, so the sheet opens already in conversation — the greeter stage
   * collapses the way it does after a first typed turn.
   */
  markConversationStarted(): void;
  /**
   * Standalone Experience (P2): mark the widget as the whole-page surface
   * (share link / experience embed) so it hides the minimize + close
   * chrome that would otherwise collapse to a chat button over blank.
   */
  setStandaloneExperience(on: boolean): void;
  /**
   * Durable first-touch (P2): render the operator's curated playlist as
   * the "My Playlist" listing and open the FIRST item immediately — no
   * assembleJourney round-trip. The agent then welcomes + narrates.
   */
  loadDurablePlaylist(items: PlaylistItem[]): void;
  /**
   * Presenter (agent-led demos, docs/PRESENTER_AGENT.md §5): attach the
   * stage router. The five stage ChatEvents route to it before the normal
   * dispatcher; `done` notifies its pacing (continuation tick).
   */
  setPresenter(p: PresenterStage | null): void;
  /** Presenter: open a hub tour in the content pane, agent mode. */
  openPresenterTour(tourPublicId: string): void;
  /** Presenter: tear the tour viewer down (agent tourControl close /
      agent navigate) — the visitor sees the page itself again. No-op
      when nothing is on the stage. */
  closePresenterStage(): void;
  /** STAGE TRUTH: the content experience closed while a TOUR was on it —
      by ANY path (viewer's X, backdrop, agent close). The shell acks it
      so stage memory stops narrating a stage nobody sees. */
  onTourStageClosed(cb: (tourPublicId: string) => void): void;
  /**
   * PRESENCE LAW (docs/PRESENTER_AGENT.md): the tour currently on the
   * widget's own stage (content pane), or null. Site mounts stamp it on
   * chat context so the runtime keys the presenter hat mid-conversation;
   * cleared the moment the pane closes or shows non-tour content.
   */
  activeTourPublicId(): string | null;
  /**
   * Gating (P2): overlay a lead-capture gate over the content area. For
   * `capture` the content is blank behind it; for `reverse` the preview
   * shows behind a scrim. `onCaptured` fires after a successful submit so
   * the caller can unlock + load the full playlist. Returns a disposer.
   */
  mountExperienceGate(opts: {
    fields: string[];
    mode: 'capture' | 'reverse';
    lockedCount: number;
    onCaptured: () => void;
    /** G-1 — the content_gate form's full field list (per-form labels,
     *  email rules). Absent/empty = legacy bare-key render. */
    formFields?: unknown;
    /** G-1 — echoed on the capture POST so the after-ladder runs. */
    formId?: string | null;
  }): () => void;
  /**
   * Slice 6.6 — proactive override of the engage card welcome bubble.
   * The /v1/decide call races the 1800ms engage chirp timer; if it
   * wins, we replace the static `agent.welcomeMessage` with a per-
   * visitor message before it gets chirped + appended. If /decide
   * arrives after the static welcome has already appended, this is
   * a no-op (visitor already saw the unchanged greeting; replacing
   * it mid-flight would be jarring).
   */
  /** Returns true when the greeting was committed (P2-strict may refuse a post-paint swap). */
  setEngageGreeting(message: string): boolean;
  /**
   * Opening Turn — append the composed opening bubble + swap chips and
   * ghost suggestion. Pre-conversation only (resumed sessions refuse).
   * Returns true when applied — the caller threads the message into the
   * first /chat turn as the prior assistant message.
   */
  applyComposedOpening(opening: {
    message: string;
    chips: string[];
    ghostSuggestion?: string | null;
  }): boolean;
  /**
   * Beam IS the presence on the page — a proactive line is said by HER
   * (caption card), never by a second card beside her. Returns false
   * when she is not on stage so the caller falls back to its own surface.
   */
  presenceSay(message: string): boolean;
  /** True when the mobile UX program flag (ui.mobileV2) is active. */
  isMobileV2(): boolean;
  /** M3/P7 — dock handles for the Pilot Prompt teaser avatar. */
  avatarTeaserDock: { register(anchor: HTMLElement): void; release(): void };
  /**
   * Feed the agent's live utterance to the BEAM speech card. Was
   * `setPipCaption` until 2026-10-25, when the PiP's own caption was
   * removed — a name pointing at a surface it no longer touches is how
   * this file gets misread.
   */
  setSpeechCaption(text: string): void;
  /** Phase 5 (O1) — set the launcher's collapsed-offer badge count. */
  setOfferBadge(count: number): void;
  /** Subscribe to visual-layout changes (D3 card lifecycle). Returns a disposer. */
  onLayoutChange(cb: (layout: Layout) => void): () => void;
  /**
   * M8 metric ladder (Phase 6) — subscribe to pre-session presence
   * funnel events (shown / accepted / dismissed). Boot-time events are
   * queued and replayed on registration. index.tsx forwards these to
   * POST /v1/presence/events (PostHog counts; no Neon row).
   */
  onPresence(cb: (e: PresenceFunnelEvent) => void): () => void;
  renderHistory(history: ChatHistoryMessage[]): void;
  appendUserMessage(text: string): void;
  /**
   * Personalize v1 — programmatically open the chat panel from an
   * outside surface (Pilot Prompt reply tap, chip click) and dispatch
   * the visitor's text as the first turn. Optionally renders the
   * preceding assistant message (the proactive prompt copy) into
   * history first so the conversation reads continuously — the
   * "agent greeted, I replied" shape.
   *
   * Skips if a chat is already in progress (history non-empty) — the
   * prompt's job is the cold open, not interrupting a live thread.
   * No-op if no sendHandler is registered (mock mode etc.).
   */
  submitFromPrompt(payload: { precedingAssistantMessage?: string; userText: string }): void;
  /**
   * Append text to the most-recently-appended user bubble. Used in
   * realtime voice mode where the user transcript arrives in deltas:
   * caller mounts an empty bubble via `appendUserMessage('')` on
   * speech-start, then live-updates with `appendUserDelta` as whisper
   * deltas come in, then locks in the canonical text via
   * `finalizeUserMessage`.
   */
  appendUserDelta(text: string): void;
  /** Replace the live-updating user bubble's text with the canonical final transcript. */
  finalizeUserMessage(text: string): void;
  handleChatEvent(event: ChatEvent): void;
  setStreaming(streaming: boolean): void;
  onSend(handler: (message: string, opts: { fromVoice: boolean; hidden?: boolean }) => void): void;
  /**
   * Fires whenever the content viewer opens a playlist item —
   * regardless of trigger (LLM tool, recommendation card, agent-link
   * chip, drawer click). Host wires this to a fire-and-forget POST
   * so the admin Conversations inbox Content tab captures opens that
   * bypass the LLM tool dispatch (agent links etc.).
   */
  onContentOpened(handler: (it: PlaylistItem) => void): void;
  onContentProgress(handler: (p: ContentProgress) => void): void;
  /**
   * Fires when the visitor COMPLETES a booking in the in-widget modal
   * (provider postMessage, e.g. Calendly `event_scheduled`) — the
   * confirmed-meeting ground truth, distinct from merely opening the modal.
   * Embedder posts a `meeting-booked` session event.
   */
  onMeetingBooked(handler: (p: { provider: string; bookingUrl: string }) => void): void;
  /** v3-B — deterministic slot booking: the tap POSTs /book-slot via this
   *  handler (index owns apiBaseUrl/sessionId); ui renders the outcome.
   *  `slot-taken` failures carry fresh times → chips re-offer inline. */
  onBookSlot(handler: (body: SlotBookBody) => Promise<SlotBookOutcome>): void;
  /** Slice 3 — the picker's data feed: embedder fetches the full
   *  availability window (GET /meeting-options), optionally paged via
   *  fromIso. Null = fetch failed; the picker keeps its seeded slots. */
  onSlotOptions(
    handler: (fromIso?: string) => Promise<{
      startsIso: string[];
      durationMinutes: number;
      needsEmail: boolean;
      nextFromIso: string | null;
    } | null>,
  ): void;
  /** Slot-ladder telemetry (rep/booking slice 1) — the rungs only the
   *  client observes: a chip tap, and the ladder degrading to the embed
   *  modal. Embedder posts them as session events. */
  onSlotFunnel(
    handler: (
      type:
        | 'meeting-slot-tapped'
        | 'meeting-fell-to-modal'
        | 'meeting-email-shown'
        | 'meeting-email-given',
      payload: Record<string, unknown>,
    ) => void,
  ): void;
  /** Prequalify P-2 — post-email route-readiness check (+ gap-form
   *  answers). Embedder POSTs /route-readiness; null = fall back to
   *  the pre-email routing. */
  onRouteReadiness(
    handler: (
      answers?: Record<string, string>,
      force?: boolean,
    ) => Promise<SlotReadiness | null>,
  ): void;
  /**
   * Hook for the "Talk Now" greeter button. Embedder returns an audio
   * URL (typically by hitting /v1/agents/:id/speak with the agent's
   * welcomeMessage) and the widget plays it via the existing audio
   * pipeline. Resolving null = no-op (e.g. voice disabled).
   */
  onTalkNow(handler: () => Promise<string | null>): void;
  /**
   * Hook for the realtime voice toggle. Receives 'start' when the
   * visitor enters voice mode and 'stop' when they leave. The embedder
   * owns the realtime session lifecycle; the UI just reflects state.
   * When unset, voice mode is disabled — Talk Now falls back to the
   * legacy greeter behaviour.
   */
  onVoiceToggle(handler: (next: 'start' | 'stop') => void): void;
  /**
   * Reflect realtime voice state. Updates Talk Now button labels,
   * adds a `--voice` class on the root for CSS hooks, and toggles the
   * mic-icon visibility in the input area.
   */
  setVoiceModeActive(active: boolean): void;
  /**
   * Hook for the embedder to handle journey-answer submissions
   * (option clicks). Embedder calls /v1/agents/:publicId/journey-answer
   * and pushes the next step back via `handleChatEvent({type:'journey-step',…})`.
   */
  onJourneyAnswer(
    handler: (stepId: string, value: string | string[] | null) => void,
  ): void;
  /**
   * Hook for the inline contact-capture form (requestContactCapture
   * tool). Embedder calls /v1/contact-capture; widget shows submit
   * state inline. Promise rejection surfaces an error message under
   * the form so the visitor can fix + retry.
   */
  onContactCapture(
    handler: (
      fields: {
        name?: string;
        email?: string;
        phone?: string;
        company?: string;
      },
      /** Capture ladder rung — 'callback' pages a rep now (slice 3);
       *  'booking' is the email-first step, capture without an alert. */
      intent?: 'contact' | 'callback' | 'booking',
      /** F-3 — the agent form this submit fulfils. */
      formId?: string,
      /** F-4 — non-quartet answers keyed by the form's field keys. */
      answers?: Record<string, string>,
    ) => Promise<void>,
  ): void;
  /**
   * Slice F — hook for Daily call lifecycle. Embedder receives
   * decline/end intents and POSTs the matching chat-api endpoint
   * (/v1/sessions/:id/call/:callId/decline or /end). The widget owns
   * the Daily Prebuilt overlay mount; embedder owns the network.
   */
  onCallLifecycle(
    handler: (event: { kind: 'declined' | 'ended'; callId: string }) => void,
  ): void;
  /**
   * Hook for cross-page highlight handoff. Embedder receives the queued
   * highlight metadata, persists it to sessionStorage, and navigates
   * the host page. The next-page widget picks the highlight up on boot.
   * If unset, cross-page highlights silently fall back to same-page
   * text-substring lookup (which usually fails on a different URL).
   */
  /**
   * Called synchronously RIGHT BEFORE any agent-driven host-page
   * navigation (navigate-url tool, agent-link cards). The embedder
   * persists the session snapshot here — without it the page unloads
   * MID-TURN and the next page cold-mounts with no conversation (live
   * catch 2026-07-13: agent navigated home → /pricing and arrived as
   * a fresh visitor; sessionStorage was empty because the post-turn
   * persist never got to run).
   */
  onBeforeHostNavigate(handler: (next?: URL) => void): void;
  onHighlightNavigate(
    handler: (highlight: { text: string; reason?: string; url: string }) => void,
  ): void;
  /** Replay a queued highlight after page-load — see `onHighlightNavigate`. */
  applyPendingHighlight(highlight: { text: string; reason?: string; url?: string }): void;
  showError(message: string): void;
  /**
   * Snapshot of the widget's current view, state, and experience-overlay
   * flag for inclusion in each chat request. Lets the agent know whether
   * the visitor is on the rail, in conversation, or actively watching
   * content — without this, the agent thinks the visitor is "still
   * watching a video" after they've closed the viewer and navigated
   * elsewhere.
   */
  getWidgetStateSnapshot(): {
    view: AgentView;
    state: string;
    experienceActive: boolean;
  };
  /** The visitor has text in the composer — typing, not quiet (quiet-nudge.ts). */
  hasDraft(): boolean;
  /** Something is open in the content viewer — reading/watching, not quiet. */
  hasContentOpen(): boolean;
  /**
   * Notify the widget that the host page just changed (SPA nav). Forces
   * the AgentLinks island to re-evaluate `{feature}` substitution
   * against the new page's `<meta name="agent-feature">` tag.
   */
  notifyPageChange(): void;
}

export interface MountOptions {
  initialLayout: Layout;
  /** SIDEBAR rail: push the host page aside ('dock', default) or overlay it. */
  railMode?: RailMode;
  /** Where static assets live (videos/, placeholders/). Resolved by config. */
  assetBaseUrl: string;
  /** The agent-led tour door — the live face keeps its own backdrop (live-key.ts). */
  presentDoor?: boolean;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

/**
 * Phone check for transition context + yield-viewport behaviors.
 * M1 (mobile UX program): now the LIVE viewport device signal — the old
 * width-only matchMedia treated narrow desktop windows as phones and
 * never re-evaluated after boot; this reflects rotation/resize and
 * requires an actual coarse pointer.
 */
function isMobile(): boolean {
  return deviceSig.value === 'phone';
}


function isTablet(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(max-width:1023px)').matches;
}

/**
 * Walk up from `el` looking for ancestors that are visually hidden
 * (closed accordions, inactive tabs, aria-hidden regions). Trigger
 * each one to open by clicking its toggle. Best-effort — patterns vary
 * per-customer, but these heuristics cover Bootstrap, Headless UI,
 * Radix, shadcn, Bulma, Tailwind UI, and plain `<details>`.
 *
 * Returns true if any expansion occurred (caller waits for animation
 * before highlighting).
 *
 * Why this exists: legacy's most painful failure mode was "agent says
 * 'let me show you' and then nothing happens" because the matched FAQ
 * answer lived inside a `<div class="collapse">`. Ingest now skips
 * those, but visitors can also LAND on a page with content collapsed
 * (e.g. customer A/B tests starting all FAQs closed). Auto-expanding
 * gives us a second line of defence.
 */
function expandHiddenAncestors(el: HTMLElement): boolean {
  let didExpand = false;
  let curr: HTMLElement | null = el;
  // Cap ancestor walk at 12 — deep enough for any real DOM, short
  // enough to bail if we've drifted into <body>.
  for (let i = 0; i < 12 && curr; i++) {
    const parent: HTMLElement | null = curr.parentElement;
    if (!parent || parent.tagName === 'BODY') break;

    // <details> / <summary>: native accordion. Just set [open].
    if (parent.tagName === 'DETAILS' && !parent.hasAttribute('open')) {
      parent.setAttribute('open', '');
      didExpand = true;
    }

    // aria-expanded="false" on a button/link → the element it controls
    // is closed. Click the toggle. Bootstrap, Headless UI, hand-rolled.
    const ariaExpandedFalse = parent.querySelector<HTMLElement>(
      '[aria-expanded="false"]',
    );
    if (
      ariaExpandedFalse &&
      ariaExpandedFalse.getAttribute('aria-controls')?.length
    ) {
      const controlled = document.getElementById(
        ariaExpandedFalse.getAttribute('aria-controls')!,
      );
      if (controlled && controlled.contains(curr)) {
        ariaExpandedFalse.click();
        didExpand = true;
      }
    }

    // Radix-style data-state="closed" — find the trigger that controls
    // this branch and click it. Radix uses [data-state] on both panel
    // and trigger.
    if (parent.getAttribute('data-state') === 'closed') {
      // Trigger usually lives as a sibling labelled by aria-controls.
      const id = parent.id;
      if (id) {
        const trigger = document.querySelector<HTMLElement>(
          `[aria-controls="${id}"]`,
        );
        if (trigger) {
          trigger.click();
          didExpand = true;
        }
      }
    }

    // Bootstrap / Headless: `.collapse` without `.show`. Find a button
    // whose data-bs-target / data-target / aria-controls points to it.
    const cls = parent.className;
    if (typeof cls === 'string' && /(?:^|\s)collapse(?!\s+show)(?:\s|$)/.test(cls)) {
      const id = parent.id;
      if (id) {
        const trigger = document.querySelector<HTMLElement>(
          `[data-bs-target="#${id}"], [data-target="#${id}"], [aria-controls="${id}"]`,
        );
        if (trigger) {
          trigger.click();
          didExpand = true;
        }
      }
    }

    curr = parent;
  }
  return didExpand;
}

/** True if `el` has a non-zero rendered box and isn't display:none. */
function isVisible(el: HTMLElement): boolean {
  if (!el.isConnected) return false;
  const cs = window.getComputedStyle(el);
  if (cs.display === 'none' || cs.visibility === 'hidden' || cs.opacity === '0') {
    return false;
  }
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

/**
 * Module-level reference to the currently outlined element + its style
 * restore function. Exactly one highlight is active at a time. Cleared
 * when:
 *   - a new highlight replaces it (next agent action)
 *   - the visitor sends a new message (`appendUserMessage`)
 *   - another visitor-facing tool fires (`navigate-url`, `open-content`,
 *     `book-meeting`)
 *
 * Replaces the previous fixed 3-second auto-clear, which fired before
 * the visitor finished reading the agent's reply and felt like a flicker.
 */
let activeHighlight: { el: HTMLElement; restore: () => void } | null = null;

function clearActiveHighlight(): void {
  cursorIfLoaded()?.hideAgentCursor();
  if (!activeHighlight) return;
  try {
    activeHighlight.restore();
  } catch {
    /* element may have been removed from DOM by host */
  }
  activeHighlight = null;
}

/**
 * Visually highlight an element on the host page by its visible text.
 * Used by the agent runtime's `highlightElement` tool. Adds an outline
 * + scrolls the element into view; persists until the next agent action
 * or visitor message (see {@link clearActiveHighlight}).
 *
 * Pipeline:
 *   1. Clear any previous highlight (one outline visible at a time).
 *   2. Find element by text-substring match.
 *   3. If not visible, walk ancestors and try to expand collapsed
 *      containers (accordion, tabs, details). Wait briefly for
 *      animation, then re-check.
 *   4. Apply the outline + scroll if visible. Otherwise silently skip
 *      (page changed, host re-rendered, LLM hallucinated, etc.).
 *
 * Returns whether the highlight actually ran — caller can use this to
 * suppress verbal claims of "I'm showing you now" when delivery failed.
 */
/** Brand primary from /v1/config — the cursor chip wears it (set in setConfig). */
let brandPrimary = '';

function highlightOnHostPage(text: string, agentName = ''): boolean {
  // Always clear the previous highlight first — even if we fail to find
  // a new target, the visitor's last action (sending a message) means
  // the prior highlight has served its purpose.
  clearActiveHighlight();

  const el = findElementByText(text);
  if (!el) return false;

  const apply = () => {
    if (!isVisible(el)) return false;
    const prevOutline = el.style.outline;
    const prevOffset = el.style.outlineOffset;
    const prevTransition = el.style.transition;
    const prevBoxShadow = el.style.boxShadow;
    // Initial pulse: stronger glow that fades to a steady outline.
    // Without the pulse, a 3px outline on a busy page can be missed
    // — visitor says "I'm not sure how long the highlight lasted"
    // because they didn't catch the moment it appeared.
    // Brand primary when the config carries one — the outline, the halo
    // and her cursor chip then read as one gesture in the customer's colour.
    const tone = brandPrimary || '#4f46e5';
    const toneRgb = hexToRgb(tone) ?? '79, 70, 229';
    el.style.outline = `3px solid ${tone}`;
    el.style.outlineOffset = '4px';
    el.style.boxShadow = `0 0 0 8px rgba(${toneRgb}, 0.35)`;
    el.style.transition = 'outline 200ms ease, outline-offset 200ms ease, box-shadow 700ms ease';
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Her cursor glides to the outline so the visitor sees WHO is pointing.
    if (agentName) {
      const color = brandPrimary;
      void loadCursor()
        .then((m) => {
          if (activeHighlight?.el === el) m.showAgentCursor(el, agentName, color);
        })
        .catch(() => undefined); // the outline alone still delivers
    }
    // Fade the pulse halo after 700ms — outline itself stays put.
    const pulseTimer = window.setTimeout(() => {
      el.style.boxShadow = `0 0 0 0px rgba(${toneRgb}, 0)`;
    }, 700);
    activeHighlight = {
      el,
      restore: () => {
        window.clearTimeout(pulseTimer);
        el.style.outline = prevOutline;
        el.style.outlineOffset = prevOffset;
        el.style.boxShadow = prevBoxShadow;
        el.style.transition = prevTransition;
      },
    };
    return true;
  };

  if (isVisible(el)) {
    return apply();
  }

  // Element matched by text but isn't visible — try the auto-expand
  // walker. Wait for animations (250ms covers Bootstrap collapse @
  // 200ms + a small margin) before re-checking.
  const expanded = expandHiddenAncestors(el);
  if (!expanded) {
    return false;
  }
  setTimeout(() => {
    apply();
  }, 250);
  return true;
}

export function mountWidget(mount: HTMLElement, opts: MountOptions): WidgetUI {
  injectStyles();
  brandPrimary = ''; // module state — a fresh mount starts without a brand tone

  const assetBase = opts.assetBaseUrl.replace(/\/$/, '');
  const DEFAULT_AVATAR = `${assetBase}/placeholders/images/agent-avatar.png`;

  const root = el('div', 'sp-root');
  root.setAttribute('data-layout', opts.initialLayout);
  // No transitions on the very first paint — the initial (or resumed)
  // layout must render at its final shape, not morph into it.
  root.classList.add('sp-booting');
  requestAnimationFrame(() => requestAnimationFrame(() => root.classList.remove('sp-booting')));
  // M1 — live data-device / data-orientation attrs; new CSS keys on these
  // instead of repeating media-query compounds.
  installViewportAttrs(root);

  // ---------- Reactive chrome state ----------
  // Signals drive the pre-launch chrome (Launcher / WelcomeBubble /
  // Backdrop) via Preact islands. `setConfig` mutates the signals when
  // /v1/config arrives; launcher click toggles welcomeVisible. Mirrors
  // the legacy imperative mutations (launcherAvatarImg.src = …,
  // welcomeBubble.style.display = …) without holding DOM refs.
  const firstNameSig = signal<string>('');
  const avatarUrlSig = signal<string>(DEFAULT_AVATAR);
  const welcomeTextSig = signal<string>('');
  const welcomeVisibleSig = signal<boolean>(false);
  // Standalone Experience (P2): the widget is the whole page (share link /
  // experience embed), so minimize + close are hidden — collapsing to the
  // launcher over a blank page makes no sense.
  const standaloneExperienceSig = signal<boolean>(false);
  // Directed Experience (P2): once suppressed, the static inbound welcome
  // ("how can I help?") never sets or shows again — checked at set-time in
  // setConfig, so it holds regardless of when clearWelcome is called
  // relative to /config arriving.
  let welcomeSuppressed = false;
  // Actions on a phone: the agent's first move is up as the bar's line.
  let barNoticeUp = false;
  // Header signals — agent name in the title bar, current layout drives
  // fullscreen/sidebar button visibility.
  const agentNameSig = signal<string>('Chat');
  // Agent role/title shown lighter beside the name. Defaults to a sensible
  // "AI Agent"; a per-agent customer-set title can override it once that
  // config field exists.
  const agentTitleSig = signal<string>('AI Agent');
  // False until /config lands and the real identity is applied. Gates the
  // header identity (avatar + name + role) so it FADES IN with real data
  // instead of flashing the placeholder ("Chat · AI Agent" + default avatar)
  // and snapping to the real values a beat later.
  const configReadySig = signal<boolean>(false);
  // Safety: reveal the header even if /config never lands (fetch failed /
  // offline) so the identity isn't hidden forever.
  setTimeout(() => {
    configReadySig.value = true;
  }, 2500);
  const currentLayoutSig = signal<Layout>(opts.initialLayout);
  // Mirror of widgetState.view as a signal so the Header (Preact island)
  // re-renders its action buttons when the view changes. Updated by
  // setView() and applyAction() — kept in sync with widgetState.view.
  const viewSig = signal<AgentView>(viewFromLegacyLayout(opts.initialLayout).view);
  // Footer signals.
  const hasInputTextSig = signal<boolean>(false);
  const recordingSig = signal<boolean>(false);
  const bookVisibleSig = signal<boolean>(false);
  // Resolved booking URL — set from config.meeting.bookingUrl when /config
  // arrives. The .sp-book-btn click handler reads this to open the URL
  // directly (the LLM still has its own bookMeeting tool path, but the
  // visitor-facing button shouldn't depend on a chat turn to work).
  let meetingBookingUrl: string | null = null;
  // U1 fallback for servers that send no `cta` on `done`.
  let meetingEnabledForCta = false;
  // What the Book button says on the visitor's behalf when no link can
  // open directly — a plain, visible chat turn (never hidden) so the
  // thread reads honestly and the agent's booking tool takes it from
  // there.
  const BOOK_MEETING_ASK = "I'd like to book a meeting.";
  const suggestionsSig = signal<Array<{ label: string }>>([]);
  /**
   * Agent CTA — dedicated wide button between suggestions and input.
   * Set once from /config; cleared on first user submit. Hidden on
   * Engage via CSS (deferred to multi-link chips feature).
   */
  const ctaSig = signal<{ text: string; link: string } | null>(null);
  /**
   * True once ANY content has entered the session (openContent /
   * recommendations). Gates the header's fullscreen button — expanding
   * into the FULL content experience with an empty playlist lands the
   * visitor in a blank shell with no context for the transition
   * (David 2026-07-13). The button appears when there is something to
   * expand INTO.
   */
  const hasContentSig = signal<boolean>(false);
  /**
   * Agent Links — up to 4 starter-action buttons rendered above the
   * chat body on Engage + Rail surfaces. Set once from /config; the
   * AgentLinks Preact island gates visibility on chatStartedSig (hides
   * after first user message) + viewSig (only engage / rail). pageNonce
   * forces re-render on SPA nav so `{feature}` re-evaluates against the
   * new page's `<meta name="agent-feature">` tag.
   */
  const linksSig = signal<AgentLinkResponseItem[]>([]);
  const linksPageNonceSig = signal<number>(0);
  // KB-derived URL → topic map from /config. AgentLinks reads this to
  // resolve `{feature}` substitution against the visitor's current URL
  // — replaces the v1 `<meta name="agent-feature">` requirement that
  // customers couldn't realistically add to every page.
  const pageTopicsSig = signal<{ url: string; topic: string }[]>([]);
  const sendDisabledSig = signal<boolean>(false);
  // Video-greeter state — shared between big + mini avatars so they
  // stay in lock-step (audio level, speaking, listening, status label).
  const greeterUrlSig = signal<string | null>(null);
  // Composed-opening chips are live (pre-conversation) — AgentLinks
  // stand down so there's one chip system at a time.
  const openingChipsSig = signal<boolean>(false);
  const speakingSig = signal<boolean>(false);
  const listeningSig = signal<boolean>(false);
  const audioLevelSig = signal<number>(0);
  const statusLabelSig = signal<string>('Talk Now');
  const chatStartedSig = signal<boolean>(false);
  // "Joining" window for the summon/load overlay: true from voice-connect
  // until the live face actually binds (or a timeout). The bot publishes its
  // video a beat AFTER audio joins, so keying the summon on voiceState==
  // 'connecting' alone makes it vanish too early, leaving an empty avatar gap.
  const joiningSig = signal<boolean>(false);
  // True once the live avatar's <video> has PAINTED its first frame (not just
  // received the track). The bot publishes the track ~1-2s before frames
  // actually render; ending the summon on track-received flashes a white card.
  // The VideoGreeter sets this from requestVideoFrameCallback / 'playing'.
  const liveReadySig = signal<boolean>(false);
  let faceReadySent = false;
  // True once the realtime voice path is wired (onVoiceToggle registered) —
  // gates the chat-mode "Speak with {name}" CTA.
  const voiceAvailableSig = signal<boolean>(false);
  // Mobile UX program rollout flag (ui.mobileV2 on /config). EVERY visual
  // change in the program gates on this; the kill switch is the server env.
  // Stamped as .sp-mv2 on the root so program CSS keys on
  // `.sp-root.sp-mv2[data-device=phone] …`.
  const mobileV2Sig = signal<boolean>(false);
  // M2/D2 — voice as the start bar's primary affordance (flag + mode ≥C,
  // every device: D2 makes the bar the desktop-default presence too).
  // Deployment-mode aware (David 2026-07-14): the LABELED Talk button is
  // the voice entry only when there's no video persona. With a video
  // greeter on screen, its "Speak with {name}" pill IS the voice CTA —
  // the input demotes to the quiet mic glyph so there's never two
  // labeled voice buttons. Chat-only agents have
  // voiceAvailable=false and show neither.
  const talkPrimarySig = computed(
    () =>
      mobileV2Sig.value &&
      voiceAvailableSig.value &&
      // Phone sheet (slice 2): the Speak pill leaves the thread, so the
      // composer's Talk button carries the call whatever the greeter.
      (greeterUrlSig.value === null ||
        (deviceSig.value === 'phone' && currentLayoutSig.value === LAYOUTS.SIDEBAR)),
  );
  // Program flag class — all program CSS keys on .sp-mv2.
  effect(() => {
    root.classList.toggle('sp-mv2', mobileV2Sig.value);
  });
  // Phase 6 CSS cleanup — the deployed bar's OWNED context class. Every
  // bar rule keys on .sp-bar instead of re-deriving
  // .sp-mv2...[data-layout=input] per selector (the specificity
  // arms-race behind the styling whack-a-mole). Reactive to both the
  // flag (setConfig) and every layout change (setLayout writes
  // currentLayoutSig).
  effect(() => {
    root.classList.toggle(
      'sp-bar',
      mobileV2Sig.value && currentLayoutSig.value === LAYOUTS.INPUT,
    );
  });
  // The phone sheet's OWNED context class (slice 2) — same pattern as .sp-bar.
  effect(() => {
    root.classList.toggle(
      'sp-sheet',
      mobileV2Sig.value && deviceSig.value === 'phone' && currentLayoutSig.value === LAYOUTS.SIDEBAR,
    );
  });
  // M6 — keyboard-up hook: CSS can't read --sp-kb-offset, so mirror it as
  // a root class (drives the typing-overlay in FULL portrait and the phone
  // sheet's keyboard posture). Thresholded: a URL-bar collapse is not a keyboard.
  effect(() => {
    root.classList.toggle('sp-kb-up', keyboardState(kbOffsetSig.value) === 'up');
  });
  // M5 — co-pilot PiP state. sessionMinimizedSig mirrors
  // widgetState.sessionMinimized for chrome reactivity; pipVisible also
  // requires an active voice session (the PiP is a CALL surface).
  const sessionMinimizedSig = signal<boolean>(false);
  /**
   * True in the layouts that render NO header, so nothing on screen can
   * minimize. Today that is the site-agent bar (INPUT) —
   * `[data-layout=input] .sp-header{display:none}`. The live-call pill
   * grows its own minimize button here and only here.
   */
  const headerlessLayoutSig = computed(() => currentLayoutSig.value === LAYOUTS.INPUT);
  const pipVisibleSig = computed(
    () =>
      sessionMinimizedSig.value &&
      voiceState.value.name !== 'idle' &&
      voiceState.value.name !== 'error',
  );
  const pipHasLiveSig = computed(() => botVideoStream.value !== null);
  // While the PiP owns the call, the PAGE is the hero (frame 6): every
  // other widget surface yields. Without this the voice-mode rules that
  // force the big-avatar hero visible across layouts (I6) leave a ghost
  // white slab + controls floating over the page.
  effect(() => {
    root.classList.toggle('sp-pip-up', pipVisibleSig.value);
  });
  // Phase 5 (O1) — launcher badge: a collapsed toast stays reachable as a
  // red count on the launcher (pacing-friendly persistence, budget cost 0).
  const offerBadgeSig = signal<number>(0);
  // Dev-only preview (inert in prod): ?sp_voice shows chat + the Speak CTA
  // with Alex's real assets; ?sp_summon (handled in index.tsx) additionally
  // forces the connecting load card.
  if (typeof location !== 'undefined') {
    const q = new URLSearchParams(location.search);
    if (q.has('sp_voice') || q.has('sp_summon') || q.has('sp_live')) {
      avatarUrlSig.value =
        'https://pub-b83d2cb8bc8342bf98eddd853dc4acad.r2.dev/system/personas/alex/poster.jpg';
      firstNameSig.value = 'Alex';
      agentNameSig.value = 'Alex';
      voiceAvailableSig.value = true;
      configReadySig.value = true;
    }
  }
  const greeterBits: VideoGreeterBits = {
    greeterUrl: greeterUrlSig,
    speaking: speakingSig,
    listening: listeningSig,
    audioLevel: audioLevelSig,
    statusLabel: statusLabelSig,
    // Live Simli avatar track during a voice call. When present it takes
    // over both avatars as the real-time talking head (see VideoGreeter).
    liveStream: botVideoStream,
    // Summon/load overlay: blurred agent still + shimmer while voice is
    // connecting, resolving into the live face. Poster = the per-agent
    // avatar still (avatarUrlSig), not the generic fallback.
    connecting: joiningSig,
    liveReady: liveReadySig,
    posterUrl: avatarUrlSig,
    agentName: firstNameSig,
    // Visitor's own mic mute state — drives the live-call pill's mic button.
    micMuted: computed(() => voiceState.value.micMuted),
    // Live presence: the voice state name drives the on-face identity chip
    // (Listening / Speaking / Thinking) and which glow the avatar wears.
    voiceStatus: computed(() => voiceState.value.name),
    // Glow intensity — the active speaker's level (bot when speaking, visitor
    // when listening), so the ring breathes with whoever's talking.
    voiceLevel: computed(() => {
      const v = voiceState.value;
      if (v.name === 'speaking') return v.botAudioLevel;
      if (v.name === 'listening') return v.micAudioLevel;
      return 0;
    }),
  };

  // M4 — the ONE persistent home of the greeter + live <video> elements.
  // Re-parented between the big/mini dock anchors (below) as the layout
  // changes; media lifecycle lives in avatar-stage.ts. liveReady survives
  // dock moves (invariant I3).
  const greeterPausedSig = signal<boolean>(false);
  // Key the live stream in the box when the persona is Beam-minted and
  // this isn't the Beam view (live-key.ts) — set from config + view.
  const keyLiveSig = signal<boolean>(false);
  const avatarStage = createAvatarStage({
    greeterUrl: greeterUrlSig,
    speaking: speakingSig,
    liveStream: botVideoStream,
    liveReady: liveReadySig,
    greeterPaused: greeterPausedSig,
    keyLive: keyLiveSig,
  });

  // Root-level CSS hook for the live video avatar. The stream arrives a
  // beat AFTER voice starts (the bot joins, then publishes its video
  // track), so we key off the stream signal — not the voice-mode toggle.
  // `.sp-avatar-live` lets the stylesheet keep the face visible +
  // prominent across every layout's greeter hide/shrink rules (engage
  // collapses the avatar on start, voice-mode hides the greeter <video>,
  // etc.). See styles.ts "Live video avatar overrides".
  // Manage the join window. Starts on 'connecting', ends when the live face
  // binds, or on idle/error, or after a 7s safety timeout (so an audio-only
  // agent — no video track ever — doesn't sit on the summon forever).
  let joinTimer: ReturnType<typeof setTimeout> | null = null;
  const endJoining = (): void => {
    joiningSig.value = false;
    if (joinTimer) {
      clearTimeout(joinTimer);
      joinTimer = null;
    }
  };
  const armJoinTimeout = (ms: number): void => {
    if (joinTimer) clearTimeout(joinTimer);
    joinTimer = setTimeout(endJoining, ms);
  };
  effect(() => {
    const name = voiceState.value.name;
    const trackBound = botVideoStream.value !== null;
    // Reset the painted flag once the track is gone (call ended) so the next
    // call starts fresh and shows its summon.
    if (!trackBound) liveReadySig.value = false;
    // "live" = the face is actually PAINTING, not just the track bound. This
    // is what ends the summon and flips on the controls/chip/glow, so they
    // appear with the face — never over a white warming-up card.
    const live = liveReadySig.value;
    root.classList.toggle('sp-avatar-live', live);
    // Tell the bot the face is on screen — it holds its first line for
    // this (first-turn hold, 2026-10-26). Once per paint, not per re-run.
    // On the Beam stage the paint happens behind the walk-in clip with
    // her audio still held, so the stage sends it at the reveal instead
    // (onRevealed below, 2026-10-30).
    if (live && !faceReadySent && !boundlessHandle) {
      faceReadySent = true;
      notifyVoiceVisitorReady('face-ready');
      markVoiceFaceReady();
    } else if (!live) {
      faceReadySent = false;
    }
    // The face must not paint above a keyboard-shrunk viewport.
    if (live && deviceSig.value === 'phone') dismissKeyboard(document);
    if (live || name === 'idle' || name === 'error') {
      // Face is painting, or the call ended — drop the loading stage.
      endJoining();
    } else if (name === 'connecting' || trackBound) {
      // Hold the loading stage through the WHOLE join: the connecting window
      // (bot cold-spawn can take ~8s — the old 7s failsafe dropped the summon
      // into a bare gap BEFORE the face arrived) AND the warming-up window
      // after the video track binds but before the first frame paints. 15s
      // backstop so a stalled spawn/paint can't hang the summon forever.
      joiningSig.value = true; // signal dedups; write-only
      armJoinTimeout(15000);
    } else if (name === 'listening' || name === 'speaking') {
      // Bot is connected (audio) but no video track has appeared. Give it a
      // short grace for a live track to publish; if none shows, this is an
      // audio-only agent — end the summon promptly instead of sitting on the
      // 15s backstop. If a track binds during the grace, the branch above
      // re-holds until the face paints.
      armJoinTimeout(2500);
    }
  });
  // Surface the big avatar (full card) through the WHOLE join so the summon
  // overlay has a stage even before the greeter clip / live face exists, and
  // it doesn't flicker away in the connecting→live gap. Separate effect so the
  // join-management effect above never reads joiningSig (avoids a self-loop).
  effect(() => {
    root.classList.toggle('sp-voice-connecting', joiningSig.value);
  });

  // ---------- ICON launcher (Preact island) ----------
  const launcher = el('div', 'sp-launcher-anchor');
  mountPreactIsland(
    launcher,
    <Launcher
      bits={{ firstName: firstNameSig, avatarUrl: avatarUrlSig }}
      extras={{
        mobileV2: mobileV2Sig,
        hasGreeter: computed(() => greeterUrlSig.value !== null),
        liveDot: voiceAvailableSig,
        dock: {
          register(anchor) {
            avatarStage.registerDock('launcher', anchor);
            if (currentLayoutSig.value === LAYOUTS.ICON) avatarStage.dock('launcher');
          },
          release() {
            avatarStage.unregisterDock('launcher');
          },
        },
        onIdlePause: (paused) => {
          // Battery hygiene is a LAUNCHER concern — never pause the shared
          // stage when it's docked elsewhere (opening the panel hides the
          // launcher, which must not blank the sheet hero — David's
          // 2026-07-08 mobile screenshot).
          if (paused && avatarStage.activeDock() !== 'launcher') return;
          greeterPausedSig.value = paused;
        },
        badge: offerBadgeSig,
      }}
      onClick={() => {
        welcomeVisibleSig.value = false;
        offerBadgeSig.value = 0; // opening = the offer is being seen
        // M8 ladder — a non-phone launcher click opens the conversation
        // surface directly = accept. A PHONE tap only deploys the bar
        // (still browsing), so it counts as a bar impression instead
        // (the setLayout choke point emits it).
        if (!widgetState.chatStarted && deviceSig.value !== 'phone') {
          emitPresenceAccepted('launcher', 'clicked');
        }
        // Desktop launcher click = straight to the RAIL (David
        // 2026-07-07: the bar is a deployment presence, not a hop —
        // hover does the invite work via the bubble, click converses).
        if (
          mobileV2Sig.value &&
          deviceSig.value !== 'phone' &&
          widgetState.view === 'site-agent' &&
          !widgetState.chatStarted
        ) {
          applyAction({ type: 'open' }); // dock → prompt (honest state)
          setLayout(LAYOUTS.SIDEBAR); // rail posture; submit converges
          return;
        }
        if (mobileV2Sig.value && widgetState.view === 'engage') {
          const cold =
            !widgetState.chatStarted &&
            (widgetState.state === 'compact' || widgetState.state === 'dock');
          if (deviceSig.value === 'phone' && cold) {
            // Mobile finals frame 3: the launcher tap opens the START
            // BAR — a visual posture over the compact state (the state
            // machine has no bar tier for engage). The bar's own
            // affordances resume normal transitions: submit/talk →
            // open → sheet; dismiss → minimize → dock → launcher.
            applyAction({ type: 'open' });
            setLayout(LAYOUTS.INPUT);
            return;
          }
          // Desktop/tablet: plain open — engage keeps its panel
          // (D3-as-engage reverted, David 2026-07-07).
        }
        applyAction({ type: 'open' });
      }}
    />,
  );

  // ---------- FULL-layout backdrop (Preact island) ----------
  // Click outside the FULL content panel = end the experience overlay.
  // The state machine restores the underlying view state.
  const backdrop = el('div', 'sp-full-backdrop-anchor');
  mountPreactIsland(
    backdrop,
    <Backdrop
      onClick={() => {
        if (!widgetState.experienceActive) return;
        // Standalone experience = the viewer IS the page; peeling the
        // content would strand the visitor with no way back. Backdrop
        // click is a no-op here (minimize/close/sidebar are hidden too).
        if (standaloneExperienceSig.value) return;
        // During a live voice call the source IS the rail (the avatar lives
        // there). Plain endContent only peels the overlay and preserves the
        // underlying view-state — which for Site Agent is `conversation`
        // (EXPANDED/wide), so the panel flashes wide before voice logic
        // settles it back to the rail. Peel STRAIGHT to the rail instead
        // (same as the header sidebar button) so there's no wide intermediate.
        if (voiceModeActive) {
          applyAction({ type: 'experienceToSidebar' });
        } else {
          applyAction({ type: 'endContent' });
        }
      }}
    />,
  );

  // ---------- Mini video avatar (Preact island) ----------
  // INPUT layout's 140px circle to the left of the input pill — plays
  // the agent's looping video greeter, falls back to a static avatar
  // PNG when no greeter URL exists or the video errors. Status overlay
  // (sound-bars + "Talk Now" label) lives inside the same component
  // so legacy structure is preserved.
  const miniAvatar = el('div', 'sp-mini-avatar-anchor');
  // M2 — start-bar tap affordances (frame 3): avatar = identity → opens
  // the sheet. Never a second Talk button. Flag + phone only.
  miniAvatar.addEventListener('click', (e) => {
    if (!(mobileV2Sig.value && deviceSig.value === 'phone')) return;
    // The status button (hidden on phone, but defensive) owns its own click.
    if ((e.target as HTMLElement).closest('button')) return;
    applyAction({ type: 'expand' });
  });
  mountPreactIsland(
    miniAvatar,
    <VideoGreeter
      bits={greeterBits}
      size="mini"
      stage={avatarStage}
      dockName="mini"
      fallbackUrl={DEFAULT_AVATAR}
      bars={{ height: '14px', barWidth: '2.5px', gap: '2px' }}
      onStatusClick={(e) => {
        e.stopPropagation();
        handleTalkButton();
      }}
    />,
  );

  // ---------- Welcome bubble (Preact island) ----------
  // Above panel pre-interaction. Visibility + text + avatar all driven
  // by signals; the launcher click handler clears welcomeVisibleSig.
  const welcomeBubble = el('div', 'sp-welcome-bubble-anchor');
  mountPreactIsland(
    welcomeBubble,
    <WelcomeBubble
      bits={{
        firstName: firstNameSig,
        avatarUrl: avatarUrlSig,
        text: welcomeTextSig,
        visible: welcomeVisibleSig,
      }}
    />,
  );

  // ---------- Panel (header + body + footer) ----------
  const panel = el('div', 'sp-panel');
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-label', 'Chat');

  // ----- Header (Preact island) -----
  // Agent avatar + name, voice status badge slot, minimize/sidebar/
  // fullscreen/close buttons. fullscreen/sidebar visibility derives
  // from currentLayoutSig; click handlers fire the appropriate layout
  // transition. The voice-badge anchor is rendered as part of the
  // header tree and queried below for the WidgetUI return shape.
  const header = el('div', 'sp-header-anchor');
  mountPreactIsland(
    header,
    <Header
      bits={{
        agentName: agentNameSig,
        agentTitle: agentTitleSig,
        avatarUrl: avatarUrlSig,
        currentLayout: currentLayoutSig,
        view: viewSig,
        hasContent: hasContentSig,
        isTablet,
        standaloneExperience: standaloneExperienceSig,
      }}
      handlers={{
        onMinimize: () => {
          // M5 (behind ui.mobileV2): minimizing DURING an active voice
          // session drops to the co-pilot PiP instead of just shrinking —
          // the call continues, the page becomes the hero (frame 6).
          const voiceActive =
            voiceState.value.name !== 'idle' && voiceState.value.name !== 'error';
          if (mobileV2Sig.value && voiceActive) {
            applyAction({ type: 'minimizeSession' });
            return;
          }
          applyAction({ type: 'minimize' });
        },
        onSidebar: () => {
          // From the content viewer (experience overlay) the sidebar
          // button peels the overlay and lands the visitor in the
          // view's SIDEBAR-layout state — site-agent → rail, the others
          // → open. Routes through applyAction so data-state stays in
          // sync (see feedback_data_state_debug memory).
          if (widgetState.experienceActive) {
            applyAction({ type: 'experienceToSidebar' });
            return;
          }
          // Normal (non-experience) sidebar press on Site Agent: same
          // semantics as agentNavigate (conversation → rail).
          if (widgetState.view === 'site-agent') {
            applyAction({ type: 'agentNavigate' });
          } else {
            setLayout(LAYOUTS.SIDEBAR);
          }
        },
        onFullscreen: () => applyAction({ type: 'showContent' }),
        onClose: () => {
          // Close = tear down voice mode if it's running. Without this
          // the realtime WS stays open after dismiss, the model keeps
          // generating, and audio keeps playing. Minimize intentionally
          // does NOT stop voice (the visitor may want to keep talking).
          if (voiceModeActive && voiceToggleHandler) voiceToggleHandler('stop');
          // Phase 3 auto-open etiquette: closing an auto-opened rail
          // sticks for the session (the discover path boots docked).
          if (mobileV2Sig.value && widgetState.view === 'rail') {
            try {
              sessionStorage.setItem('sp_rail_dismissed', '1');
            } catch {
              /* ignore */
            }
          }
          applyAction({ type: 'close' });
        },
      }}
    />,
  );
  // Voice status badge anchor — the voice surface queries this slot
  // from the header to mount its Preact island.
  const voiceBadgeAnchor = header.querySelector<HTMLElement>(
    '.sp-voice-badge-anchor',
  )!;

  // ----- BIG video avatar (Preact island) -----
  // Skyscraper hero above the message list (EXPANDED / SIDEBAR / FULL
  // layouts). Defaults to 200px tall full-width; shrinks to a 52px
  // status bar once chatStartedSig flips so it morphs into a header.
  // Hidden entirely when no greeter URL is configured.
  const bigAvatar = el('div', 'sp-big-avatar-anchor');
  mountPreactIsland(
    bigAvatar,
    <VideoGreeter
      bits={greeterBits}
      size="big"
      stage={avatarStage}
      dockName="big"
      chatStarted={chatStartedSig}
      fallbackUrl={DEFAULT_AVATAR}
      bars={{ height: '16px', barWidth: '2.5px', gap: '2.5px' }}
      onStatusClick={(e) => {
        e.stopPropagation();
        handleTalkButton();
      }}
      // Live-call controls pill (shows only while a live face streams):
      // mute toggles the visitor's own mic, End stops voice via the same path
      // as "End voice". No source picker (v2 — OS-default mic for now).
      onToggleMute={() => setVoiceMicMuted(!voiceState.value.micMuted)}
      onEndCall={() => {
        if (voiceModeActive && voiceToggleHandler) voiceToggleHandler('stop');
      }}
      // Minimize lives on the HEADER wherever there is one. The bar hides
      // the header, so a call started there had mute and End and no way
      // down — leaving the co-pilot PiP unreachable from the surface most
      // calls begin on (prod 2026-10-24). Pass the handler only for the
      // headerless layout so every other surface keeps the original rule.
      onMinimize={
        headerlessLayoutSig.value
          ? () => applyAction({ type: 'minimizeSession' })
          : undefined
      }
    />,
  );

  // M4 — dock the avatar stage by layout: INPUT shows the mini circle,
  // every other layout the big hero. Mirrors which island was VISIBLE
  // before the stage existed (CSS shows exactly one per layout), so this
  // is dark. Teaser/PiP docks join in Phases 2/5.
  // ONE routing function: every temporary-dock release (teaser Card
  // close, PiP restore) must come back through this, or the stage gets
  // stranded in a hidden dock (the docked launcher loses its face and
  // the Card hero goes blank — David-caught 2026-07-06).
  const dockForLayout = (): AvatarDockName => {
    if (currentLayoutSig.value === LAYOUTS.INPUT) return 'mini';
    if (currentLayoutSig.value === LAYOUTS.ICON && mobileV2Sig.value) return 'launcher';
    return 'big';
  };
  effect(() => {
    const layout = currentLayoutSig.value;
    // Read mobileV2Sig unconditionally so the effect re-runs when the
    // flag lands post-mount (config is async) — dockForLayout reads it
    // behind an ICON check, which would otherwise drop the subscription.
    void mobileV2Sig.value;
    // While the PiP owns the call it owns the STAGE — the minimize
    // layout change must not steal the face back into a hidden dock
    // (landscape lost this race; portrait happened to win it). When
    // the PiP closes this effect re-runs and re-docks by layout.
    if (!pipVisibleSig.value) avatarStage.dock(dockForLayout());
    // Leaving the launcher clears its battery pause — the hero must play.
    if (layout !== LAYOUTS.ICON && greeterPausedSig.value) {
      greeterPausedSig.value = false;
    }
  });

  // ----- Co-pilot PiP (M5, Preact island; body-level overlay) -----
  // Rides OUTSIDE the panel (the page is the hero while minimized).
  const pipAnchor = el('div', 'sp-pip-anchor');
  mountPreactIsland(
    pipAnchor,
    <CopilotPip
      bits={{
        visible: pipVisibleSig,
        hasLiveVideo: pipHasLiveSig,
        micMuted: computed(() => voiceState.value.micMuted),
        agentName: firstNameSig,
      }}
      handlers={{
        onRestore: () => applyAction({ type: 'restoreSession' }),
        onToggleMute: () => setVoiceMicMuted(!voiceState.value.micMuted),
        dock: {
          register(anchor) {
            avatarStage.registerDock('pip', anchor);
            avatarStage.dock('pip');
          },
          release() {
            avatarStage.unregisterDock('pip');
            avatarStage.dock(dockForLayout());
          },
        },
      }}
    />,
  );

  // ----- Body (message stream) -----
  const body = el('div', 'sp-body');
  const bodyInner = el('div', 'sp-body-inner');
  body.appendChild(bodyInner);
  // Agent Links — Preact island pinned BELOW the scrolling chat body,
  // above the input footer. Welcome message renders inside bodyInner
  // (above this), so visual order is: greeting → links → input. Links
  // hide themselves when chatStartedSig flips, when the view isn't
  // engage/rail, or when no links are configured.
  const agentLinksAnchor = el('div', 'sp-agent-links-anchor');
  mountPreactIsland(
    agentLinksAnchor,
    <AgentLinks
      bits={{
        links: linksSig,
        view: viewSig,
        pageNonce: linksPageNonceSig,
        chatStarted: chatStartedSig,
        openingChips: openingChipsSig,
        pageTopics: pageTopicsSig,
      }}
      handlers={{
        onSendMessage: (message) => {
          // Same path as a chip click — visitor sees their message
          // appended, agent responds normally.
          sendHandler?.(message, { fromVoice: false });
        },
        onOpenUrl: (url) => {
          try {
            const next = new URL(url, window.location.href);
            const rebasedLink = rebaseToVisitorOrigin(next, window.location);
            if (rebasedLink) {
              // Same-site nav, rebased onto the current host so the
              // session snapshot survives the reload.
              beforeHostNavigate?.();
              window.location.href = rebasedLink.href;
            } else {
              // Cross-origin opens a new tab — visitor stays on this
              // page but the agent should know the click happened so
              // it can acknowledge ("I opened that in a new tab — anything
              // else?") instead of repeating the offer.
              window.open(next.href, '_blank', 'noopener,noreferrer');
              // Voice → app-message; text → synthesize a short user
              // message so the agent acknowledges (matches the
              // onOpenContent pattern above).
              const sentToVoice = notifyVoiceOfVisitorAction({
                kind: 'open_url',
                label: next.href,
                url: next.href,
              });
              if (!sentToVoice && sendHandler) {
                sendHandler(
                  `(I just opened ${next.href} in a new tab.)`,
                  { fromVoice: false },
                );
              }
            }
          } catch {
            /* malformed URL — silently ignore */
          }
        },
        onOpenContent: (dest) => {
          // Synthesize an `open-content` ChatEvent with the server-
          // resolved metadata so the viewer renders the actual asset
          // (video / PDF / image / iframe) instead of "Content preview
          // not available." The /config response carried these fields
          // pre-resolved from playlist_resources / playlist_items.
          handleChatEvent({
            type: 'open-content',
            payload: {
              contentId: dest.contentId,
              contentType: 'library',
              contentExtension: dest.contentExtension,
              title: dest.title,
              url: dest.url,
              thumbnailUrl: dest.thumbnailUrl ?? null,
              embedUrl: dest.embedUrl,
            },
          });
          // Tell the agent the visitor just took the action the agent
          // curated. Voice → app-message to bot (interrupt + system
          // note + force-new-turn so the bot speaks the ack — see
          // bot.py showpilot:visitor-action). Text → synthesize a
          // short user message so the next turn reacts in chat.
          // The input briefly disables during the synthetic streaming
          // (normal chat-runtime behavior); accepted tradeoff so the
          // agent actually acknowledges the click.
          const title = dest.title || 'content';
          const sentToVoice = notifyVoiceOfVisitorAction({
            kind: 'open_content',
            label: title,
            contentTitle: title,
          });
          if (!sentToVoice && sendHandler) {
            sendHandler(`(I just opened: ${title}.)`, { fromVoice: false });
          }
        },
      }}
    />,
  );
  // Mounted to chatColumn below — pinned above the footer, sibling of
  // body so it never scrolls with chat messages.

  // ----- Footer (Preact island) -----
  // Corner actions (expand/close for INPUT pill), suggestion chips,
  // input pill with book + audio + send buttons, privacy disclaimer.
  // The textarea itself is created imperatively below and slotted
  // into the rendered footer post-mount so the 30+ `input.foo` call
  // sites stay unchanged.
  const footer = el('div', 'sp-footer-anchor');
  const inputSlotRef: { current: HTMLDivElement | null } = { current: null };
  // The textarea — created imperatively, slotted into the Preact tree
  // after mount. Legacy default placeholder is overridden by
  // /v1/config when per-agent placeholders arrive.
  const input = el('textarea', 'sp-input');
  input.placeholder = 'Send a message…';
  input.rows = 1;
  // Tracks the agent-configured placeholder. We swap input.placeholder
  // to the first suggestion's label while ghost mode is active, then
  // restore from this variable when the visitor types or sends.
  let configuredPlaceholder = input.placeholder;
  /**
   * The suggested next question currently shown as ghost placeholder
   * text. Enter on an EMPTY input sends it (David 2026-07-13: "the
   * suggested next question should be in the chatbox and user can hit
   * enter to ask"). The suggestions case swapped the placeholder but
   * submit() bailed on empty input, so accept-on-Enter never worked.
   */
  let ghostSuggestion: string | null = null;
  mountPreactIsland(
    footer,
    <Footer
      bits={{
        hasInputText: hasInputTextSig,
        recording: recordingSig,
        bookVisible: bookVisibleSig,
        suggestions: suggestionsSig,
        cta: ctaSig,
        sendDisabled: sendDisabledSig,
        talkPrimary: talkPrimarySig,
        voiceActive: computed(
          () => voiceState.value.name !== 'idle' && voiceState.value.name !== 'error',
        ),
      }}
      handlers={{
        // Site Agent's corner-expand on the INPUT pill — manually
        // promote to conversation without typing. Routes through the
        // state machine via the `expand` action so widgetState.state
        // moves prompt → conversation (chatStarted stays false). Direct
        // setLayout here was the desync that broke subsequent header
        // actions (state stayed `prompt` while layout was EXPANDED).
        onCornerExpand: () => applyAction({ type: 'expand' }),
        // In the mv2 BAR the × is a dismissal (same path as the phone's
        // swipe-down: morph out, record it, dock); the legacy pill keeps
        // its close.
        onCornerClose: () => {
          if (mobileV2Sig.value && currentLayout === LAYOUTS.INPUT && barDismiss) barDismiss();
          else applyAction({ type: 'close' });
        },
        onSubmit: () => submit(),
        onBookClick: () => {
          // Direct-open path — visitor tapped the dedicated book-a-demo
          // button. Opens the same in-widget booking modal the LLM's
          // bookMeeting tool uses, so the visitor never leaves the page.
          // The always-on visitor-initiated path; the button is never a
          // dead-end: with no org-level link on /config (Settings →
          // Meetings unset, inactive, or deleted; grant-only orgs) the
          // tap becomes a chat turn, so the server-side booking ladder
          // runs — routed rep link, slot offer over a calendar grant, or
          // an honest follow-up capture when nothing can open (settings
          // QA 2026-09-13, Meetings F-01). Before this a tap did nothing.
          if (meetingBookingUrl) {
            openBookingModal(meetingBookingUrl);
            return;
          }
          sendHandler?.(BOOK_MEETING_ASK, { fromVoice: false });
        },
        onAudioClick: () => handleAudioBtnClick(),
        onSuggestionClick: (label) =>
          sendHandler?.(label, { fromVoice: false }),
      }}
      inputSlotRef={inputSlotRef}
    />,
  );
  if (inputSlotRef.current) inputSlotRef.current.appendChild(input);

  // Toggle send vs mic visibility based on input length — driven now
  // by the hasInputTextSig signal that the Footer subscribes to.
  const updateSendMicVisibility = (): void => {
    hasInputTextSig.value = input.value.trim().length > 0;
  };
  input.addEventListener('input', updateSendMicVisibility);

  // Browser-native speech-to-text (legacy VoiceRecorder mixin port).
  // Final transcripts get appended to the input field; interim
  // transcripts replace each other so the placeholder updates in
  // real time. UI feedback: button gains --recording class, inner
  // sound-bars switch to animated mode.
  let interimReplaceLength = 0;
  // Speech-to-text lives in the lazy speech.js chunk (bundle law: a mic
  // nobody taps must not cost every visitor). `voice` is null until the
  // first tap; the handlers are built here so the recorder is created once.
  let voice: VoiceRecorder | null = null;
  const voiceHandlers: VoiceRecorderHandlers = {
    onText: ({ type, text }) => {
      if (type === 'final') {
        // Drop any in-progress interim text we'd been showing, then
        // append the finalised chunk.
        if (interimReplaceLength > 0) {
          input.value = input.value.slice(0, -interimReplaceLength);
          interimReplaceLength = 0;
        }
        input.value += text;
        lastInputWasVoice = true;
        updateSendMicVisibility();
      } else {
        // Replace the prior interim chunk with the new one in-place.
        if (interimReplaceLength > 0) {
          input.value = input.value.slice(0, -interimReplaceLength);
        }
        input.value += text;
        interimReplaceLength = text.length;
        updateSendMicVisibility();
      }
    },
    onStart: () => {
      recordingSig.value = true;
    },
    onStop: () => {
      interimReplaceLength = 0;
      recordingSig.value = false;
    },
    onError: (err) => {
      console.warn('[showpilot] voice input error:', err);
      recordingSig.value = false;
    },
    // Auto-submit after 3s of silence if the input has text — legacy
    // parity (`ChatInterface.vue:664-669` `onSilenceTimeoutReached`).
    // The recorder also stops itself after the timeout fires.
    onSilenceTimeout: () => {
      if (input.value.trim()) submit();
    },
  };
  const ensureVoice = (): Promise<VoiceRecorder> =>
    voice
      ? Promise.resolve(voice)
      : loadSpeech().then((m) => (voice ??= m.createVoiceRecorder(voiceHandlers)));
  /**
   * Realtime voice unification: when the embedder has wired
   * `onVoiceToggle` (Phase 3 realtime path), the mic icon and Talk
   * Now button drive the SAME single voice mode. Two competing voice
   * entry points (mic-icon → browser-STT, Talk Now → realtime) is
   * the UX bug visitors keep hitting — only one is the modern hot-
   * mic experience and they look identical. Fall back to legacy
   * browser STT only when the realtime path isn't available.
   */
  const handleAudioBtnClick = (): void => {
    if (voiceToggleHandler) {
      handleTalkButton();
      return;
    }
    if (!speechSupported()) {
      console.warn(
        '[showpilot] speech recognition not supported in this browser ' +
          '(use Chrome, Edge, or Safari).',
      );
      return;
    }
    // Promote the layout so the user can see the chat panel filling
    // up with their transcript. Site Agent's prompt → conversation
    // (INPUT → EXPANDED) is the canonical "start chatting" transition;
    // other views stay where they are.
    if (currentLayout === LAYOUTS.INPUT) {
      markChatStarted();
      applyAction({ type: 'submit' });
    }
    void ensureVoice()
      .then((v) => v.toggle())
      .catch((err: unknown) => console.warn('[showpilot] speech chunk failed to load', err));
  };

  // "Talk Now" buttons — mini (input layout) and big (expanded). On
  // click, expand to chat layout (legacy `chatStarted`), ask the
  // embedder for an audio URL (they fetch /speak with the agent's
  // welcomeMessage) and play it. Legacy parity:
  // `index.vue:999-1004` `handlePlayWelcomeGreeting` (uses pre-recorded
  // S3 files; we generate on the fly via TTS).
  const triggerGreeting = async (): Promise<void> => {
    if (!talkNowHandler) return;
    markChatStarted();
    if (currentLayout === LAYOUTS.INPUT) {
      applyAction({ type: 'submit' });
    }
    try {
      const url = await talkNowHandler();
      if (url) await audioSpeech.play(url);
    } catch (err) {
      console.warn('[showpilot] talk-now greeting failed', err);
    }
  };

  const handleTalkButton = (): void => {
    // Realtime voice path takes priority when wired by the embedder.
    // Toggle on first click → enter voice mode; second click → leave.
    // Falls back to the legacy greeter when voice toggle isn't wired
    // (e.g. agent.voice.realtimeEnabled === false).
    if (voiceToggleHandler) {
      const starting = !voiceModeActive;
      // M8 ladder — first conversation-starting act via Talk.
      if (starting && !widgetState.chatStarted) {
        emitPresenceAccepted(surfaceForLayout(currentLayout) ?? 'bar', 'talked');
      }
      markChatStarted();
      if (starting) {
        if (currentLayout === LAYOUTS.INPUT) {
          applyAction({ type: 'submit' });
        } else if (currentLayout === LAYOUTS.ENGAGE) {
          // Starting a call from the compact Engage card: promote it to the
          // shared SIDEBAR (rail) chrome rather than ballooning the live face
          // inside the 296px card (the retired "extended engage" look).
          // engage: compact → open → SIDEBAR (stateToLayout).
          applyAction({ type: 'engage' });
        }
      }
      voiceToggleHandler(voiceModeActive ? 'stop' : 'start');
      return;
    }
    void triggerGreeting();
  };
  // Avatar Talk-Now click handlers are wired through the VideoGreeter
  // islands above via onStatusClick → handleTalkButton.

  // TTS playback. The Web Audio analyser drives `--audio-level` 0..1
  // on both avatars via audioLevelSig (the VideoGreeter component sets
  // the CSS var on its root). speakingSig flips the visualizer +
  // pauses the video (handled inside VideoGreeter's useEffect).
  const audioSpeech = createAudioSpeech({
    onLevel: (level) => {
      audioLevelSig.value = level;
    },
    onPlay: () => {
      // Pause the mic recogniser while the agent is speaking so it
      // doesn't pick up the agent's own voice. Restart on stop.
      voice?.stop();
      speakingSig.value = true;
    },
    onStop: () => {
      speakingSig.value = false;
    },
    onError: (err) => {
      console.warn('[showpilot] audio playback error:', err);
    },
  });

  // Privacy disclaimer is now rendered by the Preact Footer component
  // (footer.tsx ~line 177). The imperative creation that lived here
  // during the pre-Preact phase was leftover dead code that produced
  // a second .sp-privacy node, hence the doubled disclaimer reports.

  // Legacy DOM structure (verbatim from ChatInterface.vue:41-66):
  //   panel
  //   ├── header (ChatHeader, only when isExpanded)
  //   └── body-wrapper (flex-row in FULL, flex-col elsewhere)
  //       ├── playlist-drawer (display:none unless layout=full)
  //       ├── content-panel   (display:none unless layout=full)
  //       └── chat-column (always; flex 1)
  //           ├── big-avatar (only when isExpanded)
  //           ├── messages
  //           └── footer
  const playlistDrawer = createPlaylistDrawer();
  const contentPanel = createContentPanel();
  const chatColumn = el('div', 'sp-chat-column');
  // The Speak pill carries the avatar in chat → hide the header thumb so
  // there's one avatar (header = name + title only).
  effect(() => {
    root.classList.toggle('sp-config-ready', configReadySig.value);
    root.classList.toggle('sp-speak-on', voiceAvailableSig.value);
    root.classList.toggle('sp-chat-started', chatStartedSig.value);
    // The "Speak with {name}" pill is a TEXT-mode entry point. Hide it ONLY
    // while the FULL greeter intro card is up — i.e. a greeter is configured
    // AND we're PRE-chat (its own "Talk Now" is the entry then). Once chat
    // starts (greeter intro done, or a call just ended), show the pill so the
    // visitor can (re-)enter voice — otherwise hanging up leaves no entry.
    // (voice/connecting/live states are hidden separately via CSS.)
    root.classList.toggle(
      'sp-greeter-on',
      greeterUrlSig.value !== null && !chatStartedSig.value,
    );
  });
  // Chat-mode "Speak with {name}" CTA — pinned at the top of the thread,
  // hidden during a voice session (CSS). Starts a voice/video call.
  const speakCtaAnchor = el('div', 'sp-speak-cta-anchor');
  mountPreactIsland(
    speakCtaAnchor,
    <SpeakCta
      agentName={agentNameSig}
      avatarUrl={avatarUrlSig}
      voiceAvailable={voiceAvailableSig}
      onSpeak={() => handleTalkButton()}
    />,
  );
  // Bottom-sheet handle bar — only renders visually on tablet/mobile
  // (CSS toggle); click expands/collapses the chat column. Verbatim port
  // of legacy `.sp_v2_modal_chat_column_header` + handle.
  const sheetHandle = el('div', 'sp-bottom-sheet-handle');
  let bottomSheetExpanded = true;
  const setBottomSheet = (expanded: boolean): void => {
    bottomSheetExpanded = expanded;
    chatColumn.classList.toggle('sp-chat-column--collapsed', !expanded);
  };
  sheetHandle.addEventListener('click', () => setBottomSheet(!bottomSheetExpanded));
  chatColumn.appendChild(sheetHandle);
  chatColumn.appendChild(bigAvatar);
  chatColumn.appendChild(speakCtaAnchor);
  chatColumn.appendChild(body);
  // Agent Links — pinned between scrolling chat body and the footer
  // (input pill). Persistent CTAs that stay visible for the entire
  // session, not starter prompts that disappear after first message.
  chatColumn.appendChild(agentLinksAnchor);
  chatColumn.appendChild(footer);
  const bodyWrap = el('div', 'sp-body-wrap');
  // M7 (behind ui.mobileV2) — landscape full-bleed media: swipe left/right
  // navigates the playlist (the content header + its nav buttons are hidden
  // full-bleed; swipe is the nav). Vertical intent (scrolling a PDF) wins:
  // only a dominant horizontal move ≥60px fires. Routes through the SAME
  // nav buttons the header uses, so eligibility (hasPrev/hasNext disabled
  // state) is respected for free.
  let swipeStart: { x: number; y: number } | null = null;
  bodyWrap.addEventListener(
    'touchstart',
    (e) => {
      if (!mobileV2Sig.value || deviceSig.value === 'desktop') return;
      if (currentLayoutSig.value !== LAYOUTS.FULL) return;
      if (orientationSig.value !== 'landscape') return;
      const t = e.touches[0];
      if (t) swipeStart = { x: t.clientX, y: t.clientY };
    },
    { passive: true },
  );
  bodyWrap.addEventListener(
    'touchend',
    (e) => {
      const start = swipeStart;
      swipeStart = null;
      if (!start) return;
      const t = e.changedTouches[0];
      if (!t) return;
      const dx = t.clientX - start.x;
      const dy = t.clientY - start.y;
      if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      const btns = bodyWrap.querySelectorAll<HTMLButtonElement>('.sp-content-nav-btn');
      // [0] = Previous, [1] = Next (content-panel header order).
      const target = dx < 0 ? btns[1] : btns[0];
      if (target && !target.disabled) target.click();
    },
    { passive: true },
  );
  bodyWrap.appendChild(playlistDrawer.el);
  bodyWrap.appendChild(contentPanel.el);
  bodyWrap.appendChild(chatColumn);
  panel.appendChild(header);
  panel.appendChild(bodyWrap);

  // Engage phone-landscape rotate prompt — gated on first interaction.
  // The compact Engage card renders normally in landscape so the visitor
  // can see the host page through it; only when they try to USE the
  // widget (focus input, tap a chip, hit Talk Now) do we know they're
  // committed and the landscape posture really doesn't work. Adding the
  // `sp-rotate-prompt` class triggers the CSS overlay that's scoped to
  // `(pointer:coarse) and (orientation:landscape) and (max-height:500px)`.
  const rotateOverlay = el('div', 'sp-rotate-overlay');
  const rotateIcon = el('div', 'sp-rotate-overlay-icon');
  const rotateText = el('div', 'sp-rotate-overlay-text', 'Rotate to portrait to continue');
  rotateOverlay.appendChild(rotateIcon);
  rotateOverlay.appendChild(rotateText);
  const armRotatePrompt = (): void => {
    if (currentLayout !== LAYOUTS.ENGAGE) return;
    root.classList.add('sp-rotate-prompt');
  };
  panel.addEventListener('focusin', armRotatePrompt);
  panel.addEventListener('click', armRotatePrompt);

  // Floating close button for landscape FULL (pure-content mode where
  // the widget header is hidden). CSS-toggled visibility — display:none
  // outside the landscape FULL media query. Click teardown mirrors the
  // header close button.
  const landscapeClose = el('button', 'sp-landscape-close');
  landscapeClose.setAttribute('type', 'button');
  landscapeClose.setAttribute('aria-label', 'Close');
  landscapeClose.innerHTML =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
    '<path d="M18 6L6 18"/><path d="M6 6L18 18"/></svg>';
  landscapeClose.addEventListener('click', () => {
    // Beam content mode reuses this button: it ends the CONTENT and she
    // stays on the call (beam-content.ts). Landscape media keeps the
    // original teardown.
    if (stopsVoiceOnContentDismiss(widgetState.view) && voiceModeActive && voiceToggleHandler) {
      voiceToggleHandler('stop');
    }
    applyAction(contentDismissAction(widgetState.view));
  });
  panel.appendChild(landscapeClose);

  // Floating close button for ENGAGE (header strip is hidden in this
  // layout — greeter + close X replace it). CSS gates visibility to
  // data-layout=engage only.
  const engageClose = el('button', 'sp-engage-close');
  engageClose.setAttribute('type', 'button');
  engageClose.setAttribute('aria-label', 'Close');
  engageClose.innerHTML =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
    'stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
    '<path d="M18 6L6 18"/><path d="M6 6L18 18"/></svg>';
  engageClose.addEventListener('click', () => {
    if (voiceModeActive && voiceToggleHandler) voiceToggleHandler('stop');
    applyAction({ type: 'close' });
  });
  panel.appendChild(engageClose);

  root.appendChild(welcomeBubble);
  root.appendChild(miniAvatar);
  root.appendChild(backdrop);
  root.appendChild(panel);
  root.appendChild(rotateOverlay);
  root.appendChild(launcher);
  root.appendChild(pipAnchor);
  mount.appendChild(root);

  // ---------- State + handlers ----------
  let currentLayout: Layout = opts.initialLayout;
  // The bar's dismissal, set once the panel exists (the footer's × calls
  // it; the phone swipe and desktop Escape call it directly).
  let barDismiss: (() => void) | null = null;
  let sendHandler: ((message: string, opts: { fromVoice: boolean; hidden?: boolean }) => void) | null = null;
  let talkNowHandler: (() => Promise<string | null>) | null = null;
  let voiceToggleHandler: ((next: 'start' | 'stop') => void) | null = null;

  // ---------- Unbound (the keyed agent standing ON the page) ----------
  // Mounts when the EFFECTIVE view is 'unbound' (server already demoted
  // ineligible agents to engage — resolveUnboundView). The stage lives
  // in a lazy chunk: chrome deploys never pay for the WebGL keyer.
  let boundlessHandle: BoundlessHandle | null = null;
  let unboundAssets: {
    greeterUrl: string;
    transitionUrl: string | null;
    greeterAlpha: { webm: string | null; mov: string | null } | null;
    transitionAlpha: { webm: string | null; mov: string | null } | null;
    cropAt: 'legs' | 'waist' | 'full';
    keyStrength: number;
  } | null = null;
  let unboundMounting = false;
  let lastAgentPublicId: string | null = null;
  let lastPresentation: 'chrome' | 'boundless' | null = null;
  const maybeMountUnbound = (): void => {
    if (widgetState.view !== 'unbound' || boundlessHandle || unboundMounting) return;
    if (!unboundAssets) return; // config not in yet — retried on setConfig
    unboundMounting = true;
    root.classList.add('sp-unbound');
    const anchor = el('div', 'sp-unbound-anchor');
    root.appendChild(anchor);
    void loadBoundless()
      .then((m) => {
        // PRESENCE-WINS: she is on stage in this session — later pages of
        // the visit mount Beam even where their own rule says otherwise.
        if (lastAgentPublicId) markBeamPresence(sessionStorageOrNull(), lastAgentPublicId);
        boundlessHandle = m.mountBoundless(
          anchor,
          {
            ...unboundAssets!,
            pillLabel: "Let's talk",
            // index.tsx flags a mid-call arrival on the root at boot
            // (sp-voice-mode, before the async re-join) — the stage
            // boots already open in the live framing, no rest loop.
            resumeLive: root.classList.contains('sp-voice-mode'),
            // Settled posture on scroll/idle (settle.ts); a placement built
            // around the full figure opts out with data-showpilot-beam="stay".
            settleOnScroll: mount.getAttribute('data-showpilot-beam') !== 'stay',
          },
          {
            startVoice: () => voiceToggleHandler?.('start'),
            endVoice: () => voiceToggleHandler?.('stop'),
            setMicMuted: (muted) => setVoiceMicMuted(muted),
            setBotAudioMuted: (muted) => setBotAudioMuted(muted),
            // face-ready at the REVEAL — her audio is released here, so
            // the bot's first words can never land muted (2026-10-30).
            onRevealed: () => {
              if (faceReadySent) return;
              faceReadySent = true;
              notifyVoiceVisitorReady('face-ready');
              markVoiceFaceReady();
            },
            sendText: (text) => sendHandler?.(text, { fromVoice: false }),
          },
        );
      })
      .catch((err) => {
        // Chunk failed (transient network) — fall back to the engage
        // presence rather than a dead page corner.
        console.warn('[showpilot] unbound chunk failed, falling back to engage', err);
        root.classList.remove('sp-unbound');
        anchor.remove();
        unboundMounting = false;
        setView('engage');
      });
  };
  // Live stream + speech forwarding (the chunk owns no shared signals).
  effect(() => {
    const stream = botVideoStream.value;
    boundlessHandle?.setLiveStream(stream);
  });
  /**
   * Where interactive cards (meeting slots, contact forms) mount: the
   * thread everywhere, her card's SHEET in Beam — the thread is never on
   * screen there (found live 2026-09-04: she offered times, the visitor
   * saw nothing). Chunks built before the sheet have no host → thread.
   */
  const interactiveHost = (): HTMLElement =>
    beamInteractiveHost(widgetState.view, boundlessHandle?.sheetHost?.() ?? null) ?? bodyInner;
  /**
   * Fires whenever the content viewer opens a playlist item — regardless
   * of source (LLM `openContent` tool, recommendation card click,
   * agent-link chip, drawer click). Index wires this to a fire-and-forget
   * POST to /v1/sessions/:id/events so the admin Conversations inbox
   * can populate the Content tab from session_events when the LLM tool
   * path didn't fire (agent-link clicks bypass the LLM entirely).
   */
  let contentOpenedHandler: ((it: PlaylistItem) => void) | null = null;
  let contentProgressHandler: ((p: ContentProgress) => void) | null = null;
  let voiceModeActive = false;
  /**
   * Last input mode — flips to true when the voice recorder emits a
   * final transcript, back to false when the user types or sends a
   * non-voice suggestion. Drives the per-request `speak` flag so the
   * agent only speaks back when the user spoke first (legacy parity:
   * `index.vue:513` — `speak: source === "voice"`).
   */
  let lastInputWasVoice = false;
  let journeyAnswerHandler:
    | ((stepId: string, value: string | string[] | null) => void)
    | null = null;
  /**
   * Set by the embedder via `onContactCapture`. Called when the inline
   * contact form (requestContactCapture tool) is submitted. Embedder
   * POSTs to /v1/contact-capture which funnels through the existing
   * captureQualifyingAnswer → identifyByEmail → HubSpot create-on-miss
   * pipeline. Resolves on success; rejects with an error message on
   * failure so the widget can show inline retry.
   */
  let contactCaptureHandler:
    | ((
        fields: {
          name?: string;
          email?: string;
          phone?: string;
          company?: string;
        },
        intent?: 'contact' | 'callback' | 'booking',
        formId?: string,
        /** F-4 — non-quartet answers keyed by the form's field keys. */
        answers?: Record<string, string>,
      ) => Promise<void>)
    | null = null;
  /**
   * Set by the embedder via `onHighlightNavigate`. Called when the
   * agent's `highlightElement` SSE event targets a different URL than
   * the visitor's current page — embedder persists the highlight to
   * sessionStorage and triggers a same-origin navigation.
   */
  let highlightNavigator:
    | ((highlight: { text: string; reason?: string; url: string }) => void)
    | null = null;
  /** Agent-driven navigation is about to unload the page; `next` is the destination when the agent chose it. */
  let beforeHostNavigate: ((next?: URL) => void) | null = null;
  /**
   * Set on `journey-complete`, cleared on the next `recommendations`
   * event. When true, the recommendations event auto-promotes the
   * widget to FULL and opens the first item — that's the post-journey
   * playlist reveal moment legacy designed for.
   */
  let awaitingPostJourneyRecs = false;
  /**
   * In-memory index of the most-recent recommendations. Lets the
   * `open-content` handler look up full PlaylistItem metadata when the
   * server payload only carries id+type (text-mode path). Voice-mode
   * payloads now carry full metadata directly so the lookup is just
   * belt-and-suspenders.
   */
  const recommendationsById = new Map<string, PlaylistItem>();
  /**
   * Running playlist for the FULL-layout drawer. Two paths populate it:
   *   (1) `recommendations` events replace it wholesale (cards in chat
   *       + drawer reflect the latest content_recommend batch),
   *   (2) `open-content` events append the opened item if it's not
   *       already there — so a bot that skips content_recommend and
   *       jumps to content_view still renders SOMETHING in the drawer
   *       instead of the "No content available" empty state.
   */
  const currentPlaylistItems: PlaylistItem[] = [];
  let activeStreaming: ReturnType<typeof createStreamingText> | null = null;
  let activeAssistantText = '';
  let streaming = false;
  /**
   * Tracks whether the user has interacted (typed/sent) at least once.
   * Drives the `.initialized` class on the big video avatar — once true,
   * the avatar shrinks from a 200px hero block to a 52px status bar.
   * Verbatim port of legacy `chatStarted` (ChatInterface.vue:318).
   */
  let chatStarted = false;
  function markChatStarted(): void {
    if (chatStarted) return;
    chatStarted = true;
    chatStartedSig.value = true;
    pushMilestone('conversation_started', { agent: agentNameSig.value });
    // Engage swaps the 160px greeter block for the standard
    // avatar+name header strip once the visitor commits to chatting.
    // Class on root drives the CSS swap; checked per-render in
    // styles.ts under .sp-engage-started.
    root.classList.add('sp-engage-started');
    // If the visitor is acting before Engage's delayed welcome reveal
    // fires, drop the ceremony — they're already committed.
    cancelEngageWelcomeReveal();
  }

  /**
   * Engage view delays appending the welcome message by ~2s after mount
   * so the visitor sees a "tucked-in" greeter card first and the welcome
   * arrives like a chat notification (chirp + haptic + panel grows).
   * Timer id stored so user interaction or a layout change can preempt.
   */
  let engageWelcomeTimerId: ReturnType<typeof setTimeout> | null = null;
  // Pre-config greeting override (offer tie-back / early decide) — read
  // by applyConfig so the static welcome doesn't clobber it.
  let pendingGreetingOverride: string | null = null;
  // Slice 6.6 — track the engage welcome anchor explicitly so the
  // proactive override (setEngageGreeting) can replace its content
  // after AgentLinks chips or other elements render below it.
  let engageWelcomeAnchor: HTMLDivElement | null = null;
  function cancelEngageWelcomeReveal(): void {
    if (engageWelcomeTimerId !== null) {
      clearTimeout(engageWelcomeTimerId);
      engageWelcomeTimerId = null;
    }
  }

  // Per CSS-isolation policy (feedback_widget_css_isolation memory) the
  // widget never mutates the host <body>. Layout changes happen entirely
  // on `.sp-root[data-layout]` — the rail overlays the page rather than
  // pushing it.
  const syncBodyClass = (): void => undefined;

  // Host dock — the rail pushes the page aside (see host-dock.ts). Runs on
  // every layout change and on resize/device flips so the pushed width
  // tracks the panel's real width (tablet clamps it).
  const railMode: RailMode = opts.railMode ?? 'dock';
  const syncHostDock = (): void => {
    const doc = root.ownerDocument;
    if (shouldDock({ mode: railMode, layout: currentLayout, device: deviceSig.value })) {
      const measured = panel.getBoundingClientRect().width;
      applyHostDock(doc, measured > 0 ? measured : 400);
    } else {
      removeHostDock(doc);
    }
  };
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', syncHostDock, { passive: true });
  }

  /**
   * Internal widget state — per-view state machine. Initialized from
   * the legacy boot layout (viewFromLegacyLayout) so today's mount
   * paths keep working. `setView()` overwrites this once /config
   * returns the canonical view.
   */
  const seed = viewFromLegacyLayout(opts.initialLayout);
  let widgetState: WidgetState = {
    view: seed.view,
    state: seed.state,
    experienceActive: opts.initialLayout === LAYOUTS.FULL,
    chatStarted: false,
    sessionMinimized: false,
  };

  // M1 — the real per-agent takeover opt-in, learned at setConfig. Defaults
  // true pre-config so the boot window behaves exactly as before (index.tsx's
  // boot path owns the conservative pre-config cap via capLayoutForMobile).
  let takeoverAllowed = true;

  function getTransitionContext(): TransitionContext {
    return {
      mobile: isMobile(),
      mobileTakeoverAllowed: takeoverAllowed,
    };
  }

  /**
   * Drive every user-visible transition through here. Maps an abstract
   * action ('open' / 'minimize' / 'close' / 'submit' / 'engage' /
   * 'agentNavigate' / 'showContent' / 'endContent') to (a) a new
   * internal state via the per-view transition table and (b) a new
   * visual Layout via stateToLayout.
   */
  // Phase 3 (behind ui.mobileV2) — the rail is THE engaged desktop/tablet
  // posture: Site Agent's conversation renders as the rail (SIDEBAR)
  // instead of the 560px bottom-centered EXPANDED panel. Phones keep the
  // sheet mapping; content experience (FULL) untouched.
  const promoteLayoutForMv2 = (layout: Layout): Layout => {
    if (!mobileV2Sig.value) return layout;
    if (deviceSig.value === 'phone') {
      // Mobile finals: the ENGAGE panel does not exist on phones. Cold
      // presence is the DOCKED launcher (frame 1) with the teaser Card
      // as the proactive expression (M3); the launcher tap opens the
      // START BAR (frame 3 — wired at the launcher onClick); typing or
      // talking opens the full sheet (frames 4-5). Booting straight
      // into the bar was pushy and stacked it over dormant engage
      // chrome (David's 2026-07-06 screenshots).
      if (layout === LAYOUTS.ENGAGE) return LAYOUTS.ICON;
      // Conversation/call posture on phones is the full SHEET — never
      // the legacy floating card (the call rendered in a bottom float
      // with the page peeking around it).
      if (layout === LAYOUTS.EXPANDED) return LAYOUTS.SIDEBAR;
      return layout;
    }
    // Spatial continuity (David 2026-07-07): a first message sent from
    // the CENTERED bar opens the centered panel right where the bar
    // was — jumping to the right-edge rail felt disconnected. The rail
    // stays the engaged posture for everything already railed (or
    // launcher-opened — that path sets SIDEBAR explicitly).
    if (layout === LAYOUTS.EXPANDED) {
      return currentLayoutSig.value === LAYOUTS.INPUT ? LAYOUTS.EXPANDED : LAYOUTS.SIDEBAR;
    }
    // ENGAGE keeps its panel on desktop/tablet — D3-as-engage reverted
    // (David, 2026-07-07): the Card copy of the engage first touch
    // lacked voice entry, close, and the chirp/decide swap. The engage
    // panel is the mature surface; calls promote to the rail from it.
    return layout;
  };

  let tourStageClosedCb: ((tourPublicId: string) => void) | null = null;

  const applyAction = (action: TransitionAction): void => {
    const wasExperienceActive = widgetState.experienceActive;
    const next = transition(widgetState, action, getTransitionContext());
    widgetState = next;
    viewSig.value = next.view;
    sessionMinimizedSig.value = next.sessionMinimized;
    setLayout(promoteLayoutForMv2(stateToLayout(next)));
    syncStateAttr();
    // When the content experience ENDS (backdrop click, X, endContent, or any
    // transition that drops experienceActive), tear down the viewer. Hiding
    // the column via layout alone leaves the <video>/iframe mounted, so the
    // media keeps playing audio in the background. Unmounting via
    // setContent(null) is the only reliable stop — esp. for YouTube/Vimeo
    // iframes, which have no pause API here. This is the single choke point
    // every close path routes through (see "route all transitions through
    // applyAction").
    if (wasExperienceActive && !next.experienceActive) {
      const closedTour = activeTourPublicId();
      const closedTitle = currentContentItem?.name ?? null;
      contentPanel.setContent(null);
      currentContentId = null;
      currentContentItem = null;
      syncTourStage();
      // Stage truth: a TOUR left the stage — whoever closed it (viewer's
      // X, backdrop, agent close/navigate), the shell acks so the agent
      // stops narrating a stage nobody sees (2026-08-28 live test).
      if (closedTour) tourStageClosedCb?.(closedTour);
      // The bot's copy of stage truth: whatever was open is gone.
      notifyVoiceOfContentClosed(closedTitle);
    }
  };

  // ---------- M8 presence funnel emitter (Phase 6) ----------
  // The pre-session ladder rungs: shown → accepted (per act) →
  // dismissed. Guards live HERE so call sites stay one-liners:
  // 'shown' fires once per surface per page load and only while the
  // visitor hasn't engaged; 'accepted' fires once, on the FIRST
  // conversation-starting act (typed / talked / clicked-through —
  // a phone launcher tap that merely deploys the bar is a bar
  // impression, not an accept). Boot-time events are queued until
  // index.tsx registers the forwarder (POST /v1/presence/events).
  let presenceCb: ((e: PresenceFunnelEvent) => void) | null = null;
  const presenceQueue: PresenceFunnelEvent[] = [];
  const presenceShownSent = new Set<PresenceFunnelEvent['surface']>();
  let presenceAcceptedSent = false;
  const firePresence = (e: PresenceFunnelEvent): void => {
    if (presenceCb) presenceCb(e);
    else if (presenceQueue.length < 8) presenceQueue.push(e);
  };
  const surfaceForLayout = (l: Layout): PresenceFunnelEvent['surface'] | null =>
    l === LAYOUTS.INPUT
      ? 'bar'
      : l === LAYOUTS.ICON
        ? 'launcher'
        : l === LAYOUTS.ENGAGE
          ? 'engage'
          : null;
  const emitPresenceShown = (surface: PresenceFunnelEvent['surface']): void => {
    if (presenceAcceptedSent || presenceShownSent.has(surface)) return;
    presenceShownSent.add(surface);
    firePresence({ type: 'shown', surface });
  };
  const emitPresenceAccepted = (
    surface: PresenceFunnelEvent['surface'],
    act: NonNullable<PresenceFunnelEvent['act']>,
  ): void => {
    if (presenceAcceptedSent) return;
    presenceAcceptedSent = true;
    firePresence({ type: 'accepted', surface, act });
  };

  // ---------- Hover invite (desktop, behind ui.mobileV2) ----------
  // David 2026-07-07 v2: the launcher⇄bar hover morph was a hop with no
  // job (typing left it for the rail immediately). Hover now does the
  // INVITE work instead: reveal the personalized welcome bubble (the
  // moment that earns the click); click goes straight to the conversation.
  let bubbleHoverRevealed = false;
  const hoverCapable = (): boolean => {
    try {
      return window.matchMedia('(hover:hover)').matches;
    } catch {
      return false;
    }
  };
  launcher.addEventListener('mouseenter', () => {
    if (!mobileV2Sig.value || deviceSig.value === 'phone' || !hoverCapable()) return;
    if (currentLayout !== LAYOUTS.ICON || widgetState.chatStarted) return;
    if (!welcomeVisibleSig.value && welcomeTextSig.value) {
      welcomeVisibleSig.value = true;
      bubbleHoverRevealed = true;
      emitPresenceShown('bubble');
    }
  });
  launcher.addEventListener('mouseleave', () => {
    if (!bubbleHoverRevealed) return;
    bubbleHoverRevealed = false;
    welcomeVisibleSig.value = false;
  });
  // ONE way down from the bar, three gestures in: swipe DOWN on phones
  // (David 2026-07-07, replaced the floating corner ×), and on desktop
  // the hover-revealed × badge or Escape from the composer (audit
  // 2026-10-25: desktop had NO dismissal at all — the corner actions were
  // in the markup and hidden). A draft in the field makes the bar sticky
  // in every case (never eat typed text). Plays the collapse morph, then
  // records the dismissal and docks to the launcher.
  const dismissBar = (): void => {
    if (currentLayout !== LAYOUTS.INPUT || input.value.trim()) return;
    input.blur();
    root.classList.add('sp-bar-collapsing');
    setTimeout(() => {
      root.classList.remove('sp-bar-collapsing');
      if (currentLayout === LAYOUTS.INPUT && !input.value.trim()) {
        firePresence({ type: 'dismissed', surface: 'bar' });
        applyAction({ type: 'minimize' });
      }
    }, 170);
  };
  barDismiss = dismissBar;
  panel.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || !mobileV2Sig.value || currentLayout !== LAYOUTS.INPUT) return;
    if (deviceSig.value === 'phone') return;
    if (input.value.trim()) return;
    e.stopPropagation();
    dismissBar();
  });
  let barTouchStartY: number | null = null;
  panel.addEventListener(
    'touchstart',
    (e) => {
      if (deviceSig.value !== 'phone' || !mobileV2Sig.value) return;
      if (currentLayout !== LAYOUTS.INPUT) return;
      barTouchStartY = e.touches[0]?.clientY ?? null;
    },
    { passive: true },
  );
  panel.addEventListener(
    'touchmove',
    (e) => {
      if (barTouchStartY === null || currentLayout !== LAYOUTS.INPUT) return;
      const dy = (e.touches[0]?.clientY ?? barTouchStartY) - barTouchStartY;
      if (dy < 56) return;
      barTouchStartY = null;
      dismissBar();
    },
    { passive: true },
  );
  panel.addEventListener(
    'touchend',
    () => {
      barTouchStartY = null;
    },
    { passive: true },
  );

  /**
   * Mirror widgetState onto `data-state` for live debugging. Hand-readable
   * format: "site-agent/conversation" or "engage/compact+exp". DevTools
   * shows it inline; lets us catch state↔layout desyncs without adding
   * console.log noise. No runtime semantics — pure debug surface.
   */
  function syncStateAttr(): void {
    const s = widgetState;
    root.setAttribute(
      'data-state',
      `${s.view}/${s.state}${s.experienceActive ? '+exp' : ''}`,
    );
  }

  /**
   * First setLayout call comes from mountWidget passing `opts.initialLayout`.
   * On phones the slice-1 takeover cap may have collapsed an EXPANDED /
   * SIDEBAR / FULL agent down to INPUT at boot — the visitor didn't ask
   * for that, so we shouldn't autofocus the textarea and pop the mobile
   * keyboard. Skip auto-focus on the very first call; later user-driven
   * transitions (launcher click, header buttons) still focus normally.
   */
  let setLayoutCalled = false;
  const setLayout = (next: Layout): void => {
    const isFirstCall = !setLayoutCalled;
    setLayoutCalled = true;
    const previous = currentLayout;
    currentLayout = next;
    currentLayoutSig.value = next;
    root.setAttribute('data-layout', next);
    syncBodyClass();
    syncHostDock();
    // M8 ladder — a dormant presence surface became visible (boot bar/
    // launcher, engage panel, post-dismiss launcher). Guards inside:
    // once per surface, never after engagement.
    if (!widgetState.chatStarted) {
      const presenceSurface = surfaceForLayout(next);
      if (presenceSurface) emitPresenceShown(presenceSurface);
    }
    // fullscreen + sidebar button visibility is derived from
    // currentLayoutSig inside the Header component — no imperative
    // style mutation here anymore.
    // Phones never focus here — it raises the keyboard on voice, avatar
    // and Action transitions (composer-focus.ts).
    if (focusComposerOnLayout(next, LAYOUTS.ICON, isFirstCall, deviceSig.value === 'phone')) {
      input.focus();
    }
    // Engage's delayed welcome ceremony is bound to the compact state.
    // If we transition out of ENGAGE before it fires (visitor clicked
    // a chip → Skyscraper, hit X → ICON, etc.) drop the timer.
    if (previous === LAYOUTS.ENGAGE && next !== LAYOUTS.ENGAGE) {
      cancelEngageWelcomeReveal();
    }
    // An Action's bar notice lives only while the bar does.
    if (barNoticeUp && next !== LAYOUTS.INPUT) {
      barNoticeUp = false;
      welcomeVisibleSig.value = false;
    }
  };

  const getLayout = (): Layout => currentLayout;

  /**
   * Switch views after /config returns. Recomputes the widget state
   * from the canonical view (with mobile + resume rules) and applies
   * the corresponding layout. No-op when the view hasn't changed.
   */
  const setView = (view: AgentView): void => {
    if (widgetState.view === view) return;
    widgetState = bootStateFor(view, {
      mobile: isMobile(),
      // The real per-agent opt-in (setConfig runs before setView on the
      // boot path, so this is populated by the time views switch).
      mobileTakeoverAllowed: takeoverAllowed,
      resumeWithHistory: widgetState.chatStarted,
    });
    viewSig.value = view;
    keyLiveSig.value = shouldKeyLive(lastPresentation, view, { presentDoor: opts.presentDoor });
    setLayout(promoteLayoutForMv2(stateToLayout(widgetState)));
    syncStateAttr();
    maybeMountUnbound();
  };

  // Chrome click handlers (launcher, backdrop, header buttons) are
  // all wired through the Preact islands at construction time above.
  // No addEventListener calls here.

  // Smart auto-scroll. Ported from legacy ChatInterface.vue:497-549 via
  // the parked mobile-floor branch. Auto-scrolls the chat body to its
  // bottom whenever new content arrives, but yields to the user the
  // moment they manually scroll — detected via wheel/touchmove/scroll
  // drift >15px from the last position we set. Clears automatically
  // when the user catches back up within 40px of the bottom, OR on the
  // next submit (so their own message lands visibly).
  let userInterruptedScroll = false;
  let lastSetScrollTop = 0;
  const SCROLL_DRIFT_THRESHOLD = 15;
  const SCROLL_RESUME_THRESHOLD = 40;

  const onUserScroll = (): void => {
    if (
      !userInterruptedScroll &&
      Math.abs(body.scrollTop - lastSetScrollTop) > SCROLL_DRIFT_THRESHOLD
    ) {
      userInterruptedScroll = true;
      return;
    }
    if (userInterruptedScroll) {
      const distanceFromBottom = body.scrollHeight - body.scrollTop - body.clientHeight;
      if (distanceFromBottom < SCROLL_RESUME_THRESHOLD) {
        userInterruptedScroll = false;
      }
    }
  };
  body.addEventListener('wheel', onUserScroll, { passive: true });
  body.addEventListener('touchmove', onUserScroll, { passive: true });
  body.addEventListener('scroll', onUserScroll, { passive: true });

  const scrollToBottom = (): void => {
    if (userInterruptedScroll) return;
    body.scrollTop = body.scrollHeight;
    lastSetScrollTop = body.scrollTop;
  };

  /** Called by submit() — user explicitly sent, they expect to see it. */
  const resetScrollInterrupt = (): void => {
    userInterruptedScroll = false;
  };

  const submit = (): void => {
    let value = input.value.trim();
    // Empty input + ghost suggestion → Enter accepts the suggested
    // next question exactly as if the visitor typed it.
    if (!value && ghostSuggestion) value = ghostSuggestion;
    if (!value || streaming) return;
    // M8 ladder — first conversation-starting act via typing. Surface
    // = wherever the field lives right now (bar / engage / rail).
    if (!widgetState.chatStarted) {
      emitPresenceAccepted(surfaceForLayout(currentLayout) ?? 'bar', 'typed');
    }
    markChatStarted();
    // Layout transition on submit. Legacy `handleSend` (ChatInterface
    // .vue:445-456) calls `this.handleFocus()` after emitting send;
    // handleFocus then emits 'expand' which index.vue routes to
    // INPUT → EXPANDED on desktop, INPUT → FULL on mobile. We do the
    // transition here directly because it's clearer than chaining
    // through a focus event, and avoids the page-load misfire that
    // happens if we wire @focus directly.
    //
    // Layout transitions on first send go through the per-view
    // state machine. Each view owns its own submit semantics:
    //   site-agent prompt    → conversation (INPUT → EXPANDED)
    //   site-agent rail      → conversation (visitor typed mid-rail)
    //   engage compact       → open         (ENGAGE → SIDEBAR)
    //   chat-button open     → stay
    //   rail open            → stay
    // chatStarted=true is set by transition() itself; markChatStarted()
    // above stays for the avatar-shrink visual signal.
    applyAction({ type: 'submit' });
    input.value = '';
    autoResize();
    // Programmatic .value = '' doesn't fire the 'input' event, so the
    // send-vs-mic visibility signal would otherwise stay stale at
    // `true` from the user's last keystroke. Reset explicitly so the
    // mic returns. Also clear last-turn suggestion chips — they're
    // stale once the user has moved on to a new message.
    hasInputTextSig.value = false;
    suggestionsSig.value = [];
    ctaSig.value = null;
    clearSuggestBubbles();
    // Restore the configured placeholder — the ghost suggestion is one-shot.
    ghostSuggestion = null;
    input.placeholder = configuredPlaceholder;
    // Reset smart-scroll: user submitting expects their message + the
    // reply to land at the bottom even if they had scrolled up reading.
    resetScrollInterrupt();
    const fromVoice = lastInputWasVoice;
    lastInputWasVoice = false;
    sendHandler?.(value, { fromVoice });
  };
  // Send button click is wired through Footer's onSubmit prop.
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  });
  input.addEventListener('input', autoResize);
  // Ghost keyboard interaction (David 2026-07-23): on an EMPTY input,
  // ArrowRight ACCEPTS the ghost as real input text (dark, editable —
  // Enter then sends as usual), and ArrowUp/Down CYCLE through the
  // other suggestions of the turn (only #1 is ghosted otherwise, the
  // rest were invisible). Guarded on empty input so arrows behave
  // normally while composing.
  let ghostIndex = 0;
  input.addEventListener('keydown', (e) => {
    if (input.value.length > 0) return;
    if (e.key === 'ArrowRight' && ghostSuggestion) {
      e.preventDefault();
      const accepted = ghostSuggestion;
      ghostSuggestion = null;
      input.placeholder = configuredPlaceholder;
      input.value = accepted;
      input.setSelectionRange(accepted.length, accepted.length);
      // Programmatic .value doesn't fire 'input' — dispatch so
      // autoResize / hasInputText / chat-started listeners all run.
      input.dispatchEvent(new Event('input', { bubbles: true }));
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      const items = suggestionsSig.value;
      if (items.length < 2) return;
      e.preventDefault();
      ghostIndex =
        (ghostIndex + (e.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length;
      const next = items[ghostIndex]?.label;
      if (next) {
        ghostSuggestion = next;
        input.placeholder = ghostPlaceholder(next);
      }
    }
  });
  // The ghost suggestion lives in input.placeholder. Restore the
  // configured placeholder as soon as the visitor starts typing so
  // the ghost doesn't reappear when they delete back to empty.
  input.addEventListener('input', () => {
    if (input.value.length > 0 && input.placeholder !== configuredPlaceholder) {
      input.placeholder = configuredPlaceholder;
    }
  });
  // Chat starts when the visitor actually TYPES CONTENT — the
  // big-avatar starts shrinking the moment real input exists, even
  // before send. Was any-keydown (legacy handleKeyDown), which meant a
  // stray key with the input focused (Tab, arrow, modifier) collapsed
  // the greeter + welcome into post-conversation chrome with no
  // conversation (David prod catch 2026-07-14).
  input.addEventListener('input', () => {
    if (input.value.trim().length > 0) markChatStarted();
  });
  // Any keystroke that isn't auto-filled by the voice recogniser
  // means the user is typing — flip back so we don't TTS a typed reply.
  input.addEventListener('keydown', (e) => {
    if (e.key.length === 1 || e.key === 'Backspace' || e.key === 'Delete') {
      lastInputWasVoice = false;
    }
  });
  // Ghost-text accept: when the input is empty and a suggestion is
  // showing as ghost text, pressing Enter sends the suggestion as the
  // visitor's message. Same outcome as clicking a chip pre-redesign,
  // but the visitor stays anchored on the input pill.
  input.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    if (input.value.trim().length > 0) return;
    const ghost = suggestionsSig.value[0]?.label;
    if (!ghost) return;
    e.preventDefault();
    if (sendHandler) {
      // Suggestions are one-shot — clear before send so the chip
      // doesn't reappear on the next render before suggestionsSig
      // gets the new turn's data.
      suggestionsSig.value = [];
      clearSuggestBubbles();
      sendHandler(ghost, { fromVoice: false });
    }
  });
  /**
   * Ghost text shown in the input placeholder — TRUNCATED for display
   * (the pill is narrow next to the Talk button; a clipped mid-word
   * placeholder reads as a bug — David 2026-07-14). Enter still sends
   * the FULL suggestion; only the preview is shortened.
   */
  const ghostPlaceholder = (text: string): string => {
    const max = 34;
    if (text.length <= max) return text;
    const cut = text.slice(0, max);
    const atWord = cut.slice(0, Math.max(cut.lastIndexOf(' '), 18));
    return `${atWord.trimEnd()}\u2026`;
  };

  function autoResize(): void {
    input.style.height = 'auto';
    input.style.height = Math.min(input.scrollHeight, 120) + 'px';
  }

  // Book button click is wired through Footer's onBookClick prop. The
  // booking flow itself opens via the bookMeeting tool call (book-
  // meeting SSE event → window.open(bookingUrl)) — the click is a
  // no-op today, kept for future direct-click UX.

  const onResize = (): void => syncBodyClass();
  if (typeof window !== 'undefined') window.addEventListener('resize', onResize);

  const setConfig = (config: AgentConfigResponse): void => {
    // M1 — capture the per-agent mobile-takeover opt-in for the live
    // transition context (was hardcoded true).
    takeoverAllowed = config.agent.mobileTakeoverAllowed;
    // Program rollout flag (Phase 0 contract; defaults false on old servers).
    const flagWasOff = !mobileV2Sig.value;
    mobileV2Sig.value = config.ui?.mobileV2 ?? false;
    // The mount happened BEFORE /config, so the boot layout was computed
    // flag-off. Re-apply the mv2 layout promotion now (D3: engage → docked
    // Card presence; Phase 3: EXPANDED → rail). No-op when unchanged.
    if (flagWasOff && mobileV2Sig.value) {
      setLayout(promoteLayoutForMv2(stateToLayout(widgetState)));
    }
    // Header title + launcher label + welcome bubble are all signal-
    // driven now — components re-render when these update.
    agentNameSig.value = config.agent.name;
    agentTitleSig.value = config.agent.title ?? 'AI Agent';
    const firstName = config.agent.name.split(' ')[0] ?? '';
    firstNameSig.value = firstName;
    // {name} → agent's first name. Lets the customer-facing default
    // "Ask {name} anything..." personalize without per-agent rewrites,
    // while still letting them override with anything else they want.
    configuredPlaceholder = config.agent.chatPlaceholder.replace(/\{name\}/g, firstName);
    input.placeholder = configuredPlaceholder;
    const primary = config.theme.primaryColor;
    if (primary) {
      brandPrimary = primary;
      root.style.setProperty('--sp-primary', primary);
      const rgb = hexToRgb(primary);
      if (rgb) root.style.setProperty('--sp-primary-rgb', rgb);
    }
    const avatar = config.theme.avatarUrl;
    if (avatar) {
      avatarUrlSig.value = avatar;
    }
    // Real identity is in — reveal the header (see configReadySig).
    configReadySig.value = true;
    // Wire video greeter from chat-api's resolved URL. Server has
    // already mapped agent.voice.videoGreeter (slug | 'custom' | null)
    // to a fully-qualified URL or null. Both VideoGreeter islands
    // observe greeterUrlSig — null hides them, a real URL plays.
    greeterUrlSig.value = config.voice?.videoGreeterUrl ?? null;
    // Unbound plates (served only when the deploy is eligible; the
    // greeter plate is required by the serving gate, so presence of the
    // URL is the mount key).
    const boundlessGreeter = config.voice?.boundlessGreeterUrl ?? null;
    lastAgentPublicId = config.agent.publicId ?? null;
    lastPresentation = config.voice?.presentation ?? null;
    keyLiveSig.value = shouldKeyLive(lastPresentation, widgetState.view, { presentDoor: opts.presentDoor });
    const alphaPair = (webm: string | null | undefined, mov: string | null | undefined) =>
      webm || mov ? { webm: webm ?? null, mov: mov ?? null } : null;
    unboundAssets = boundlessGreeter
      ? {
          greeterUrl: boundlessGreeter,
          transitionUrl: config.voice?.boundlessTransitionUrl ?? null,
          // Pre-keyed transparent renditions — the stage prefers these
          // and skips the browser key (see boundless/plate-source.ts).
          greeterAlpha: alphaPair(config.voice?.boundlessGreeterWebmUrl, config.voice?.boundlessGreeterMovUrl),
          transitionAlpha: alphaPair(
            config.voice?.boundlessTransitionWebmUrl,
            config.voice?.boundlessTransitionMovUrl,
          ),
          cropAt: config.voice?.boundlessCropAt ?? 'waist',
          keyStrength: config.voice?.boundlessKeyStrength ?? 9,
        }
      : null;
    maybeMountUnbound();
    // Persona focal point on the ROOT so every crop of the shared
    // greeter (rail hero strip, launcher circle, sheet hero) frames the
    // face — the Card anchor sets its own copy (it mounts outside root).
    const focal = config.voice?.greeterFocalPoint;
    if (focal) root.style.setProperty('--sp-greeter-focus', focal);
    if (config.agent.welcomeMessage && !welcomeSuppressed) {
      // {name} → agent's first name. Same substitution as chatPlaceholder
      // so operator-authored welcomes can read like "Hey, I'm {name} —
      // how can I help?" without per-agent rewrites.
      const welcomeWithName = config.agent.welcomeMessage.replace(/\{name\}/g, firstName);
      welcomeTextSig.value = pendingGreetingOverride ?? welcomeWithName;
      if (currentLayout === LAYOUTS.ICON && widgetState.view !== 'unbound') {
        welcomeVisibleSig.value = true;
        emitPresenceShown('bubble');
      }
      if (bodyInner.childElementCount === 0) {
        // Engage view boots a "tucked-in" greeter card; the welcome
        // arrives as a chat notification (chirp + haptic + panel grows).
        // 1800ms keeps perceived load fast — the generic chirps quickly,
        // then the in-place swap replaces it with the personalized
        // greeting from /v1/decide (now reliable after the Preact
        // diff fix). A longer timer just makes the card feel laggy
        // when /decide is warm. Resume sessions skip the ceremony —
        // bodyInner is non-empty.
        if (currentLayout === LAYOUTS.ENGAGE) {
          engageWelcomeTimerId = setTimeout(() => {
            engageWelcomeTimerId = null;
            // Bail if the visitor moved on or another path appended.
            if (
              currentLayout !== LAYOUTS.ENGAGE ||
              bodyInner.childElementCount > 0
            ) {
              return;
            }
            playChirp();
            // Read the signal at fire time so a Slice 6.6 proactive
            // override (setEngageGreeting) wins the race against the
            // static welcomeMessage. Tracks the anchor so a post-fire
            // override (SPA nav re-trigger) can update this specific
            // bubble, not whatever lastElementChild happens to be.
            const anchor = el('div', 'sp-msg-anchor');
            bodyInner.appendChild(anchor);
            mountPreactIsland(anchor, <AssistantBubble markdown={welcomeTextSig.value} />);
            scrollToBottom();
            engageWelcomeAnchor = anchor;
          }, 1800);
        } else {
          appendAssistant(welcomeWithName);
        }
      }
    }
    if (config.meeting?.enabled) {
      // U1: the composer "Book a demo" is EARNED — hidden until a turn's
      // `done` says the goal is ready (`cta.book`). Explicit asks still
      // open the booking card through the tool. Older servers send no
      // `cta`; the done handler falls back to meeting.enabled then.
      meetingBookingUrl = config.meeting.bookingUrl ?? null;
      meetingEnabledForCta = true;
    }
    // Seed the agent CTA ("Schedule a Demo", etc.) as a dedicated wide
    // button between suggestions and the input pill. Click opens the
    // link in a new tab. Cleared on first user submit. CSS hides it
    // on Engage (deferred to the multi-link chips feature).
    //
    // Suppress when meeting booking is enabled AND a calendar link is
    // configured — the dedicated "Book a demo" button inside the input
    // pill (.sp-book-btn) opens that same link, so rendering both is
    // visual duplication. Meetings on with NO link keeps the operator's
    // CTA: it points somewhere else by definition, and hiding it left
    // the footer with nothing that opened (settings QA 2026-09-13).
    // Non-meeting CTAs (a link to a free trial, an interactive demo,
    // etc.) still render normally.
    if (
      config.cta &&
      config.cta.text &&
      config.cta.link &&
      !(config.meeting?.enabled && config.meeting.bookingUrl)
    ) {
      ctaSig.value = { text: config.cta.text, link: config.cta.link };
    }
    // Agent Links — set the array once /config arrives. AgentLinks
    // island re-renders automatically via the signal subscription.
    // Defaults to [] when an older chat-api build omits the field.
    linksSig.value = config.links ?? [];
    // KB-derived page-topic map. AgentLinks uses this to resolve
    // {feature} substitution against the visitor's current URL.
    pageTopicsSig.value = config.pageTopics ?? [];
  };

  function appendAssistant(text: string): void {
    const anchor = el('div', 'sp-msg-anchor');
    bodyInner.appendChild(anchor);
    mountPreactIsland(anchor, <AssistantBubble markdown={text} />);
    scrollToBottom();
  }

  // Suggested-reply bubbles IN the chat flow (David 2026-07-23): the
  // turn's suggestions render as tappable pills under the agent's last
  // message. Tap SENDS immediately — a tap is a decision. One-shot:
  // cleared on tap/send and replaced each turn. The input ghost +
  // arrow keys stay as the keyboard path to the same suggestions.
  let suggestFlowEl: HTMLElement | null = null;
  function clearSuggestBubbles(): void {
    suggestFlowEl?.remove();
    suggestFlowEl = null;
  }
  function renderSuggestBubbles(items: Array<{ label: string }>): void {
    clearSuggestBubbles();
    if (items.length === 0) return;
    const holder = el('div', 'sp-suggest-flow');
    for (const { label } of items) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'sp-suggest-bubble';
      b.textContent = label;
      b.addEventListener('click', () => {
        clearSuggestBubbles();
        suggestionsSig.value = [];
        ghostSuggestion = null;
        input.placeholder = configuredPlaceholder;
        // A tap is a conversation-starting act like typed submit —
        // without these the greeter hero never collapses to the
        // talk pill (David 2026-07-23 share-page catch).
        markChatStarted();
        applyAction({ type: 'submit' });
        sendHandler?.(label, { fromVoice: false });
      });
      holder.appendChild(b);
    }
    suggestFlowEl = holder;
    bodyInner.appendChild(holder);
    scrollToBottom();
  }

  // ---- Booking picker (slice 3; supersedes the 3-chip flow) --------
  // The meeting-slots event triggers the card; the widget then hydrates
  // the FULL availability set from /meeting-options and renders a
  // full picker: day strip, per-day times, timezone label,
  // "Later dates" paging on the calendar engine. The event's 3 spread
  // slots stay as the instant first paint + the fallback when the
  // options fetch fails. A tap books DETERMINISTICALLY through the
  // registered handler; slot-taken re-offers inline; only hard
  // failures ladder to the modal, and every fall is recorded.
  let slotBookHandler: ((body: SlotBookBody) => Promise<SlotBookOutcome>) | null = null;
  let slotOptionsFetcher:
    | ((fromIso?: string) => Promise<{
        startsIso: string[];
        durationMinutes: number;
        needsEmail: boolean;
        nextFromIso: string | null;
      } | null>)
    | null = null;
  let slotFunnelHandler:
    | ((
        type:
          | 'meeting-slot-tapped'
          | 'meeting-fell-to-modal'
          | 'meeting-email-shown'
          | 'meeting-email-given',
        payload: Record<string, unknown>,
      ) => void)
    | null = null;
  let routeReadinessHandler:
    | ((answers?: Record<string, string>, force?: boolean) => Promise<SlotReadiness | null>)
    | null = null;
  /**
   * Conflict re-offers before giving up to the modal. Two vanished
   * slots back-to-back can happen on a busy calendar; a third means
   * availability is churning faster than the picker can promise.
   */
  const MAX_CONFLICT_ROUNDS = 2;
  /** Free-mail domains the 'business' email policy nudges, then blocks. */
  const FREE_MAIL =
    'gmail.com googlemail.com yahoo.com ymail.com hotmail.com outlook.com live.com msn.com aol.com icloud.com me.com mac.com proton.me protonmail.com pm.me mail.com gmx.com gmx.de gmx.net yandex.com yandex.ru fastmail.com hey.com duck.com'.split(
      ' ',
    );
  const isFreeMail = (email: string): boolean =>
    FREE_MAIL.includes(email.slice(email.lastIndexOf('@') + 1).toLowerCase());
  function renderMeetingSlots(payload: {
    slots: Array<{ startIso: string }>;
    durationMinutes: number;
    needsEmail: boolean;
    bookingUrl: string;
    repName?: string;
    repTitle?: string;
    repAvatarUrl?: string;
    /** false = skip the "now or reach out?" fork (rep-sent / SLA card). */
    askChoice?: boolean;
    /** Lead line above the picker ("The team could not jump on…"). */
    notice?: string;
    /** Known identity — pre-fills the reach-out form + email step. */
    prefill?: { name?: string; email?: string };
    /** Email FIRST (prequalify P-1) — collect before the fork/picker.
     *  'business' blocks free-mail domains; absent = legacy post-tap. */
    emailPolicy?: 'business' | 'any';
  }): void {
    // ONE live booking card at a time — the agent owns the booking
    // flow. A new offer replaces any earlier card the visitor hasn't
    // completed; a booked confirmation (sp-slot-confirm) stays.
    const slotHost = interactiveHost();
    for (const scope of new Set([bodyInner, slotHost])) {
      scope.querySelectorAll('.sp-slot-card').forEach((c) => {
        if (!c.querySelector('.sp-slot-confirm')) c.remove();
      });
    }
    const holder = el('div', 'sp-slot-card');
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    const fmtFull = new Intl.DateTimeFormat(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
    const fmtTime = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });
    const dayKey = (t: number): string => {
      const d = new Date(t);
      // Visitor-local YYYY-MM-DD — the grouping key for the day strip.
      return (
        d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' +
        String(d.getDate()).padStart(2, '0')
      );
    };

    // Picker state — hydrated from /meeting-options, seeded by the event.
    let allStarts: number[] = payload.slots
      .map((s) => Date.parse(s.startIso))
      .filter((t) => Number.isFinite(t));
    let durationMinutes = payload.durationMinutes;
    let needsEmail = payload.needsEmail;
    let nextFromIso: string | null = null;
    let hydrated = false;
    let selectedDay: string | null = null;
    let selectedTime: number | null = null;
    /** Sunday (visitor-local) of the week on display. */
    let weekAnchor: number | null = null;
    let knownEmail: string | undefined = payload.prefill?.email;
    let conflictRounds = 0;
    let loadingMore = false;
    // P-2 — the rep can CHANGE after the email step (post-identify
    // re-route), so the header reads mutable locals, hidden until
    // readiness settles (never promise rep A, deliver rep B).
    let repName = payload.repName;
    let repTitle = payload.repTitle;
    let repAvatar = payload.repAvatarUrl;
    let repHidden = Boolean(payload.emailPolicy) && !knownEmail && payload.askChoice !== false;
    /** Resolved by the email-first capture chain; CONSUMED only by the
     *  "Pick a time" tap — resolution never advances the step. */
    let readinessP: Promise<SlotReadiness | null> | null = null;

    const applyReadiness = (r: SlotReadiness | null): void => {
      repHidden = false;
      if (!r || !r.ready || r.repUnchanged) return;
      if ('rep' in r) {
        repName = r.rep?.name ?? undefined;
        repTitle = r.rep?.title ?? undefined;
        repAvatar = r.rep?.avatarUrl ?? undefined;
      }
      if (r.startsIso && r.startsIso.length > 0) {
        allStarts = r.startsIso.map((s) => Date.parse(s)).filter((t) => Number.isFinite(t));
        allStarts.sort((a, b) => a - b);
        durationMinutes = r.durationMinutes || durationMinutes;
        nextFromIso = r.nextFromIso ?? null;
        // Invalidate any in-flight hydration — it was fetched for the
        // OLD rep's calendar and must never stomp this set.
        hydrated = true;
        selectedDay = null;
        selectedTime = null;
        weekAnchor = null;
        conflictRounds = 0;
      }
    };
    /**
     * Which step is on screen — hydration must re-render the CURRENT
     * step, never advance it (the fetch completing a second after
     * paint used to replace the choice fork with the picker).
     */
    let step: 'email-first' | 'choice' | 'gaps' | 'picker' | 'email' =
      payload.emailPolicy && !knownEmail
        ? 'email-first'
        : payload.askChoice === false
          ? 'picker'
          : 'choice';

    const fallback = (): void => {
      slotFunnelHandler?.('meeting-fell-to-modal', { stage: 'book' });
      holder.remove();
      if (payload.bookingUrl) openBookingModal(payload.bookingUrl);
    };
    const showLine = (text: string): void => {
      holder.textContent = '';
      const done = el('div', 'sp-slot-confirm');
      done.textContent = text;
      holder.appendChild(done);
      scrollToBottom();
    };

    const book = (startIso: string, email?: string): void => {
      if (email) knownEmail = email;
      if (!slotBookHandler) return fallback();
      holder
        .querySelectorAll('button,input')
        .forEach((b) => ((b as HTMLButtonElement).disabled = true));
      void slotBookHandler({
        startIso,
        ...(knownEmail ? { email: knownEmail } : {}),
        timezone: tz,
      })
        .then((r) => {
          if (r.ok) {
            const when = fmtFull.format(new Date(r.startIso));
            showLine(
              '\u2713 Booked \u2014 ' + when + ' (' +
                durationMinutes + ' min). Calendar invite on its way.',
            );
            // The agent hears the booking landed (capture-ack.ts) and
            // confirms it in its own words instead of offering again.
            notifyVoiceOfVisitorAction(meetingBookedAck(when, durationMinutes));
            // Beam: the confirmation is a beat in her card, not a
            // fixture — the card returns to captions once read.
            if (holder.closest('.spb-sheet')) {
              setTimeout(() => holder.remove(), 7000);
            }
            return;
          }
          if (r.code === 'slot-taken' && conflictRounds < MAX_CONFLICT_ROUNDS) {
            conflictRounds += 1;
            const tapped = Date.parse(startIso);
            allStarts = allStarts.filter((t) => t !== tapped);
            for (const s of r.freshSlots ?? []) {
              const t = Date.parse(s.startIso);
              if (Number.isFinite(t) && !allStarts.includes(t)) allStarts.push(t);
            }
            allStarts.sort((a, b) => a - b);
            renderPicker('That time was just taken \u2014 pick another.');
            return;
          }
          if (r.code === 'already-booked') {
            showLine('\u2713 You already have a meeting booked \u2014 the invite is in your email.');
            return;
          }
          fallback();
        })
        .catch(fallback);
    };

    // Email step — same design language as the contact-capture form
    // (the good form UX), rendered inside the card with a way back.
    const renderEmailStep = (startIso: string): void => {
      step = 'email';
      holder.textContent = '';
      appendRepHeader();
      const row = el('div', 'sp-contact-form__row');
      const label = el('label', 'sp-contact-form__label');
      label.textContent = 'Work email for the invite';
      const emailInput = document.createElement('input');
      emailInput.type = 'email';
      emailInput.placeholder = 'name@company.com';
      emailInput.className = 'sp-contact-form__input';
      row.appendChild(label);
      row.appendChild(emailInput);
      const go = document.createElement('button');
      go.type = 'button';
      go.className = 'sp-contact-form__submit';
      go.textContent = 'Book ' + fmtFull.format(new Date(startIso));
      go.addEventListener('click', () => {
        const v = emailInput.value.trim();
        if (!v || !v.includes('@') || !v.includes('.')) {
          emailInput.focus();
          return;
        }
        book(startIso, v);
      });
      const back = document.createElement('button');
      back.type = 'button';
      back.className = 'sp-slot-back';
      back.textContent = '\u2190 Pick a different time';
      back.addEventListener('click', () => renderPicker());
      holder.appendChild(row);
      holder.appendChild(go);
      holder.appendChild(back);
      scrollToBottom();
      emailInput.focus();
    };

    const pick = (startIso: string): void => {
      if (needsEmail && !knownEmail) renderEmailStep(startIso);
      else book(startIso);
    };

    // The "send the request instead" rung — shared by the choice fork and
    // the email-first block copy (personal-email safe harbor: the reach-
    // out form accepts any email; a rep follows up).
    const reachOut = (): void => {
      markChatStarted();
      holder.remove();
      // Pre-fill invariant — a just-typed email-first value rides along
      // so the form never re-asks what this card already collected.
      const pre = { ...(payload.prefill ?? {}), ...(knownEmail ? { email: knownEmail } : {}) };
      renderContactForm({
        fields: ['name', 'email'],
        ...(Object.keys(pre).length > 0 ? { prefill: pre } : {}),
        ...(repName && !repHidden
          ? { reason: repName + ' will reach out to find a time that works.' }
          : { reason: 'The team will reach out to find a time that works.' }),
        ctaLabel: 'Send request',
        intent: 'callback',
      });
    };

    // Prequalify P-1 — email FIRST: the lead is captured the moment the
    // visitor submits, before the fork/picker (abandonment-proof). The
    // 'business' policy nudges free-mail once, then blocks with the
    // reach-out safe harbor.
    const renderEmailFirst = (): void => {
      step = 'email-first';
      holder.textContent = '';
      appendRepHeader();
      const lead = el('div', 'sp-slot-notice');
      lead.textContent =
        payload.notice ?? 'One quick step — your email gets this to the right person.';
      holder.appendChild(lead);
      const row = el('div', 'sp-contact-form__row');
      const label = el('label', 'sp-contact-form__label');
      const business = payload.emailPolicy === 'business';
      label.textContent = business ? 'Work email' : 'Email';
      const input = document.createElement('input');
      input.type = 'email';
      input.placeholder = 'name@company.com';
      input.className = 'sp-contact-form__input';
      row.appendChild(label);
      row.appendChild(input);
      const err = el('div', 'sp-slot-notice');
      err.style.display = 'none';
      const go = document.createElement('button');
      go.type = 'button';
      go.className = 'sp-contact-form__submit';
      go.textContent = 'Continue';
      const back = document.createElement('button');
      back.type = 'button';
      back.className = 'sp-slot-back';
      back.textContent = 'Have the team reach out instead';
      back.style.display = 'none';
      back.addEventListener('click', reachOut);
      let nudged = false;
      go.addEventListener('click', () => {
        const v = input.value.trim();
        if (!v || !v.includes('@') || !v.includes('.')) {
          input.focus();
          return;
        }
        if (business && isFreeMail(v)) {
          err.textContent = nudged
            ? 'A work email is required to book here.'
            : 'A work email routes you to the right team — got one to use?';
          err.style.display = '';
          if (nudged) back.style.display = '';
          nudged = true;
          input.focus();
          return;
        }
        knownEmail = v;
        markChatStarted();
        slotFunnelHandler?.('meeting-email-given', {});
        // Lead captured NOW. Readiness (P-2) chains on capture SUCCESS
        // only — a failed capture means identify never ran, so we keep
        // the pre-email routing (email still travels with /book-slot).
        // Runs during the fork beat; only the "Pick a time" tap
        // consumes the result. Rep-sent cards (askChoice:false) keep
        // their rep — the choice was explicit.
        const cap = contactCaptureHandler?.({ email: v }, 'booking');
        if (cap && routeReadinessHandler && payload.askChoice !== false) {
          const fetchReadiness = routeReadinessHandler;
          readinessP = cap.then(() => fetchReadiness()).catch(() => null);
        } else {
          void cap?.catch(() => {});
          repHidden = false;
        }
        if (payload.askChoice === false) renderPicker();
        else renderChoice();
      });
      holder.appendChild(row);
      holder.appendChild(err);
      holder.appendChild(go);
      holder.appendChild(back);
      scrollToBottom();
      input.focus();
    };

    const appendRepHeader = (): void => {
      if (repHidden || !repName) return;
      const rep = el('div', 'sp-slot-rep');
      if (repAvatar) {
        const img = document.createElement('img');
        img.className = 'sp-slot-rep-avatar';
        img.src = repAvatar;
        img.alt = '';
        rep.appendChild(img);
      }
      const label = el('span', 'sp-slot-rep-name');
      label.textContent =
        'Times with ' + repName + (repTitle ? ' \u00b7 ' + repTitle : '');
      rep.appendChild(label);
      holder.appendChild(rep);
    };

    const loadMore = (): void => {
      if (!slotOptionsFetcher || !nextFromIso || loadingMore) return;
      loadingMore = true;
      const from = nextFromIso;
      void slotOptionsFetcher(from)
        .then((opts) => {
          loadingMore = false;
          if (!opts) return;
          nextFromIso = opts.nextFromIso;
          const known = new Set(allStarts);
          const fresh = opts.startsIso
            .map((s) => Date.parse(s))
            .filter((t) => Number.isFinite(t) && !known.has(t));
          if (fresh.length > 0) {
            allStarts = [...allStarts, ...fresh].sort((a, b) => a - b);
            // Jump the view to the first newly loaded day.
            selectedDay = dayKey(fresh[0]!);
            weekAnchor = startOfWeek(fresh[0]!);
            selectedTime = null;
          }
          renderPicker();
        })
        .catch(() => {
          loadingMore = false;
        });
    };

    const startOfWeek = (t: number): number => {
      const d = new Date(t);
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() - d.getDay());
      return d.getTime();
    };
    const DAY_MS = 24 * 60 * 60 * 1000;
    const fmtMonth = new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' });
    const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

    const renderPicker = (notice?: string): void => {
      step = 'picker';
      holder.textContent = '';
      appendRepHeader();
      const lead = notice ?? payload.notice;
      if (lead) {
        const n = el('div', 'sp-slot-notice');
        n.textContent = lead;
        holder.appendChild(n);
      }
      const byDay = new Map<string, number[]>();
      for (const t of allStarts) {
        if (t <= Date.now()) continue;
        const k = dayKey(t);
        const list = byDay.get(k) ?? [];
        list.push(t);
        byDay.set(k, list);
      }
      const days = [...byDay.keys()];
      if (days.length === 0) {
        const empty = el('div', 'sp-slot-notice');
        empty.textContent = 'No open times in this window.';
        holder.appendChild(empty);
      } else {
        if (!selectedDay || !byDay.has(selectedDay)) selectedDay = days[0]!;
        const firstSlotOfSelected = byDay.get(selectedDay)![0]!;
        if (weekAnchor === null) weekAnchor = startOfWeek(firstSlotOfSelected);

        // Month label + week arrows (‹ › move a week; › past the loaded
        // range pages the next availability window in first).
        const nav = el('div', 'sp-slot-nav');
        const month = el('span', 'sp-slot-month');
        month.textContent = fmtMonth.format(new Date(weekAnchor + 3 * DAY_MS));
        const arrows = el('div', 'sp-slot-arrows');
        const prev = document.createElement('button');
        prev.type = 'button';
        prev.className = 'sp-slot-arrow';
        prev.textContent = '\u2039';
        const thisWeek = startOfWeek(Date.now());
        prev.disabled = weekAnchor <= thisWeek;
        prev.addEventListener('click', () => {
          weekAnchor = weekAnchor! - 7 * DAY_MS;
          selectedTime = null;
          renderPicker();
        });
        const next = document.createElement('button');
        next.type = 'button';
        next.className = 'sp-slot-arrow';
        next.textContent = '\u203a';
        next.addEventListener('click', () => {
          const target = weekAnchor! + 7 * DAY_MS;
          const lastLoaded = allStarts[allStarts.length - 1] ?? 0;
          weekAnchor = target;
          selectedTime = null;
          if (target + 7 * DAY_MS > lastLoaded && nextFromIso) loadMore();
          else renderPicker();
        });
        arrows.appendChild(prev);
        arrows.appendChild(next);
        nav.appendChild(month);
        nav.appendChild(arrows);
        holder.appendChild(nav);

        // Calendar-true week row — all 7 days, dead days disabled.
        const week = el('div', 'sp-slot-week');
        for (let i = 0; i < 7; i++) {
          const dayStart = weekAnchor + i * DAY_MS;
          const k = dayKey(dayStart);
          const cell = el('div', 'sp-slot-cell');
          const dow = el('span', 'sp-slot-dow');
          dow.textContent = DOW[new Date(dayStart).getDay()]!;
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'sp-slot-date' + (k === selectedDay ? ' sp-slot-date--on' : '');
          btn.textContent = String(new Date(dayStart).getDate());
          btn.disabled = !byDay.has(k);
          btn.addEventListener('click', () => {
            selectedDay = k;
            selectedTime = null;
            renderPicker();
          });
          cell.appendChild(dow);
          cell.appendChild(btn);
          week.appendChild(cell);
        }
        holder.appendChild(week);

        const meta = el('div', 'sp-slot-meta');
        const tzLabel = el('span', 'sp-slot-tz');
        tzLabel.textContent = 'Times in ' + tz.replace(/_/g, ' ');
        const dur = el('span', 'sp-slot-tz');
        dur.textContent = durationMinutes + ' min';
        meta.appendChild(tzLabel);
        meta.appendChild(dur);
        holder.appendChild(meta);

        // Times for the selected day — contained 2-column grid; pick
        // then Continue (a two-step, so no mis-taps).
        const selectedInWeek = byDay.has(selectedDay) && startOfWeek(byDay.get(selectedDay)![0]!) === weekAnchor;
        if (selectedInWeek) {
          const grid = el('div', 'sp-slot-grid');
          for (const t of byDay.get(selectedDay)!) {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'sp-slot-time' + (t === selectedTime ? ' sp-slot-time--sel' : '');
            b.textContent = fmtTime.format(new Date(t));
            b.addEventListener('click', () => {
              selectedTime = t;
              markChatStarted();
              slotFunnelHandler?.('meeting-slot-tapped', { startIso: new Date(t).toISOString() });
              renderPicker();
            });
            grid.appendChild(b);
          }
          holder.appendChild(grid);
        } else {
          const none = el('div', 'sp-slot-notice');
          none.textContent = nextFromIso && loadingMore ? 'Loading times\u2026' : 'No open times this week.';
          holder.appendChild(none);
        }

        const go = document.createElement('button');
        go.type = 'button';
        go.className = 'sp-contact-form__submit';
        go.disabled = selectedTime === null;
        go.textContent =
          selectedTime !== null
            ? 'Continue \u2014 ' + fmtFull.format(new Date(selectedTime))
            : 'Continue';
        go.addEventListener('click', () => {
          if (selectedTime !== null) pick(new Date(selectedTime).toISOString());
        });
        holder.appendChild(go);
      }
      scrollToBottom();
    };

    // P-2 — the rules-driven gap form: ONLY the fields active routing
    // rules need and the 360 can't answer, each with its why. ≤3
    // fields; Skip routes with what we have (catch-all safe harbor).
    const renderGapForm = (
      gaps: Array<{
        key: string;
        label: string;
        why: string;
        options?: Array<{ value: string; label: string }>;
      }>,
    ): void => {
      step = 'gaps';
      holder.textContent = '';
      const values: Record<string, string> = {};
      for (const g of gaps.slice(0, 3)) {
        const row = el('div', 'sp-contact-form__row');
        const label = el('label', 'sp-contact-form__label');
        label.textContent = g.label;
        row.appendChild(label);
        if (g.options && g.options.length > 0) {
          const grid = el('div', 'sp-slot-grid');
          for (const o of g.options) {
            const b = document.createElement('button');
            b.type = 'button';
            b.className = 'sp-slot-time';
            b.textContent = o.label;
            b.addEventListener('click', () => {
              values[g.key] = o.value;
              grid
                .querySelectorAll('button')
                .forEach((x) => x.classList.remove('sp-slot-time--sel'));
              b.classList.add('sp-slot-time--sel');
            });
            grid.appendChild(b);
          }
          row.appendChild(grid);
        } else {
          const input = document.createElement('input');
          input.type = 'text';
          input.className = 'sp-contact-form__input';
          input.addEventListener('input', () => {
            values[g.key] = input.value.trim();
          });
          row.appendChild(input);
        }
        const why = el('div', 'sp-slot-tz');
        why.textContent = g.why;
        row.appendChild(why);
        holder.appendChild(row);
      }
      const go = document.createElement('button');
      go.type = 'button';
      go.className = 'sp-contact-form__submit';
      go.textContent = 'Continue';
      const skip = document.createElement('button');
      skip.type = 'button';
      skip.className = 'sp-slot-back';
      skip.textContent = 'Skip — just show times';
      const finish = (answers: Record<string, string> | undefined, force: boolean): void => {
        go.disabled = true;
        skip.disabled = true;
        void (routeReadinessHandler
          ? routeReadinessHandler(answers, force)
          : Promise.resolve<SlotReadiness | null>(null)
        ).then((r) => {
          applyReadiness(r);
          if (r && !r.ready && r.gaps && r.gaps.length > 0 && !force) renderGapForm(r.gaps);
          else renderPicker();
        });
      };
      go.addEventListener('click', () => finish(values, false));
      skip.addEventListener('click', () => finish(undefined, true));
      holder.appendChild(go);
      holder.appendChild(skip);
      scrollToBottom();
    };

    // Step 1 — the deterministic fork David specced: schedule now, or
    // send a request for the team to reach out. The card owns this
    // question; the agent never has to remember to ask it.
    const renderChoice = (): void => {
      step = 'choice';
      holder.textContent = '';
      appendRepHeader();
      const lead = el('div', 'sp-slot-notice');
      lead.textContent =
        repName && !repHidden
          ? 'Grab a time now, or have the team reach out?'
          : 'Grab a time now, or have our team reach out?';
      holder.appendChild(lead);
      const now = document.createElement('button');
      now.type = 'button';
      now.className = 'sp-contact-form__submit';
      now.textContent = 'Pick a time';
      now.addEventListener('click', () => {
        markChatStarted();
        if (!readinessP) return renderPicker();
        // Consume the post-email readiness result (usually already
        // resolved). Disabled during the await — a double-tap must not
        // race two renders.
        now.disabled = true;
        now.textContent = 'Loading times…';
        const p = readinessP;
        readinessP = null;
        void p.then((r) => {
          applyReadiness(r);
          if (r && !r.ready && r.gaps && r.gaps.length > 0) renderGapForm(r.gaps);
          else renderPicker();
        });
      });
      const later = document.createElement('button');
      later.type = 'button';
      later.className = 'sp-slot-back';
      later.textContent = 'Have the team reach out instead';
      // Capture ladder (slice 3): the CALLBACK rung — the submit pages
      // the routed rep now, and known identity pre-fills so the visitor
      // confirms rather than re-types.
      later.addEventListener('click', reachOut);
      holder.appendChild(now);
      holder.appendChild(later);
      scrollToBottom();
    };

    // Instant first paint (choice fork, or the picker directly when the
    // emitter already answered the fork — rep-sent times / SLA card),
    // then hydrate the full availability set behind it. Fetch failure =
    // seeded slots keep working.
    if (step === 'email-first') {
      renderEmailFirst();
      slotFunnelHandler?.('meeting-email-shown', {});
    } else if (step === 'choice') renderChoice();
    else renderPicker();
    slotHost.appendChild(holder);
    scrollToBottom();
    if (slotOptionsFetcher) {
      void slotOptionsFetcher()
        .then((opts) => {
          if (!opts || hydrated) return;
          hydrated = true;
          const starts = opts.startsIso
            .map((s) => Date.parse(s))
            .filter((t) => Number.isFinite(t));
          if (starts.length > 0) allStarts = [...new Set(starts)].sort((a, b) => a - b);
          durationMinutes = opts.durationMinutes || durationMinutes;
          needsEmail = opts.needsEmail;
          nextFromIso = opts.nextFromIso;
          // Refresh ONLY if the picker is already on screen — never
          // advance the visitor past the choice/email step.
          if (step === 'picker') renderPicker();
        })
        .catch(() => {});
    }
  }

  /**
   * Rep-authored bubble — Text Relay v1. Visitor sees the rep's name +
   * avatar so it's clear the AI agent is no longer driving. We don't
   * route this through the streaming path because the rep types in the
   * admin and POSTs the whole message; there's no token-by-token feed.
   */
  function appendRepMessage(payload: {
    text: string;
    repName?: string;
    repAvatarUrl?: string;
  }): void {
    removeTypingIndicator();
    clearRepTypingIndicator();
    const anchor = el('div', 'sp-msg-anchor');
    bodyInner.appendChild(anchor);
    mountPreactIsland(
      anchor,
      <RepBubble
        text={payload.text}
        repName={payload.repName}
        repAvatarUrl={payload.repAvatarUrl}
      />,
    );
    scrollToBottom();
  }

  /**
   * Slice E — render a centered system notice (rep-joined / rep-released).
   * Distinct from a chat bubble: thin horizontal rules + muted text,
   * reads as conversation chrome, not a participant.
   */
  function appendSystemLine(text: string): void {
    const anchor = el('div', 'sp-msg-anchor');
    bodyInner.appendChild(anchor);
    mountPreactIsland(anchor, <SystemLine text={text} />);
    scrollToBottom();
  }

  /**
   * Slice E — rep typing indicator. Mount-on-first-tick, refresh
   * timeout on every subsequent tick, auto-clear after the window
   * lapses. Stored as an HTMLElement reference so the next-tick
   * refresh + clear-on-rep-message both find it without DOM query.
   *
   * 4s auto-clear window slightly exceeds the admin composer's 1s
   * heartbeat cadence, so a continuously-typing rep sees the indicator
   * stay visible; a paused rep sees it fade after ~4s.
   */
  let repTypingAnchor: HTMLElement | null = null;
  let repTypingClearTimer: number | null = null;
  const REP_TYPING_AUTO_CLEAR_MS = 4_000;

  function showRepTyping(payload: {
    repName?: string;
    repAvatarUrl?: string;
  }): void {
    // If we already have a typing indicator up, just refresh its
    // auto-clear timer — no need to re-mount. Avoids a flicker on
    // every heartbeat.
    if (repTypingAnchor) {
      resetRepTypingTimer();
      return;
    }
    const anchor = el('div', 'sp-msg-anchor');
    bodyInner.appendChild(anchor);
    mountPreactIsland(
      anchor,
      <RepTypingBubble
        repName={payload.repName}
        repAvatarUrl={payload.repAvatarUrl}
      />,
    );
    repTypingAnchor = anchor;
    resetRepTypingTimer();
    scrollToBottom();
  }

  function resetRepTypingTimer(): void {
    if (repTypingClearTimer != null) {
      window.clearTimeout(repTypingClearTimer);
    }
    repTypingClearTimer = window.setTimeout(
      clearRepTypingIndicator,
      REP_TYPING_AUTO_CLEAR_MS,
    );
  }

  function clearRepTypingIndicator(): void {
    if (repTypingClearTimer != null) {
      window.clearTimeout(repTypingClearTimer);
      repTypingClearTimer = null;
    }
    if (repTypingAnchor) {
      repTypingAnchor.remove();
      repTypingAnchor = null;
    }
  }

  // -- Slice F — Daily call overlay state -----------------------------
  //
  // Two pieces of mutable state:
  //   pendingCallInviteAnchor — the DOM anchor for the accept/decline
  //     card while it's pending. Cleared on accept, decline, end, or
  //     server-pushed call-declined / call-ended.
  //   activeCallOverlay — the createCallOverlay() controller while the
  //     iframe is mounted. teardownCall() destroys + nulls it.

  let pendingCallInviteAnchor: HTMLElement | null = null;
  let activeCallOverlay: CallOverlayController | null = null;
  let activeCallId: string | null = null;
  let callLifecycleHandler:
    | ((event: { kind: 'declined' | 'ended'; callId: string }) => void)
    | null = null;

  function clearPendingCallInvite(): void {
    if (pendingCallInviteAnchor) {
      pendingCallInviteAnchor.remove();
      pendingCallInviteAnchor = null;
    }
  }

  /**
   * Mount the Daily Prebuilt overlay for the visitor. Called on accept.
   * Idempotent: a second accept (e.g. duplicate event) is a no-op.
   */
  function mountCallOverlay(payload: {
    callId: string;
    roomUrl: string;
    visitorToken: string;
    repName?: string;
  }): void {
    if (activeCallOverlay) return;
    activeCallId = payload.callId;
    activeCallOverlay = createCallOverlay({
      callId: payload.callId,
      roomUrl: payload.roomUrl,
      visitorToken: payload.visitorToken,
      onLeave: (reason) => {
        // Visitor or Daily ended the call — clean up + tell the embedder
        // so it can POST the end audit. Server-pushed call-ended also
        // funnels here via teardownCall.
        if (reason === 'visitor-left' || reason === 'error') {
          teardownCall('visitor-left');
        }
      },
    });
    void activeCallOverlay.show();
  }

  /**
   * Tear down the overlay + notify the embedder. `reason` distinguishes
   * a local visitor-left from a server-pushed call-ended so the
   * embedder can POST the right endpoint exactly once.
   */
  function teardownCall(reason: 'visitor-left' | 'call-ended'): void {
    const callId = activeCallId;
    activeCallOverlay?.destroy();
    activeCallOverlay = null;
    activeCallId = null;
    clearPendingCallInvite();
    if (reason === 'visitor-left' && callId && callLifecycleHandler) {
      callLifecycleHandler({ kind: 'ended', callId });
    }
  }

  /**
   * Render the inline accept/decline card. On accept → mount overlay
   * + clear card. On decline → tell embedder to POST /call/:id/decline
   * + clear card.
   */
  function renderCallInvite(payload: {
    callId: string;
    roomUrl: string;
    visitorToken: string;
    repName?: string;
    repAvatarUrl?: string;
  }): void {
    // Drop any older pending invite — server only sends one at a time
    // in practice, but a duplicate event shouldn't stack two cards.
    clearPendingCallInvite();
    const anchor = el('div', 'sp-msg-anchor');
    bodyInner.appendChild(anchor);
    pendingCallInviteAnchor = anchor;
    mountPreactIsland(
      anchor,
      <CallInviteCard
        repName={payload.repName}
        repAvatarUrl={payload.repAvatarUrl}
        onAccept={() => {
          clearPendingCallInvite();
          mountCallOverlay({
            callId: payload.callId,
            roomUrl: payload.roomUrl,
            visitorToken: payload.visitorToken,
            repName: payload.repName,
          });
        }}
        onDecline={() => {
          clearPendingCallInvite();
          if (callLifecycleHandler) {
            callLifecycleHandler({ kind: 'declined', callId: payload.callId });
          }
        }}
      />,
    );
    scrollToBottom();
  }

  /** Streaming-aware assistant message — uses the StreamingText fade. */
  function appendStreamingAssistant(): {
    streaming: ReturnType<typeof createStreamingText>;
  } {
    const anchor = el('div', 'sp-msg-anchor');
    bodyInner.appendChild(anchor);
    const streaming = createStreamingText();
    mountPreactIsland(
      anchor,
      <StreamingAssistantBubble streamingEl={streaming.el} />,
    );
    scrollToBottom();
    return { streaming };
  }

  /**
   * Live user-bubble — text held in a signal so voice-mode whisper
   * deltas can live-mutate the rendered bubble without re-mounting.
   * Set on `appendUserMessage` (any new bubble), cleared on
   * `finalizeUserMessage` so a stale signal doesn't pick up content
   * meant for a different turn.
   */
  let activeUserText: ReturnType<typeof signal<string>> | null = null;

  const appendUserMessage = (text: string): void => {
    // Visitor moved on — drop any active highlight before rendering
    // the new bubble, so the previous outline doesn't linger past the
    // turn it belonged to.
    clearActiveHighlight();
    const anchor = el('div', 'sp-msg-anchor');
    bodyInner.appendChild(anchor);
    const textSig = signal(text);
    mountPreactIsland(anchor, <UserBubble text={textSig} />);
    activeUserText = textSig;
    scrollToBottom();
  };

  const appendUserDelta = (delta: string): void => {
    if (!activeUserText) {
      // No live bubble — fall through to a fresh full-text append.
      appendUserMessage(delta);
      return;
    }
    activeUserText.value = activeUserText.value + delta;
    scrollToBottom();
  };

  const finalizeUserMessage = (text: string): void => {
    if (!activeUserText) {
      appendUserMessage(text);
      return;
    }
    // Replace the live-deltas with the canonical final transcript.
    // The whisper deltas are interim — final text supersedes them.
    activeUserText.value = text;
    activeUserText = null;
    scrollToBottom();
  };

  const renderHistory = (history: ChatHistoryMessage[]): void => {
    bodyInner.innerHTML = '';
    for (const m of history) {
      // Machine turns (presenter seeds, continuation ticks, media-end
      // nudges) ride history for the LLM but are never visitor bubbles —
      // on replay same as live.
      if (m.hidden) continue;
      if (m.role === 'user') appendUserMessage(m.content);
      else appendAssistant(m.content);
    }
    // A resumed conversation counts as STARTED (David 2026-07-13): the
    // thread owns the real estate — pre-conversation affordances
    // (composed opening, agent links) must not re-appear on cross-nav
    // resume just because this pageload hasn't seen a keystroke yet.
    if (history.some((m) => m.role === 'user')) {
      markChatStarted();
    }
  };

  const setStreaming = (next: boolean): void => {
    streaming = next;
    sendDisabledSig.value = next;
    if (!next) {
      // Promote the streamed token spans into a Markdown-rendered final.
      if (activeStreaming && activeAssistantText) {
        activeStreaming.setFinalMarkdown(activeAssistantText);
      }
      activeStreaming = null;
      activeAssistantText = '';
    }
  };

  const showError = (message: string): void => {
    const anchor = el('div', 'sp-msg-anchor');
    bodyInner.appendChild(anchor);
    mountPreactIsland(anchor, <ErrorBubble message={message} />);
    scrollToBottom();
  };

  const showTyping = (): void => {
    const anchor = el('div', 'sp-msg-anchor');
    bodyInner.appendChild(anchor);
    mountPreactIsland(anchor, <TypingBubble />);
    scrollToBottom();
  };

  // Thinking-indicator: live label that swaps as the agent calls more
  // tools. Cleared by removeTypingIndicator (the bubble carries
  // sp-msg-typing) on the first message chunk. Internal tools that
  // don't produce visitor-facing work (memory writes, emit*) return
  // null from labelForTool and are silently skipped.
  const thinkingLabelSig = signal<string>('');
  const showThinking = (label: string): void => {
    thinkingLabelSig.value = label;
    // If a thinking bubble is already mounted, the signal write
    // re-renders it in place — no need to re-mount.
    const existing = bodyInner.querySelector('.sp-msg-thinking');
    if (existing) return;
    // Replace any prior typing-dots bubble with the labeled variant
    // so we don't double-stack on first tool call.
    removeTypingIndicator();
    const anchor = el('div', 'sp-msg-anchor');
    bodyInner.appendChild(anchor);
    mountPreactIsland(anchor, <ThinkingBubble label={thinkingLabelSig} />);
    scrollToBottom();
  };

  /**
   * Remove the typing-indicator bubble if one is present. The bubble
   * itself lives inside a `sp-msg-anchor` wrapper (display:contents)
   * since the Preact rewrite — so we walk up to the anchor to also
   * tear out the now-unused Preact mount point.
   */
  const removeTypingIndicator = (): void => {
    const typing = bodyInner.querySelector('.sp-msg-typing');
    if (!typing) return;
    (typing.closest('.sp-msg-anchor') ?? typing).remove();
  };

  /**
   * Append Calendly-style prefill URL params to a booking URL. Calendly,
   * SavvyCal, and YouCanBookMe all recognise `name` + `email` query
   * params (Calendly also has `a1`..`an` for custom fields, ignored here
   * since the LLM tool doesn't capture them). Providers that don't
   * recognise the params drop them silently — no failure mode.
   */
  function withCalendlyPrefill(
    baseUrl: string,
    prefill: { name?: string; email?: string } | undefined,
  ): string {
    if (!prefill || (!prefill.name && !prefill.email)) return baseUrl;
    try {
      const url = new URL(baseUrl);
      if (prefill.name) url.searchParams.set('name', prefill.name);
      if (prefill.email) url.searchParams.set('email', prefill.email);
      return url.toString();
    } catch {
      return baseUrl;
    }
  }

  /**
   * Append the inline-embed hints Calendly uses to drop its own page
   * chrome and size to its container. Other providers ignore unknown
   * params, so this is a no-op for them. Calendly is the only provider
   * we explicitly know iframes cleanly; the modal's fallback link covers
   * any host that refuses embedding (X-Frame-Options / CSP frame-ancestors).
   */
  function withInlineEmbed(finalUrl: string): string {
    try {
      const url = new URL(finalUrl);
      if (/(^|\.)calendly\.com$/i.test(url.hostname)) {
        url.searchParams.set('embed_domain', window.location.hostname);
        url.searchParams.set('embed_type', 'Inline');
        url.searchParams.set('hide_gdpr_banner', '1');
      }
      return url.toString();
    } catch {
      return finalUrl;
    }
  }

  // Tracks the live booking modal so we never stack two and can tear the
  // last one down (Escape / close / backdrop) without leaking listeners.
  let bookingModalCleanup: (() => void) | null = null;
  let meetingBookedHandler:
    | ((p: { provider: string; bookingUrl: string }) => void)
    | null = null;

  /**
   * Open the booking calendar as an in-widget modal — an overlay card
   * with the provider (Calendly et al.) embedded as an iframe, so the
   * visitor books without leaving the host page. Replaces the old
   * new-tab popup, which browsers blocked from the SSE handler (no
   * user-gesture context) and which felt jarring even when it worked.
   * `finalUrl` is already prefilled (withCalendlyPrefill) before this call.
   */
  function openBookingModal(finalUrl: string): void {
    // Idempotent — collapse any existing modal first.
    bookingModalCleanup?.();

    const overlay = document.createElement('div');
    overlay.className = 'sp-booking-modal';

    const card = document.createElement('div');
    card.className = 'sp-booking-modal__card';

    const header = document.createElement('div');
    header.className = 'sp-booking-modal__header';

    const title = document.createElement('div');
    title.className = 'sp-booking-modal__title';
    // Lucide `calendar` icon (matches our admin icon set; replaces the
    // old 📅 emoji which didn't match the design system).
    title.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M8 2v4"/><path d="M16 2v4"/>' +
      '<rect width="18" height="18" x="3" y="4" rx="2"/>' +
      '<path d="M3 10h18"/></svg><span>Book a meeting</span>';

    const closeBtn = document.createElement('button');
    closeBtn.className = 'sp-booking-modal__close';
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', 'Close');
    // Lucide `x` icon — same paths the engage/landscape close buttons use.
    closeBtn.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M18 6L6 18"/><path d="M6 6L18 18"/></svg>';

    header.appendChild(title);
    header.appendChild(closeBtn);

    const frame = document.createElement('iframe');
    frame.className = 'sp-booking-modal__frame';
    frame.src = withInlineEmbed(finalUrl);
    frame.title = 'Booking calendar';
    frame.setAttribute('frameborder', '0');

    const footer = document.createElement('div');
    footer.className = 'sp-booking-modal__footer';
    const fallback = document.createElement('a');
    fallback.className = 'sp-booking-modal__fallback';
    fallback.href = finalUrl;
    fallback.target = '_blank';
    fallback.rel = 'noopener noreferrer';
    fallback.textContent = 'Trouble loading? Open in a new tab';
    footer.appendChild(fallback);

    card.appendChild(header);
    card.appendChild(frame);
    card.appendChild(footer);
    overlay.appendChild(card);
    document.body.appendChild(overlay);

    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') bookingModalCleanup?.();
    };
    // Booking-completion tracking — the provider posts a message when the
    // visitor finishes scheduling (Calendly `event_scheduled`, HubSpot
    // `meetingBookSucceeded`, …). That confirmation is the meeting-booked
    // ground truth (opening the modal ≠ booked); it's what produces the
    // `meeting-booked` session event. `detectBookingCompletion` maps each
    // provider's message shape → the provider name.
    const onProviderMessage = (e: MessageEvent): void => {
      const hit = detectBookingCompletion(e.data);
      if (hit) {
        meetingBookedHandler?.({ provider: hit.provider, bookingUrl: finalUrl });
      }
    };
    bookingModalCleanup = (): void => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('message', onProviderMessage);
      overlay.remove();
      bookingModalCleanup = null;
    };
    closeBtn.addEventListener('click', () => bookingModalCleanup?.());
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) bookingModalCleanup?.();
    });
    document.addEventListener('keydown', onKey);
    window.addEventListener('message', onProviderMessage);
  }

  /**
   * Render the inline contact-capture form inside the chat thread. One
   * input per requested field, pre-filled from `prefill` where available.
   * On submit: disables inputs, calls `contactCaptureHandler` (host POSTs
   * to /v1/contact-capture), replaces the form with a "Got it" confirmation.
   * On failure: re-enables inputs + surfaces an inline error.
   *
   * Kept as plain DOM (no Preact island) to match the bookMeeting CTA
   * pattern — minimal layout, no reactive state beyond submit lifecycle.
   */
  function renderContactForm(payload: {
    fields: Array<'name' | 'email' | 'phone' | 'company'>;
    prefill?: { name?: string; email?: string; phone?: string; company?: string };
    reason?: string;
    ctaLabel?: string;
    /** 'callback' = page a rep now (capture ladder, slice 3). */
    intent?: 'contact' | 'callback';
    /** F-3 — the agent form this card fulfils; echoed on the POST so
     *  the server runs its after-they-finish ladder. */
    formId?: string;
    /** F-3 — operator-authored success line. */
    confirmation?: string;
    /** F-4 — full-fidelity field list (per-form labels, types, email
     *  rules). Present when an agent form drives the card; absent =
     *  the legacy quartet render below, byte-identical. */
    formFields?: unknown;
  }): void {
    const wrap = el('div', 'sp-contact-form');
    if (payload.reason) {
      const reason = el('div', 'sp-contact-form__reason', payload.reason);
      wrap.appendChild(reason);
    }
    // Mount the card SYNCHRONOUSLY so it holds its slot in the thread —
    // the SSE stream keeps delivering messages while the lazy chunk
    // loads, and an async append would land the card AFTER prose that
    // followed it in the turn (David caught exactly this, 2026-08-28).
    // The chunk (or the legacy fallback) fills the mounted card in place.
    interactiveHost().appendChild(wrap);

    // F-4 full-fidelity path — the lazy forms chunk renders the form's
    // own fields (per-form labels, enum/multi, email rules). Chunk
    // failure falls back to the legacy quartet card — capture must
    // never dead-end on a network blip.
    const rawSpecs = payload.formFields;
    if (Array.isArray(rawSpecs) && rawSpecs.length > 0) {
      void loadForms()
        .then((m) => {
          const specs = m.parseFormFields(rawSpecs);
          if (specs.length > 0) renderFormFieldsCard(m, wrap, specs, payload);
          else renderLegacyContactForm(wrap, payload);
        })
        .catch(() => renderLegacyContactForm(wrap, payload));
      return;
    }
    renderLegacyContactForm(wrap, payload);
  }

  /**
   * Capture success = a RECEIPT, not a caption (David 2026-08-28): the
   * card collapses into a check + what we'll do with the address they
   * just gave — proof the right thing was captured, and a beat to spot
   * a typo'd email. The operator's authored confirmation line renders
   * as the AGENT speaking (appendAssistant) — in a chat surface, the
   * agent responding is the strongest "it worked" cue there is.
   */
  /** The hidden turn after a form lands when nothing else speaks (see renderCaptureSuccess). */
  const FORM_LANDED_DIRECTION = '(the visitor just submitted the form — it is done, their details are captured and the team will follow up. Say that in one line, in your own words and in the past tense, then offer the natural next step; never re-ask what it collected.)';

  function renderCaptureSuccess(
    wrap: HTMLElement,
    opts: { email?: string; confirmation?: string; intent?: 'contact' | 'callback' },
  ): void {
    const card = el('div', 'sp-contact-form__confirm sp-capture-receipt');
    const check = el('span', 'sp-capture-receipt__check', '✓');
    const text = el('span', 'sp-capture-receipt__text');
    text.textContent = opts.email
      ? `Sent — the team will reach you at ${opts.email}.`
      : opts.intent === 'callback'
        ? 'Sent — the team has your details and will reach out shortly.'
        : 'Sent — thanks!';
    card.appendChild(check);
    card.appendChild(text);
    wrap.replaceWith(card);
    const line = opts.confirmation?.trim();
    if (line) appendAssistant(line);
    // Tell the agent the form LANDED (capture-ack.ts): in a live call the
    // bot speaks the ack; without a call the thread's receipt + the
    // authored line above are the ack and the next turn reads the
    // captured facts server-side.
    const sentToVoice = notifyVoiceOfVisitorAction(formSubmittedAck(opts.email));
    // No call and no authored line = silence after the receipt (David
    // 2026-10-17: "the agent says nothing beyond Sent"). One hidden
    // stage direction makes the agent close the loop and offer the next
    // step; the captured facts reach it server-side on that turn.
    if (!sentToVoice && !line && sendHandler) {
      sendHandler(FORM_LANDED_DIRECTION, { fromVoice: false, hidden: true });
    }
    // In her sheet the receipt is a beat, not a fixture: she says the
    // line, and the card returns to captions once it has been read.
    if (card.closest('.spb-sheet')) {
      boundlessHandle?.setSpeech(line || text.textContent || '');
      setTimeout(() => card.remove(), 7000);
    }
    scrollToBottom();
  }

  /** "Sending… → ✓ Sent" beat on the submit button, then the receipt.
   *  The tiny causality pause is skipped for reduced-motion users. */
  function submitSuccessBeat(btn: HTMLButtonElement, done: () => void): void {
    btn.textContent = '✓ Sent';
    btn.classList.add('sp-submit--done');
    const reduce =
      typeof matchMedia === 'function' &&
      matchMedia('(prefers-reduced-motion: reduce)').matches;
    setTimeout(done, reduce ? 0 : 450);
  }

  /** The pre-F-4 quartet card — also the fallback when the forms chunk
   *  can't load. Byte-identical behavior to the original render. */
  function renderLegacyContactForm(
    wrap: HTMLElement,
    payload: {
      fields: Array<'name' | 'email' | 'phone' | 'company'>;
      prefill?: { name?: string; email?: string; phone?: string; company?: string };
      ctaLabel?: string;
      intent?: 'contact' | 'callback';
      formId?: string;
      confirmation?: string;
    },
  ): void {
    const inputs = new Map<string, HTMLInputElement>();
    const fieldMeta: Record<
      'name' | 'email' | 'phone' | 'company',
      { label: string; type: string; autocomplete: string; inputmode?: string }
    > = {
      name: { label: 'Name', type: 'text', autocomplete: 'name' },
      email: { label: 'Email', type: 'email', autocomplete: 'email' },
      phone: { label: 'Phone', type: 'tel', autocomplete: 'tel', inputmode: 'tel' },
      company: { label: 'Company', type: 'text', autocomplete: 'organization' },
    };
    for (const f of payload.fields) {
      const meta = fieldMeta[f];
      const row = el('label', 'sp-contact-form__row');
      const span = el('span', 'sp-contact-form__label', meta.label);
      const input = el('input', 'sp-contact-form__input') as HTMLInputElement;
      input.type = meta.type;
      input.name = f;
      input.placeholder = meta.label;
      input.setAttribute('autocomplete', meta.autocomplete);
      if (meta.inputmode) input.inputMode = meta.inputmode;
      if (payload.prefill && payload.prefill[f]) {
        input.value = payload.prefill[f] ?? '';
      }
      row.appendChild(span);
      row.appendChild(input);
      wrap.appendChild(row);
      inputs.set(f, input);
    }

    const errBox = el('div', 'sp-contact-form__error');
    errBox.style.display = 'none';
    wrap.appendChild(errBox);

    const btn = el(
      'button',
      'sp-contact-form__submit',
      payload.ctaLabel ?? 'Send',
    ) as HTMLButtonElement;
    btn.type = 'button';
    wrap.appendChild(btn);

    const submit = async (): Promise<void> => {
      if (!contactCaptureHandler) {
        errBox.textContent = 'Form submission is not available right now.';
        errBox.style.display = '';
        return;
      }
      const values: {
        name?: string;
        email?: string;
        phone?: string;
        company?: string;
      } = {};
      for (const [field, input] of inputs) {
        const v = input.value.trim();
        if (v) values[field as keyof typeof values] = v;
      }
      if (Object.keys(values).length === 0) {
        errBox.textContent = 'Please fill in at least one field.';
        errBox.style.display = '';
        return;
      }
      // Cheap email sanity check — server re-validates. Guards against
      // typo-on-submit ("david@gmail" without TLD) before we round-trip.
      if (values.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
        errBox.textContent = 'That email doesn\'t look right — mind double-checking?';
        errBox.style.display = '';
        return;
      }
      btn.disabled = true;
      btn.textContent = 'Sending…';
      errBox.style.display = 'none';
      inputs.forEach((i) => {
        i.disabled = true;
      });
      try {
        await contactCaptureHandler(values, payload.intent, payload.formId);
        submitSuccessBeat(btn, () =>
          renderCaptureSuccess(wrap, {
            email: values.email,
            confirmation: payload.confirmation,
            intent: payload.intent,
          }),
        );
      } catch (err) {
        btn.disabled = false;
        btn.textContent = payload.ctaLabel ?? 'Send';
        inputs.forEach((i) => {
          i.disabled = false;
        });
        errBox.textContent =
          err instanceof Error && err.message
            ? err.message
            : 'Something went wrong. Try again?';
        errBox.style.display = '';
      }
    };

    btn.addEventListener('click', () => {
      void submit();
    });
    // Enter on any input submits.
    inputs.forEach((i) => {
      i.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          void submit();
        }
      });
    });

    // (wrap is already mounted in the thread — renderContactForm holds
    // the slot synchronously; we only filled it.)
    // Focus the first empty input so the visitor can start typing
    // immediately (especially important for voice → form handoff).
    for (const input of inputs.values()) {
      if (!input.value) {
        input.focus();
        break;
      }
    }
  }

  /**
   * F-4 — the agent form's own fields drive the card (per-form labels,
   * enum/multi chips, date/int/bool inputs, email rules). Submit splits
   * quartet keys into `fields` and the rest into `answers`; the server
   * drops any answer key the form doesn't declare.
   */
  function renderFormFieldsCard(
    m: FormsModule,
    wrap: HTMLElement,
    specs: FormFieldSpec[],
    payload: {
      prefill?: { name?: string; email?: string; phone?: string; company?: string };
      ctaLabel?: string;
      intent?: 'contact' | 'callback';
      formId?: string;
      confirmation?: string;
    },
  ): void {
    const handle = m.buildFormFields(wrap, specs, payload.prefill, 'sp-contact-form');

    const errBox = el('div', 'sp-contact-form__error');
    errBox.style.display = 'none';
    wrap.appendChild(errBox);

    const btn = el(
      'button',
      'sp-contact-form__submit',
      payload.ctaLabel ?? 'Send',
    ) as HTMLButtonElement;
    btn.type = 'button';
    wrap.appendChild(btn);

    let emailNudged = false;
    const submit = async (): Promise<void> => {
      if (!contactCaptureHandler) {
        errBox.textContent = 'Form submission is not available right now.';
        errBox.style.display = '';
        return;
      }
      const values = handle.read();
      const check = m.validateFields(specs, values, emailNudged);
      if (!check.ok) {
        errBox.textContent = check.message;
        errBox.style.display = '';
        if (check.nudge) emailNudged = true;
        handle.focus(check.focusKey);
        return;
      }
      const { fields, answers } = m.splitSubmission(values);
      if (Object.keys(fields).length === 0 && Object.keys(answers).length === 0) {
        errBox.textContent = 'Please fill in at least one field.';
        errBox.style.display = '';
        return;
      }
      btn.disabled = true;
      btn.textContent = 'Sending…';
      errBox.style.display = 'none';
      handle.setDisabled(true);
      try {
        await contactCaptureHandler(fields, payload.intent, payload.formId, answers);
        submitSuccessBeat(btn, () =>
          renderCaptureSuccess(wrap, {
            email: fields.email,
            confirmation: payload.confirmation,
            intent: payload.intent,
          }),
        );
      } catch (err) {
        btn.disabled = false;
        btn.textContent = payload.ctaLabel ?? 'Send';
        handle.setDisabled(false);
        errBox.textContent =
          err instanceof Error && err.message
            ? err.message
            : 'Something went wrong. Try again?';
        errBox.style.display = '';
      }
    };

    btn.addEventListener('click', () => {
      void submit();
    });
    wrap.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && (e.target as HTMLElement)?.tagName === 'INPUT') {
        e.preventDefault();
        void submit();
      }
    });

    // (wrap already holds its thread slot — mounted synchronously by
    // renderContactForm.) The dispatcher's scrollToBottom ran before
    // the chunk resolved — scroll again now the fields exist.
    scrollToBottom();
    handle.focusFirstEmpty();
  }

  // Legacy `agent-chat-v2/ChatInterface.vue` rendered recommendations
  // as inline cards inside the assistant message bubble (lines 106-121
  // of the .vue). We render them as their own assistant-aligned row so
  // a single bubble doesn't have to contain both prose AND cards — the
  // bot's `emitMessage` runs ahead of `emitRecommendations` and the
  // cards just trail after.
  //
  // First leaf migrated to Preact (branch widget/preact-rewrite). The
  // imperative shell still creates the message-bubble wrapper so the
  // existing layout/scroll machinery is unchanged; the cards themselves
  // are a Preact island rendered into the bubble's content div.
  const appendRecommendationCards = (items: PlaylistItem[]): void => {
    if (items.length === 0) return;
    const wrap = el('div', 'sp-msg sp-msg-assistant sp-msg-recommendations');
    const content = el('div', 'sp-msg-content');
    wrap.appendChild(content);
    bodyInner.appendChild(wrap);
    mountPreactIsland(
      content,
      <RecommendationCards items={items} onSelect={(it) => openContent(it, { source: 'visitor' })} />,
    );
    scrollToBottom();
  };

  const handleChatEvent = (event: ChatEvent): void => {
    // Presenter (agent-led): the stage router consumes the five
    // tour/agenda events; everything else falls through to the switch.
    if (presenterStage?.handleEvent(event)) return;
    switch (event.type) {
      case 'message': {
        removeTypingIndicator();
        if (!activeStreaming) {
          activeStreaming = appendStreamingAssistant().streaming;
        }
        activeStreaming.appendDelta(event.payload.text);
        activeAssistantText += event.payload.text;
        boundlessHandle?.setSpeech(activeAssistantText);
        scrollToBottom();
        break;
      }
      case 'suggestions': {
        suggestionsSig.value = event.payload.items;
        // Ghost-text suggestion: swap the textarea's native placeholder
        // to the first suggestion's label when the input is empty.
        // Press Enter on an empty input to accept (handled in keydown).
        // Restored to `configuredPlaceholder` on send / typing.
        const first = event.payload.items[0]?.label;
        ghostSuggestion = first ?? null;
        ghostIndex = 0;
        if (first && !input.value) {
          input.placeholder = ghostPlaceholder(first);
        }
        renderSuggestBubbles(event.payload.items);
        break;
      }
      case 'book-meeting': {
        clearActiveHighlight();
        const baseUrl = event.payload.bookingUrl;
        if (!baseUrl) break;
        // Calendly recognises `?name=…&email=…` URL params and pre-fills
        // the booking form. Pass through anything the server captured
        // about the visitor (HubSpot identify or chat-prose capture) so
        // they only have to pick a time. Other providers (Chili Piper,
        // Calendar.app) ignore unknown params — safe to always append.
        const url = withCalendlyPrefill(baseUrl, event.payload.prefill);
        // Open the booking calendar as an in-widget modal (iframe embed).
        // This works from the SSE handler with no user-gesture context —
        // unlike the old window.open popup, which browsers silently
        // blocked, leaving the visitor staring at a chat that claimed the
        // calendar was opening while nothing happened. The modal's footer
        // link is the escape hatch for providers that refuse iframing.
        openBookingModal(url);
        break;
      }
      case 'meeting-slots': {
        // v3-B — concrete times in-chat; a tap books deterministically.
        clearActiveHighlight();
        renderMeetingSlots(event.payload);
        break;
      }
      case 'contact-form': {
        clearActiveHighlight();
        renderContactForm(event.payload);
        scrollToBottom();
        break;
      }
      case 'rep-message': {
        // Text Relay v1 — a human rep sent this message from the
        // admin. The agent runtime suppresses its next turn so we
        // don't have to coordinate here; just render the bubble.
        appendRepMessage(event.payload);
        break;
      }
      case 'rep-joined': {
        // Slice E — rep claimed the conversation. Render a centered
        // system line so the visitor has context for who they're
        // suddenly talking to. Format: "Sarah from the team joined
        // the chat." Falls back to a generic name when none provided.
        const name = event.payload.repName?.trim() || 'Someone from the team';
        const team = event.payload.teamLabel?.trim();
        const line = team
          ? `${name} from ${team} joined the chat.`
          : `${name} joined the chat.`;
        // Drop any lingering AI typing dots — the agent is no longer
        // running here. The 5-min server-side suppression keeps it
        // that way; this just clears the UI state.
        removeTypingIndicator();
        appendSystemLine(line);
        break;
      }
      case 'rep-released': {
        // Slice E — rep handed back to the AI. Clear any in-flight
        // rep typing indicator + render the release notice.
        clearRepTypingIndicator();
        const name = event.payload.repName?.trim() || 'The team';
        appendSystemLine(`${name} handed back to the AI.`);
        break;
      }
      case 'rep-typing': {
        // Slice E — rep is composing right now. Each tick refreshes a
        // short-lived "rep is typing…" UI; auto-clears after ~4s
        // without a refresh (longer than the admin's 1s heartbeat).
        showRepTyping(event.payload);
        break;
      }
      case 'rep-invite': {
        // Rep-initiated proactive nudge — a human reaches out to a
        // visitor who hasn't engaged. A one-time toaster (no stacking)
        // renders even with chat closed; accepting opens the panel and
        // seats the rep's message so the conversation starts warm.
        if (document.querySelector('.sp-rep-invite')) break;
        const esc = (s: string): string =>
          s.replace(
            /[&<>"]/g,
            (ch) =>
              ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[ch] ??
              ch,
          );
        const repName = event.payload.repName?.trim() || 'A specialist';
        const avatarUrl = event.payload.repAvatarUrl?.trim();
        const message =
          event.payload.message?.trim() ||
          `Hi! ${repName} here — happy to answer any questions.`;

        const toast = document.createElement('div');
        toast.className = 'sp-rep-invite';
        Object.assign(toast.style, {
          position: 'fixed',
          zIndex: '2147483640',
          right: '20px',
          bottom: '96px',
          width: '320px',
          maxWidth: 'calc(100vw - 40px)',
          background: '#fff',
          borderRadius: '14px',
          boxShadow: '0 18px 48px rgba(0,0,0,0.22)',
          padding: '14px',
          font: '400 13px/1.4 system-ui, -apple-system, sans-serif',
          color: '#1a1a1a',
          border: '1px solid rgba(0,0,0,0.06)',
        });
        const avatarHtml = avatarUrl
          ? '<img src="' +
            esc(avatarUrl) +
            '" alt="" style="width:40px;height:40px;border-radius:50%;object-fit:cover;flex:0 0 auto;" />'
          : '<div style="width:40px;height:40px;border-radius:50%;flex:0 0 auto;' +
            'background:var(--sp-primary,#0b3398);color:#fff;display:flex;' +
            'align-items:center;justify-content:center;font-weight:600;">' +
            esc(repName.charAt(0).toUpperCase()) +
            '</div>';
        toast.innerHTML =
          '<div style="display:flex;gap:10px;align-items:flex-start;">' +
          avatarHtml +
          '<div style="min-width:0;flex:1;">' +
          '<div style="font-weight:600;margin-bottom:2px;">' +
          esc(repName) +
          ' is inviting you to chat</div>' +
          '<div style="color:#555;">' +
          esc(message) +
          '</div></div>' +
          '<button data-sp-dismiss aria-label="Dismiss" style="flex:0 0 auto;border:0;' +
          'background:transparent;color:#999;cursor:pointer;font-size:16px;line-height:1;padding:0 2px;">×</button></div>' +
          '<div style="display:flex;gap:8px;margin-top:12px;">' +
          '<button data-sp-chat style="flex:1;border:0;border-radius:8px;' +
          'background:var(--sp-primary,#0b3398);color:#fff;font-weight:600;' +
          'font-size:13px;padding:9px;cursor:pointer;">Chat now</button>' +
          '<button data-sp-later style="border:1px solid rgba(0,0,0,0.12);' +
          'border-radius:8px;background:#fff;color:#555;font-size:13px;' +
          'padding:9px 12px;cursor:pointer;">Maybe later</button></div>';
        document.body.appendChild(toast);

        const remove = (): void => toast.remove();
        toast.querySelector('[data-sp-dismiss]')?.addEventListener('click', remove);
        toast.querySelector('[data-sp-later]')?.addEventListener('click', remove);
        toast.querySelector('[data-sp-chat]')?.addEventListener('click', () => {
          remove();
          applyAction({ type: 'expand' });
          appendRepMessage({
            text: message,
            repName,
            ...(avatarUrl ? { repAvatarUrl: avatarUrl } : {}),
          });
        });
        break;
      }
      case 'call-invite': {
        // Slice F — rep wants to escalate to a Daily call. Render
        // accept/decline card inline; visitor's tap mounts the
        // Daily Prebuilt overlay or POSTs the decline.
        renderCallInvite(event.payload);
        break;
      }
      case 'call-ended': {
        // Slice F — either side hung up (or server-pushed end). Tear
        // down the overlay if it's still up + clear the invite card.
        teardownCall('call-ended');
        appendSystemLine('Call ended.');
        break;
      }
      case 'call-declined': {
        // Slice F — visitor's decline. Server pushes this back so the
        // widget can clean the pending-invite UI on the same client
        // and the admin sees the decline. Idempotent with the local
        // decline handler.
        clearPendingCallInvite();
        break;
      }
      case 'navigate-url': {
        // Drop stale tool side-effects when the visitor just navigated.
        // See voice/daily-session.ts isInStaleToolWindow — voice bot
        // can dispatch a navigate that was decided pre-nav, the SSE
        // arrives 200-500ms after the visitor already moved, jarring.
        if (isInStaleToolWindow()) {
          console.info('[ui] dropping stale navigate-url (post-nav window)');
          break;
        }
        clearActiveHighlight();
        // NAVIGATE CLOSES THE STAGE: the agent is taking the visitor to a
        // page — a tour viewer left covering it defeats the move (live
        // 2026-08-28: "you still have the content experience open").
        if (activeTourPublicId()) applyAction({ type: 'endContent' });
        // Mobile yield-viewport: tools that point at host-page content
        // (navigate, highlight) need the page visible. Engage / Rail /
        // Skyscraper on mobile cover the page — useless when the agent
        // is saying "look at this." Drop to the Site Agent prompt pill
        // (INPUT) so the page shows above the compact pill. Desktop has
        // horizontal headroom for the rail; leave it alone there.
        if (isMobile() && currentLayout !== LAYOUTS.INPUT) {
          setLayout(LAYOUTS.INPUT);
        }
        try {
          const next = new URL(event.payload.url, window.location.href);
          // Treat `www.x.com` and `x.com` as same-site so the bot can
          // navigate to a URL minted from the agent's ingested page
          // list even when the visitor's current page uses the other
          // host variant. Exact-origin match was too strict — silently
          // dropped legitimate same-site navigations.
          const rebasedNav = rebaseToVisitorOrigin(next, window.location);
          if (rebasedNav) {
            // Persist FIRST — this unloads the page mid-turn, before
            // the post-turn persist would ever run.
            beforeHostNavigate?.(rebasedNav);
            // Rebased onto the visitor's current host so sessionStorage
            // (the conversation snapshot) survives the navigation.
            window.location.href = rebasedNav.href;
          } else {
            console.warn(
              '[showpilot] navigate-url cross-site rejected',
              next.href,
              'vs',
              window.location.href,
            );
          }
        } catch {
          /* malformed — ignore */
        }
        break;
      }
      case 'highlight-element': {
        // Mobile yield-viewport (see navigate-url comment). The agent
        // is about to direct attention to an on-page element; a
        // covering view defeats the purpose. Drop to INPUT (Site Agent
        // prompt) so the page is visible and the highlight reads.
        // Desktop stays put — rail + page side-by-side works.
        if (isMobile() && currentLayout !== LAYOUTS.INPUT) {
          setLayout(LAYOUTS.INPUT);
        }
        // Cross-page case: agent passed a different URL than the
        // visitor's current page. Hand off to the navigate handler
        // (set via setHighlightNavigator) which queues the highlight
        // in sessionStorage and triggers a same-origin navigation.
        // The next-page widget picks up the queued highlight on boot.
        const targetUrl = event.payload.url;
        if (targetUrl && highlightNavigator) {
          try {
            const target = new URL(targetUrl, window.location.href);
            const rebasedTarget = rebaseToVisitorOrigin(target, window.location);
            const samePath = target.pathname === window.location.pathname;
            if (rebasedTarget && !samePath) {
              highlightNavigator({
                text: event.payload.text,
                reason: event.payload.reason,
                url: rebasedTarget.href,
              });
              break;
            }
          } catch {
            /* malformed url — fall through to in-place highlight */
          }
        }
        highlightOnHostPage(event.payload.text, firstNameSig.value);
        break;
      }
      case 'open-content': {
        // Stale-tool gate: visitor just navigated → drop the side-effect.
        // See navigate-url case above for the rationale. Bug report:
        // Alex 2026-05-18 "Let's watch YouTube AI → nav → content_view fires."
        if (isInStaleToolWindow()) {
          console.info('[ui] dropping stale open-content (post-nav window)');
          break;
        }
        clearActiveHighlight();
        const p = event.payload;
        // Prefer the server-resolved metadata (voice path enriches);
        // fall back to a prior recommendations payload (text path
        // sometimes only emits id+type); finally a stub so the viewer
        // at least shows the title.
        const known = recommendationsById.get(p.contentId);
        const ext = p.contentExtension || known?.content_extension || p.contentType;
        const item: PlaylistItem = {
          content_id: p.contentId,
          content_type: p.contentType,
          content_extension: ext,
          name: p.title || known?.name || 'Content',
          image: p.thumbnailUrl ?? known?.image ?? null,
          url: p.url || known?.url || null,
          embed_url: p.embedUrl ?? known?.embed_url ?? null,
        };
        // Append to the running playlist so the drawer isn't empty
        // when the bot skipped content_recommend and jumped straight
        // to content_view. Dedupe by content_id.
        if (!currentPlaylistItems.some((it) => it.content_id === item.content_id)) {
          currentPlaylistItems.push(item);
          hasContentSig.value = true;
          recommendationsById.set(item.content_id, item);
          playlistDrawer.setPlaylist(currentPlaylistItems);
        }
        openContent(item, { page: p.page, timestampSecs: p.timestampSecs });
        break;
      }
      case 'content-seek': {
        // Stale-tool gate: visitor just navigated → drop the side-effect.
        if (isInStaleToolWindow()) {
          console.info('[ui] dropping stale content-seek (post-nav window)');
          break;
        }
        const p = event.payload;
        // Already open → seek in place (smooth, esp. video currentTime).
        if (currentContentId && p.contentId === currentContentId) {
          contentPanel.seekTo({ page: p.page, timestampSecs: p.timestampSecs });
          break;
        }
        // Not open → SELF-HEAL: open the asset AT the page/timestamp with
        // the metadata the server shipped. Makes "go to page 6" work
        // whether the agent picked openContent or contentSeek — no more
        // silent no-op when the model mis-picks the seek tool.
        clearActiveHighlight();
        const known = recommendationsById.get(p.contentId);
        const url = p.url || known?.url || null;
        if (!url) {
          console.info('[ui] content-seek: no url to open', p.contentId);
          break;
        }
        const ext = p.contentExtension || known?.content_extension || p.contentType || '';
        const item: PlaylistItem = {
          content_id: p.contentId,
          content_type: p.contentType ?? known?.content_type ?? '',
          content_extension: ext,
          name: p.title || known?.name || 'Content',
          image: p.thumbnailUrl ?? known?.image ?? null,
          url,
          embed_url: p.embedUrl ?? known?.embed_url ?? null,
        };
        if (!currentPlaylistItems.some((it) => it.content_id === item.content_id)) {
          currentPlaylistItems.push(item);
          hasContentSig.value = true;
          recommendationsById.set(item.content_id, item);
          playlistDrawer.setPlaylist(currentPlaylistItems);
        }
        openContent(item, { page: p.page, timestampSecs: p.timestampSecs });
        break;
      }
      case 'error': {
        removeTypingIndicator();
        showError(event.payload.message);
        break;
      }
      case 'journey-step': {
        removeTypingIndicator();
        appendJourneyStep(
          bodyInner,
          event.payload as JourneyStepPayload,
          (stepId, value) => {
            // Echo the visitor's pick as a user bubble so the chat
            // transcript reads naturally. For multi-select we join the
            // chosen labels.
            const echo = Array.isArray(value) ? value.join(', ') : value;
            if (echo) appendUserMessage(echo);
            if (journeyAnswerHandler) journeyAnswerHandler(stepId, value);
          },
        );
        scrollToBottom();
        break;
      }
      case 'journey-complete': {
        const note = el('div', 'sp-step-complete', 'Journey complete');
        bodyInner.appendChild(note);
        awaitingPostJourneyRecs = true;
        scrollToBottom();
        break;
      }
      case 'recommendations': {
        // Map SSE items → PlaylistItem shape the drawer + content
        // panel expect. Both shapes use `content_id`/`content_type`/etc.
        const items: PlaylistItem[] = event.payload.items.map((it) => ({
          content_id: it.contentId,
          content_type: it.contentType,
          content_extension: it.contentExtension || it.contentType,
          name: it.title,
          image: it.thumbnailUrl,
          url: it.url,
          embed_url: it.embedUrl ?? null,
        }));
        if (items.length === 0) break;
        // Index by every id shape so the next `open-content` event can
        // resolve metadata even when the server payload is sparse.
        for (const it of items) recommendationsById.set(it.content_id, it);
        // Merge into the running playlist (dedupe by id) so a fresh
        // content_recommend batch ADDs to the drawer instead of
        // replacing any items the visitor already opened mid-session.
        for (const it of items) {
          if (!currentPlaylistItems.some((x) => x.content_id === it.content_id)) {
            currentPlaylistItems.push(it);
            hasContentSig.value = true;
          }
        }
        // Inline cards in the chat thread (legacy parity — visitor
        // can click any card to mount it in the viewer panel) AND
        // populate the FULL-layout playlist drawer.
        appendRecommendationCards(items);
        playlistDrawer.setPlaylist(currentPlaylistItems);
        // Post-journey reveal: open first item + promote to FULL so
        // the playlist is the visitor's main canvas. For mid-chat
        // recommendations (no journey just completed), just populate
        // the drawer silently — they can click in if they want.
        if (awaitingPostJourneyRecs) {
          openContent(items[0]!);
          awaitingPostJourneyRecs = false;
        }
        break;
      }
      case 'speak': {
        // Play the agent's spoken reply. Failures are non-fatal —
        // chat already rendered the text, audio is bonus.
        audioSpeech.play(event.payload.audioUrl).catch((err) => {
          console.warn('[showpilot] tts playback failed', err);
        });
        break;
      }
      case 'tool-call': {
        const label = labelForTool(event.payload.name);
        if (label) showThinking(label);
        break;
      }
      case 'done':
        // Composer CTA on readiness (U1). No `cta` = older server → legacy.
        bookVisibleSig.value = event.payload.cta
          ? event.payload.cta.book
          : meetingEnabledForCta;
        // Turn closed. Make sure no thinking / typing bubble is left
        // mounted — if the turn ended on a tool result with no
        // follow-up message chunk, the bubble stays animating forever.
        removeTypingIndicator();
        // Presenter pacing — a turn that moved the stage schedules the
        // continuation tick (the module holds the dwell + budget rules).
        presenterStage?.onTurnDone();
        // Suggestions can arrive mid-stream — re-append the bubbles so
        // they sit BELOW the finished assistant message.
        if (suggestFlowEl) {
          bodyInner.appendChild(suggestFlowEl);
          scrollToBottom();
        }
        break;
    }
  };

  // Show typing indicator the moment streaming starts
  const _setStreaming = setStreaming;
  const trackedSetStreaming = (next: boolean): void => {
    _setStreaming(next);
    if (next && !activeStreaming) {
      // Show typing dots — will be replaced by first message chunk
      showTyping();
    }
  };

  syncBodyClass();
  setLayout(currentLayout);
  syncStateAttr();

  /** Tracks which playlist item is currently shown in the content panel
   * so prev/next can walk siblings. setSelected mirrors this into the
   * drawer's highlighted-row state — the local var is what nav reads. */
  let currentContentId: string | null = null;
  /** The item behind currentContentId — activeTourPublicId reads it. */
  let currentContentItem: PlaylistItem | null = null;
  /** TOUR CANVAS (David, 2026-08-25): a tour on the stage claims the
      playlist rail's width — the reading area is the product. One root
      class; CSS hides the drawer only while a tour is the active item,
      so every other content type keeps today's layout untouched. */
  const syncTourStage = (): void => {
    root.classList.toggle('sp-tour-stage', currentContentItem?.content_type === 'tour');
    // Beam content mode sizes the stage to a 16:9 picture for video/tour.
    root.classList.toggle('sp-cinema-stage', isCinemaContent(currentContentItem));
  };

  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const CONTENT_TOUR_URL_RE = /\/t\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;
  const activeTourPublicId = (): string | null => {
    const it = currentContentItem;
    if (!it || it.content_type !== 'tour') return null;
    const id = it.content_public_id ?? CONTENT_TOUR_URL_RE.exec(it.url ?? '')?.[1] ?? null;
    return id && UUID_RE.test(id) ? id : null;
  };

  /** Sync the content panel's prev/next button enabled state. */
  function updateContentNav(it: PlaylistItem): void {
    const idx = currentPlaylistItems.findIndex((x) => x.content_id === it.content_id);
    contentPanel.setNav({
      hasPrev: idx > 0,
      hasNext: idx >= 0 && idx < currentPlaylistItems.length - 1,
    });
  }

  /** Move to a sibling within currentPlaylistItems. delta = -1 / +1. */
  function navigateContent(delta: number): void {
    if (!currentContentId) return;
    const idx = currentPlaylistItems.findIndex((x) => x.content_id === currentContentId);
    if (idx < 0) return;
    const next = currentPlaylistItems[idx + delta];
    if (!next) return;
    contentPanel.setContent(next);
    playlistDrawer.setSelected(next);
    currentContentId = next.content_id;
    currentContentItem = next;
    syncTourStage();
    updateContentNav(next);
  }
  contentPanel.onPrev(() => navigateContent(-1));
  contentPanel.onNext(() => navigateContent(+1));
  contentPanel.onProgress((p) => contentProgressHandler?.(p));

  // Swipe-nav across the playlist on touch devices. Landscape fullscreen
  // media hides the prev/next chrome — swipe is the navigation. Swipe
  // LEFT (deltaX < 0) advances to next item; swipe RIGHT (deltaX > 0)
  // goes to previous. Only horizontal swipes; if the gesture has more
  // vertical motion (scrolling) we ignore. Threshold ~60px so we don't
  // misread tap-with-finger-drift as a swipe.
  {
    let startX = 0;
    let startY = 0;
    let startedSwipe = false;
    contentPanel.el.addEventListener(
      'touchstart',
      (e) => {
        if (e.touches.length !== 1) return;
        startX = e.touches[0]!.clientX;
        startY = e.touches[0]!.clientY;
        startedSwipe = true;
      },
      { passive: true },
    );
    contentPanel.el.addEventListener(
      'touchend',
      (e) => {
        if (!startedSwipe) return;
        startedSwipe = false;
        const t = e.changedTouches[0];
        if (!t) return;
        const dx = t.clientX - startX;
        const dy = t.clientY - startY;
        if (Math.abs(dx) < 60) return; // not enough horizontal travel
        if (Math.abs(dy) > Math.abs(dx) * 0.75) return; // mostly vertical
        navigateContent(dx < 0 ? +1 : -1);
      },
      { passive: true },
    );
  }

  // Hook a recommendation card click to open it in the content panel +
  // enter the experience runtime overlay (visual → FULL).
  function openContent(
    it: PlaylistItem,
    opts?: { page?: number; timestampSecs?: number; source?: 'agent' | 'visitor' },
  ): void {
    // GTM bridge — every open flows through here or the drawer's
    // onSelect. `source` lets marketers split agent-driven opens from
    // visitor clicks (recommendation CTR). Guarded so a seek/re-open
    // of the same item doesn't double-fire.
    if (currentContentId !== it.content_id) {
      pushMilestone('content_viewed', {
        title: it.name,
        type: it.content_type,
        url: it.url,
        source: opts?.source ?? 'agent',
      });
    }
    contentPanel.setContent(it, opts);
    playlistDrawer.setSelected(it);
    currentContentId = it.content_id;
    currentContentItem = it;
    syncTourStage();
    updateContentNav(it);
    applyAction({ type: 'showContent' });
    // On tablet / mobile FULL the chat column overlays the content as a
    // bottom sheet. Default it to collapsed when the visitor opens
    // content — they tapped to SEE the content, not to read chat. The
    // sheet handle still lets them drag the chat back up.
    setBottomSheet(false);
    // Notify the host so the admin Conversations inbox can populate
    // the Content tab regardless of source (LLM tool, recommendation
    // card, agent-link chip, drawer click). Fire-and-forget — viewer
    // already opened, persistence is best-effort.
    try {
      contentOpenedHandler?.(it);
    } catch {
      // Never let tracking break the visitor flow.
    }
  }
  playlistDrawer.onSelect((it) => {
    // Drawer selects bypass openContent (no layout transition needed) —
    // mirror its GTM push here so drawer clicks count as visitor opens.
    if (currentContentId !== it.content_id) {
      pushMilestone('content_viewed', {
        title: it.name,
        type: it.content_type,
        url: it.url,
        source: 'visitor',
      });
    }
    contentPanel.setContent(it);
    playlistDrawer.setSelected(it);
    currentContentId = it.content_id;
    currentContentItem = it;
    updateContentNav(it);
  });

  let presenterStage: PresenterStage | null = null;

  /** Presenter: a hub tour onto the stage (content pane), agent mode.
      Prefer the durable-playlist item (name/cover from the server);
      otherwise a stub whose /t/ url lets TourViewer resolve the id. */
  const openPresenterTour = (tourPublicId: string): void => {
    const known = currentPlaylistItems.find(
      (it) => it.content_id === tourPublicId || it.content_public_id === tourPublicId,
    );
    const apiBase =
      (typeof window !== 'undefined' ? window.SHOWPILOT_CONFIG?.apiBaseUrl : undefined) ?? '';
    const item: PlaylistItem = known ?? {
      content_id: tourPublicId,
      content_type: 'tour',
      content_extension: 'tour',
      content_public_id: tourPublicId,
      name: 'Tour',
      url: apiBase ? `${apiBase}/t/${tourPublicId}?embed=1` : null,
    };
    if (!currentPlaylistItems.some((it) => it.content_id === item.content_id)) {
      currentPlaylistItems.push(item);
      playlistDrawer.setPlaylist(currentPlaylistItems);
    }
    openContent(item);
  };

  const closePresenterStage = (): void => {
    // Route through the one choke point every close path uses — the
    // viewer unmounts (audio actually stops) and the tour-closed ack
    // fires exactly like a visitor-side close.
    if (widgetState.experienceActive) applyAction({ type: 'endContent' });
  };

  const loadDurablePlaylist = (items: PlaylistItem[]): void => {
    if (items.length === 0) return;
    currentPlaylistItems.length = 0;
    currentPlaylistItems.push(...items);
    playlistDrawer.setPlaylist(items);
    // Open the first item instantly — this is the durable first touch,
    // no LLM in the path. The agent's welcome streams alongside it.
    openContent(items[0]!);
  };

  const mountExperienceGate = (opts: {
    fields: string[];
    mode: 'capture' | 'reverse';
    lockedCount: number;
    onCaptured: () => void;
    formFields?: unknown;
    formId?: string | null;
  }): (() => void) => {
    const overlay = el('div', 'sp-experience-gate');
    const card = el('div', 'sp-experience-gate__card');
    const title = el(
      'div',
      'sp-experience-gate__title',
      opts.mode === 'reverse' ? 'Continue the walk-through' : 'Get instant access',
    );
    const sub = el(
      'div',
      'sp-experience-gate__sub',
      opts.mode === 'reverse'
        ? `Enter your details to unlock the rest${opts.lockedCount > 0 ? ` (${opts.lockedCount} more)` : ''}.`
        : 'Enter your details to view this content.',
    );
    card.appendChild(title);
    card.appendChild(sub);

    // G-1 — the content_gate form's fields drive the card via the lazy
    // forms chunk; legacy bare-key render otherwise (and on chunk
    // failure — the gate must never dead-end a visitor).
    const rawSpecs = opts.formFields;
    const wantForm = Array.isArray(rawSpecs) && rawSpecs.length > 0;

    const finishBuild = (m: FormsModule | null): void => {
      const specs = m ? m.parseFormFields(rawSpecs) : [];
      const formDriven = m !== null && specs.length > 0;
      const handle = formDriven && m
        ? m.buildFormFields(card, specs, undefined, 'sp-experience-gate')
        : null;
      const inputs = new Map<string, HTMLInputElement>();
      if (!formDriven) {
        const validFields = opts.fields.filter(
          (f): f is 'name' | 'email' | 'phone' | 'company' =>
            f === 'name' || f === 'email' || f === 'phone' || f === 'company',
        );
        for (const f of validFields.length ? validFields : (['name', 'email'] as const)) {
          const input = el('input', 'sp-experience-gate__input') as HTMLInputElement;
          input.type = f === 'email' ? 'email' : f === 'phone' ? 'tel' : 'text';
          input.name = f;
          input.placeholder = f.charAt(0).toUpperCase() + f.slice(1);
          input.setAttribute('autocomplete', f === 'company' ? 'organization' : f);
          card.appendChild(input);
          inputs.set(f, input);
        }
      }
      const err = el('div', 'sp-experience-gate__error');
      err.style.display = 'none';
      card.appendChild(err);
      const btn = el('button', 'sp-experience-gate__submit', 'Unlock') as HTMLButtonElement;
      btn.type = 'button';
      card.appendChild(btn);
      overlay.appendChild(card);
      contentPanel.el.appendChild(overlay);

      let emailNudged = false;
      const submit = async (): Promise<void> => {
        if (!contactCaptureHandler) {
          err.textContent = 'Capture is not available right now.';
          err.style.display = '';
          return;
        }
        let fields: Record<string, string>;
        let answers: Record<string, string> | undefined;
        if (formDriven && m && handle) {
          const values = handle.read();
          const check = m.validateFields(specs, values, emailNudged);
          if (!check.ok) {
            err.textContent = check.message;
            err.style.display = '';
            if (check.nudge) emailNudged = true;
            handle.focus(check.focusKey);
            return;
          }
          const split = m.splitSubmission(values);
          fields = split.fields as Record<string, string>;
          answers = split.answers;
        } else {
          const values: Record<string, string> = {};
          for (const [f, input] of inputs) {
            const v = input.value.trim();
            if (v) values[f] = v;
          }
          if (!values['email'] || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values['email'])) {
            err.textContent = 'A valid email is required.';
            err.style.display = '';
            return;
          }
          fields = values;
        }
        btn.disabled = true;
        btn.textContent = 'Unlocking…';
        err.style.display = 'none';
        handle?.setDisabled(true);
        try {
          // formId runs the gate form's after-ladder (conversion event,
          // notify, redirect) server-side.
          await contactCaptureHandler(fields, undefined, opts.formId ?? undefined, answers);
          // "✓ Unlocked" beat — the content appearing is the real cue;
          // this makes the cause-effect read before the overlay lifts.
          btn.textContent = '✓ Unlocked';
          btn.classList.add('sp-submit--done');
          const reduce =
            typeof matchMedia === 'function' &&
            matchMedia('(prefers-reduced-motion: reduce)').matches;
          setTimeout(
            () => {
              opts.onCaptured();
              overlay.remove();
            },
            reduce ? 0 : 400,
          );
        } catch {
          btn.disabled = false;
          btn.textContent = 'Unlock';
          handle?.setDisabled(false);
          err.textContent = 'Something went wrong — please try again.';
          err.style.display = '';
        }
      };
      btn.addEventListener('click', () => void submit());
      const onEnter = (e: Event): void => {
        if ((e as KeyboardEvent).key === 'Enter') void submit();
      };
      if (formDriven) {
        card.addEventListener('keydown', (e) => {
          if ((e.target as HTMLElement)?.tagName === 'INPUT') onEnter(e);
        });
      } else {
        for (const input of inputs.values()) {
          input.addEventListener('keydown', onEnter);
        }
      }
    };

    if (wantForm) {
      void loadForms()
        .then((m) => finishBuild(m))
        .catch(() => finishBuild(null));
    } else {
      finishBuild(null);
    }
    return () => overlay.remove();
  };

  /**
   * Opening Turn — apply a `compose_opening` decision from /v1/decide.
   * The static greeter painted instantly at boot (latency shape: fast
   * static first, personalization chimes in); the composed opening
   * APPENDS as a follow-up bubble, and the chips + ghost suggestion
   * replace the input hints. Pre-conversation only: once the visitor
   * has a turn in the thread (including RESUMED sessions), the moment
   * has passed and this refuses. On ENGAGE, a pre-reveal opening
   * becomes the welcome itself; post-reveal it appends, same as rail.
   */
  const applyComposedOpening = (opening: {
    message: string;
    chips: string[];
    ghostSuggestion?: string | null;
  }): boolean => {
    const message = opening.message.trim();
    if (!message) return false;
    if (chatStartedSig.value) return false;
    if (currentLayout === LAYOUTS.ENGAGE) {
      // Engage pre-reveal: the composed opening BECOMES the welcome —
      // the 1800ms ceremony reads welcomeTextSig at fire time. Post-
      // reveal: fall through and append as a follow-up bubble, exactly
      // like the rail (fast static first, personalization chimes in).
      if (engageWelcomeTimerId !== null || !engageWelcomeAnchor) {
        welcomeTextSig.value = message;
        pendingGreetingOverride = message;
      } else {
        appendAssistant(message);
        scrollToBottom();
      }
    } else {
      appendAssistant(message);
    }
    const chips = opening.chips
      .map((c) => c.trim())
      .filter(Boolean)
      .slice(0, 4);
    if (chips.length > 0) {
      suggestionsSig.value = chips.map((label) => ({ label }));
      openingChipsSig.value = true;
    }
    const ghost = opening.ghostSuggestion?.trim() || chips[0] || null;
    if (ghost) {
      ghostSuggestion = ghost;
      if (!input.value) input.placeholder = ghostPlaceholder(ghost);
    }
    return true;
  };

  const presenceSay = (message: string): boolean => {
    const line = message.trim();
    if (!line || widgetState.view !== 'unbound' || !boundlessHandle) return false;
    boundlessHandle.setSpeech(line);
    return true;
  };

  const setEngageGreeting = (message: string): boolean => {
    const trimmed = message.trim();
    if (!trimmed) return false;
    // P2-strict (mobile UX program, approved 2026-07-08): a visible
    // greeting is NEVER swapped after paint. Refuse only when the
    // bubble is ON SCREEN (anchor exists, timer done) — a pre-schedule
    // commit is safe: the bubble reads the signal at fire time. Callers
    // that arrive post-paint thread via proactiveGreeting (P8) instead.
    // Legacy chirp-then-swap is preserved when mobileV2 is off.
    if (mobileV2Sig.value && engageWelcomeTimerId === null && engageWelcomeAnchor) {
      return false;
    }
    welcomeTextSig.value = trimmed;
    // Pre-config: applyConfig hasn't set the static welcome yet —
    // remember the override so it survives the config write.
    if (engageWelcomeTimerId === null && !engageWelcomeAnchor) {
      pendingGreetingOverride = trimmed;
      return true;
    }
    // Path A: timer hasn't fired yet — signal update gets read at fire
    // time when the welcome bubble actually mounts.
    if (engageWelcomeTimerId !== null) return true;
    // Path B: timer fired, the welcome bubble is on screen. Re-mount
    // the same component with the personalized markdown — Preact diffs
    // and updates in place. Hands off once the visitor has started
    // chatting so we don't rewrite their conversation history.
    //
    // Don't manually wipe innerHTML before remounting: render() on the
    // same anchor with the same root component type re-uses the existing
    // vnode tree. Clearing innerHTML out-of-band detaches the DOM from
    // Preact's tree, leaving the next render() to no-op and producing
    // an empty bubble (Slice 6.6 regression — surfaced as "personalized
    // greeting disappears and nothing else loads").
    if (chatStartedSig.value) return false;
    if (!engageWelcomeAnchor) return false;
    mountPreactIsland(engageWelcomeAnchor, <AssistantBubble markdown={trimmed} />);
    return true;
  };

  // M3/P7 — the teaser dock: lets the Pilot Prompt Card borrow the
  // persistent greeter video (avatar-stage 'teaser' dock) while it is
  // on screen, then hand it back to the layout-appropriate dock.
  const avatarTeaserDock = {
    register(anchor: HTMLElement): void {
      avatarStage.registerDock('teaser', anchor);
      avatarStage.dock('teaser');
    },
    release(): void {
      avatarStage.unregisterDock('teaser');
      avatarStage.dock(dockForLayout());
    },
  };

  const clearWelcome = (): void => {
    welcomeSuppressed = true;
    welcomeTextSig.value = '';
    welcomeVisibleSig.value = false;
  };

  const markConversationStarted = markChatStarted;

  const showBarNotice = (text: string): void => {
    const trimmed = text.trim();
    if (!trimmed || currentLayout !== LAYOUTS.INPUT) return;
    welcomeTextSig.value = trimmed;
    welcomeVisibleSig.value = true;
    barNoticeUp = true;
  };

  const setStandaloneExperience = (on: boolean): void => {
    standaloneExperienceSig.value = on;
    // Root class hides every dismiss affordance via CSS — the header
    // buttons are signal-driven (above), but the imperative
    // landscape/engage close buttons and any future one are covered here
    // so the standalone viewer can never be closed to a blank page.
    root.classList.toggle('sp-standalone', on);
  };

  return {
    root,
    clearWelcome,
    showBarNotice,
    markConversationStarted,
    setStandaloneExperience,
    loadDurablePlaylist,
    setPresenter: (p) => {
      presenterStage = p;
    },
    openPresenterTour,
    closePresenterStage,
    onTourStageClosed: (cb) => {
      tourStageClosedCb = cb;
    },
    activeTourPublicId,
    mountExperienceGate,
    voiceBadgeAnchor,
    setLayout,
    getLayout,
    setView,
    setConfig,
    isMobileV2: () => mobileV2Sig.value,
    avatarTeaserDock,
    setSpeechCaption: (text: string) => {
      boundlessHandle?.setSpeech(text.trim());
    },
    setOfferBadge: (count: number) => {
      offerBadgeSig.value = Math.max(0, count);
    },
    onLayoutChange: (cb: (layout: Layout) => void) =>
      effect(() => {
        cb(currentLayoutSig.value);
      }),
    onPresence: (cb: (e: PresenceFunnelEvent) => void) => {
      presenceCb = cb;
      // Replay boot-time events that fired before registration.
      for (const e of presenceQueue.splice(0)) cb(e);
      return () => {
        if (presenceCb === cb) presenceCb = null;
      };
    },
    setEngageGreeting,
    applyComposedOpening,
    presenceSay,
    renderHistory,
    appendUserMessage,
    submitFromPrompt: ({ precedingAssistantMessage, userText }) => {
      const trimmed = userText.trim();
      if (!trimmed || !sendHandler) return;
      // Don't barge over a live conversation — if the visitor already
      // had a turn in this session, the Pilot Prompt's job is done.
      // (Slice 4 doesn't surface the prompt mid-conversation, but the
      // guard is cheap insurance.)
      if (chatStartedSig.value) return;
      if (precedingAssistantMessage && precedingAssistantMessage.trim()) {
        appendAssistant(precedingAssistantMessage.trim());
      }
      // The user bubble is rendered by the send pipeline (sendChatMessage
      // calls ui.appendUserMessage) — appending here too rendered every
      // prompt-submitted message TWICE (David's 2026-07-10 repro).
      markChatStarted();
      applyAction({ type: 'submit' });
      // Reset input chrome so the user sees a clean composer once
      // the chat panel opens (input is empty anyway; ghost suggestions
      // / CTA from prior turns shouldn't carry over).
      hasInputTextSig.value = false;
      suggestionsSig.value = [];
      ctaSig.value = null;
      clearSuggestBubbles();
      sendHandler(trimmed, { fromVoice: false });
    },
    appendUserDelta,
    finalizeUserMessage,
    handleChatEvent,
    setStreaming: trackedSetStreaming,
    onSend: (h) => {
      sendHandler = h;
    },
    onContentOpened: (h: (it: PlaylistItem) => void) => {
      contentOpenedHandler = h;
    },
    onContentProgress: (h: (p: ContentProgress) => void) => {
      contentProgressHandler = h;
    },
    onBookSlot: (h: (body: SlotBookBody) => Promise<SlotBookOutcome>) => {
      slotBookHandler = h;
    },
    onSlotOptions: (
      h: (fromIso?: string) => Promise<{
        startsIso: string[];
        durationMinutes: number;
        needsEmail: boolean;
        nextFromIso: string | null;
      } | null>,
    ) => {
      slotOptionsFetcher = h;
    },
    onSlotFunnel: (
      h: (
        type:
          | 'meeting-slot-tapped'
          | 'meeting-fell-to-modal'
          | 'meeting-email-shown'
          | 'meeting-email-given',
        payload: Record<string, unknown>,
      ) => void,
    ) => {
      slotFunnelHandler = h;
    },
    onRouteReadiness: (
      h: (
        answers?: Record<string, string>,
        force?: boolean,
      ) => Promise<SlotReadiness | null>,
    ) => {
      routeReadinessHandler = h;
    },
    onMeetingBooked: (h: (p: { provider: string; bookingUrl: string }) => void) => {
      meetingBookedHandler = h;
    },
    onTalkNow: (h) => {
      talkNowHandler = h;
    },
    onVoiceToggle: (h) => {
      voiceToggleHandler = h;
      voiceAvailableSig.value = true;
    },
    setVoiceModeActive: (active) => {
      voiceModeActive = active;
      statusLabelSig.value = active ? 'End voice' : 'Talk Now';
      listeningSig.value = active;
      // Voice-mode start counts as "chat started" for greeter shrink
      // purposes. Without this, the VideoGreeter component stays at
      // full 200px height because it only watches `chatStartedSig`,
      // not `listeningSig` — visitor entered voice but the greeter
      // never collapses to its 52px header form. Idempotent via
      // markChatStarted's internal guard.
      if (active) markChatStarted();
      // CSS hook for any styling (pulsing mic indicator, etc.) that
      // the host page can target via theming. Stays scoped under
      // `.sp-root` per the widget CSS-isolation policy.
      // A call owns a phone's screen: the keyboard goes down (David's
      // Lumina test 2026-10-21 — it stayed up into voice).
      if (active && deviceSig.value === 'phone') dismissKeyboard(document);
      if (active) root.classList.add('sp-voice-mode');
      else root.classList.remove('sp-voice-mode');
    },
    onJourneyAnswer: (h) => {
      journeyAnswerHandler = h;
    },
    onCallLifecycle: (h) => {
      callLifecycleHandler = h;
    },
    onContactCapture: (h) => {
      contactCaptureHandler = h;
    },
    onBeforeHostNavigate: (h) => {
      beforeHostNavigate = h;
    },
    onHighlightNavigate: (h) => {
      highlightNavigator = h;
    },
    notifyPageChange: () => {
      // Bump the nonce — AgentLinks island re-renders and re-reads
      // <meta name="agent-feature"> for fresh {feature} substitution.
      linksPageNonceSig.value = linksPageNonceSig.value + 1;
    },
    getWidgetStateSnapshot: () => ({
      view: widgetState.view,
      state: widgetState.state,
      experienceActive: widgetState.experienceActive,
    }),
    hasDraft: () => hasInputTextSig.value,
    hasContentOpen: () => currentContentItem !== null,
    applyPendingHighlight: ({ text, url }) => {
      // Verify we're actually on the page the highlight targeted —
      // visitors can hop between pages while the snapshot is still
      // valid, and we don't want to outline random elements on the
      // wrong page.
      if (url) {
        try {
          const target = new URL(url, window.location.href);
          if (target.pathname !== window.location.pathname) return;
        } catch {
          /* malformed url — proceed without origin check */
        }
      }
      highlightOnHostPage(text, firstNameSig.value);
    },
    showError,
  };
}

function hexToRgb(hex: string): string | null {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m || !m[1] || !m[2] || !m[3]) return null;
  return `${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)}`;
}
